// ════════════════════════════════════════════════════════════════════════════
// RED CARD EFFECTS
// See js/effects/registry.js for the registerCardEffect() API and the list
// of available triggers.
// ════════════════════════════════════════════════════════════════════════════

// ── Shared helpers (red-card-local; not part of the registry API) ──

// "[During Link][Attack] Activate [Main] on the card paired with this Unit."
// (e.g. GD05-035 Dragon Gundam, GD05-044 Gundam Rose). Directly invokes the
// paired pilot's own registered 'main' handler mid-Attack-Step — NOT via
// dispatch('ACTIVATE_COMMAND', ...), since the pilot is already on the field
// (not in hand) and must stay paired, not get trashed. triggerCardEffect()
// is the same choke point ACTIVATE_COMMAND/resolveActionPlay use, so this
// also correctly feeds state.specialMoveCommandActivatedThisTurn (GD05-089
// Master Asia) when the paired pilot has (Special Move) trait.
function activatePairedPilotMain(unit, ctx) {
  if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
  if (!isLinkUnit(unit) || !unit.pilot) { ctx?.onDone?.(); return; }
  const pilot = unit.pilot;
  const handlers = window.CardEffects?.[pilot.cardNo];
  if (!handlers?.main) { ctx.onDone(); return; }
  addLog(`${unit.name} — [During Link][Attack] activates ${pilot.name}'s [Main] effect.`);
  const handled = triggerCardEffect(pilot, 'main', {
    onDone: () => fireCommandEffectPassives(pilot, ctx.onDone),
  });
  if (!handled) ctx.onDone();
}

// Pairs a Command Pilot card FROM THE TRASH onto a chosen field Unit —
// mirrors registerAction('PAIR_PILOT', ...) (game.js) but sources/removes
// from state.playerTrash instead of state.hand. Used by GD05-112/113's
// "After activating this card's [Main], you may pair this card from your
// trash with one of your (MF) Units."
function pairFromTrash(pilot, unit, slot) {
  const idx = state.playerTrash.findIndex(c => c.uid === pilot.uid);
  if (idx !== -1) { state.playerTrash.splice(idx, 1); state.trashCount = Math.max(0, state.trashCount - 1); }
  const pairedUnit = { ...unit, pilot };
  if (!unit.pilot && isLinkUnit(pairedUnit)) {
    pairedUnit.linkedThisTurn = true;
    pairedUnit.tempCantAttack = false;
    if (state.playerBase) {
      window.CardEffects?.[state.playerBase.cardNo]?.onPlayerUnitLinked?.(state.playerBase, pairedUnit, slot);
    }
  }
  if (state.playerBase) {
    window.CardEffects?.[state.playerBase.cardNo]?.onPilotPaired?.(state.playerBase, pilot, pairedUnit, slot);
  }
  state.playerField[slot] = pairedUnit;
  addLog(`${pilot.name} paired from trash as pilot to ${unit.name}.`);
  triggerCardEffect(pilot, 'pair', { unit: pairedUnit, slot });
  triggerCardEffect(pairedUnit, 'pair', { unit: pairedUnit, slot });
  const pairedColor = (pairedUnit.color || '').toLowerCase();
  state.playerField.forEach(fieldUnit => {
    if (!fieldUnit || fieldUnit.uid === pairedUnit.uid) return;
    const fh = window.CardEffects?.[fieldUnit.cardNo];
    if (fh?.onAnyUnitPair) fh.onAnyUnitPair(fieldUnit, pairedUnit, pairedColor);
  });
  NET.send({ type: 'PLAY_CARD', slot, card: pairedUnit, counters: myCounters() });
  broadcastCounters();
  render();
  setupDropZones();
}

// Shared "you may pair this card from your trash with one of your (TRAIT)
// Units" offer, shown after a Command Pilot's own [Main] resolves (e.g.
// GD05-112 Hoka Kyoten Juzetsujin, GD05-113 Rose Screamer). No-ops if the
// card somehow isn't in trash (shouldn't happen post-ACTIVATE_COMMAND) or
// no eligible unpaired Unit exists.
function offerPairFromTrash(commandPilot, traitFilter, onDone) {
  const inTrash = state.playerTrash.some(c => c.uid === commandPilot.uid);
  if (!inTrash) { onDone(); return; }
  // cantBePaired: same permanent "This Unit can't be paired with a Pilot"
  // restriction excluded from every other pairing path (see the
  // click-to-pair unpairedUnits filter and handleDrop's occupied-slot
  // check in game.js) — this trash-pair offer was missing it too.
  const candidates = state.playerField.filter(c => c && !c.pilot && (c.trait || '').includes(traitFilter) &&
    !window.CardEffects?.[c.cardNo]?.cantBePaired?.(c));
  if (candidates.length === 0) { onDone(); return; }
  showCardPickerModal(
    commandPilot.name,
    `You may pair ${commandPilot.name} from your trash with one of your ${traitFilter} Units.`,
    candidates,
    (target) => {
      if (target) pairFromTrash(commandPilot, target, state.playerField.indexOf(target));
      onDone();
    },
    commandPilot,
    { mandatory: false, skipLabel: 'Decline' }
  );
}

// ─── GD02-125 · Gwadan ───────────────────────────────────────────────────
// [Deploy] Add 1 Shield to hand — handled generically by applyBaseDeployEffect().
// Then, if it is the player's turn, they may discard 1 red card to draw 1.
registerCardEffect('GD02-125', {
  deploy: (card) => {
    if (!state.myTurn) return;
    const redCards = state.hand.filter(c => c.color === 'red');
    if (redCards.length === 0) return;
    showCardPickerModal(
      card.name,
      'You may discard 1 red card. If you do, draw 1.',
      redCards,
      (target) => {
        if (!target) return;
        state.hand = state.hand.filter(c => c.uid !== target.uid);
        updateTempState(target.cardNo, target.copyVer, 'trash');
        state.trashCount++;
        state.playerTrash.push(toTrashCard(target));
        broadcastLog(`${card.name} — discarded ${target.name}.`);
        broadcastCardToast(`${target.name} was discarded due to ${card.name} effect.`);
        const drawn = drawCardsFromTempDeck(1, true);
        if (drawn.length > 0) {
          state.hand.push(drawn[0]);
          addLog(`${card.name} — drew ${drawn[0].name}.`);
        }
        broadcastCounters();
        render();
      },
      card
    );
  },
});

// ─── GD03-128 · Doritea ──────────────────────────────────────────────────
// [Deploy] Add 1 Shield to hand — handled generically by applyBaseDeployEffect().
// [Once per Turn] During the opponent's turn, when an opponent effect rests a
// player unit, choose 1 enemy Unit and deal 1 damage to it.
// Fires via NET.onUnitRest → onOpponentRestPlayerUnit hook in game.js.
registerCardEffect('GD03-128', {
  onOpponentRestPlayerUnit: (base, restedUnit) => {
    const candidates = state.oppField.filter(c => c != null);
    if (candidates.length === 0) {
      addLog(`${base.name} — no enemy Units to target. No effect.`);
      return;
    }
    base.passiveUsedThisTurn = true;
    addLog(`${base.name} — ${restedUnit.name} was rested by an opponent effect. Choose 1 enemy Unit to deal 1 damage.`);
    showCardPickerModal(
      base.name,
      'Choose 1 enemy Unit. Deal 1 damage to it.',
      candidates,
      (target) => {
        if (!target) return;
        dealDamageToUnit(target, 1, { fromEnemy: true });
      },
      base,
      { mandatory: true }
    );
  },
});

// ─── GD02-126 · Shuji's Hideout ──────────────────────────────────────────
// [Deploy] Add 1 Shield to hand — handled generically by applyBaseDeployEffect().
// [Destroyed] Choose 1 enemy Unit that is Lv.4 or lower. Deal 1 damage to it.
registerCardEffect('GD02-126', {
  destroyed: (card, ctx) => {
    // Battle destruction fires on both clients; only run on the base owner's
    // client (defenderSide === 'player'). Effect damage destruction fires
    // locally with ctx = {} (no defenderSide), so always proceed in that case.
    if (ctx && ctx.defenderSide !== undefined && ctx.defenderSide !== 'player') return;
    const candidates = state.oppField.filter(c => c && (parseInt(c.level) || 0) <= 4);
    if (candidates.length === 0) {
      addLog(`${card.name} — no enemy Units Lv.4 or lower. No effect.`);
      ctx.onDone?.();
      return;
    }
    showCardPickerModal(
      card.name,
      'Choose 1 enemy Unit that is Lv.4 or lower. Deal 1 damage to it.',
      candidates,
      (target) => {
        if (!target) { ctx.onDone?.(); return; }
        dealDamageToUnit(target, 1, { fromEnemy: true });
        ctx.onDone?.();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── ST03-015 · Rewloola ─────────────────────────────────────────────────
// [Deploy] Add 1 Shield to hand — handled generically by applyBaseDeployEffect().
// Then, if the enemy has a unit with 5 or less AP, choose 1 and deal 1 damage.
registerCardEffect('ST03-015', {
  deploy: (card) => {
    const candidates = state.oppField.filter(c => c && effectiveBp(c) <= 5);
    if (candidates.length === 0) return;
    showCardPickerModal(
      card.name,
      'Choose 1 enemy Unit with 5 or less AP. Deal 1 damage to it.',
      candidates,
      (target) => {
        if (!target) return;
        dealDamageToUnit(target, 1, { fromEnemy: true });
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── ST04-016 · Vesalius ─────────────────────────────────────────────────
// [Deploy] Add 1 Shield to hand — handled generically by applyBaseDeployEffect().
// [Activate･Main] Rest this Base: Choose 1 friendly Unit. It gets AP+1 this turn.
registerCardEffect('ST04-016', {
  canActivate: () => state.playerField.some(c => c != null),
  activate: (card, ctx) => {
    const candidates = state.playerField.filter(c => c != null);
    if (candidates.length === 0) {
      addLog(`${card.name} — no friendly Units in play. No effect.`);
      ctx.onDone();
      return;
    }
    showCardPickerModal(
      card.name,
      'Choose 1 friendly Unit. It gets AP+1 during this turn.',
      candidates,
      (target) => {
        if (!target) { ctx.onDone(); return; }
        target.tempApMod = (target.tempApMod || 0) + 1;
        NET.send({ type: 'FIELD_AP_MOD', uids: [target.uid], delta: 1, field: 'tempApMod' });
        addLog(`${card.name} — ${target.name} gets AP+1 during this turn.`);
        render();
        setupDropZones();
        ctx.onDone();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── ST06-014 · Clan Battle ──────────────────────────────────────────────
// [Deploy] Add 1 Shield to hand — handled generically by applyBaseDeployEffect().
// [Activate･Main] Rest this Base: If a friendly (Clan) Link Unit is in play,
// choose 1 friendly (Clan) Link Unit. It gets AP+2 during this turn.
registerCardEffect('ST06-014', {
  canActivate: () => state.playerField.some(c => c && (c.trait || '').includes('(Clan)') && isLinkUnit(c)),
  activate: (card, ctx) => {
    const candidates = state.playerField.filter(c => c && (c.trait || '').includes('(Clan)') && isLinkUnit(c));
    if (candidates.length === 0) {
      addLog(`${card.name} — no friendly (Clan) Link Units in play. No effect.`);
      ctx.onDone();
      return;
    }
    showCardPickerModal(
      card.name,
      'Choose 1 friendly (Clan) Link Unit. It gets AP+2 during this turn.',
      candidates,
      (target) => {
        if (!target) { ctx.onDone(); return; }
        target.tempApMod = (target.tempApMod || 0) + 2;
        NET.send({ type: 'FIELD_AP_MOD', uids: [target.uid], delta: 2, field: 'tempApMod' });
        addLog(`${card.name} — ${target.name} gets AP+2 during this turn.`);
        render();
        setupDropZones();
        ctx.onDone();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── ST08-014 · Valiant ──────────────────────────────────────────────────
// [Deploy] Add 1 Shield to hand — handled generically by applyBaseDeployEffect().
// Then, if the player has units, choose 1 to give AP+2 for the current turn.
registerCardEffect('ST08-014', {
  deploy: (card) => {
    const candidates = state.playerField.filter(u => u != null);
    if (candidates.length === 0) return;
    showCardPickerModal(
      card.name,
      'Choose 1 of your Units. It gets AP+2 during this turn.',
      candidates,
      (target) => {
        if (!target) return;
        target.tempApMod = (target.tempApMod || 0) + 2;
        NET.send({ type: 'FIELD_AP_MOD', uids: [target.uid], delta: 2, field: 'tempApMod' });
        addLog(`${card.name} — ${target.name} gets AP+2 during this turn.`);
        render();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── GD01-127 · Gamow ────────────────────────────────────────────────────
// [Deploy] Add 1 Shield to hand — handled generically by applyBaseDeployEffect().
// [Activate･Action] Rest this Base: Choose 1 friendly (ZAFT) Unit with 5 or
// more AP. It gains Breach 3 during this battle.
registerCardEffect('GD01-127', {
  canAction: (base, ctx) => {
    return state.playerField.some(u => u && (u.trait || '').includes('(ZAFT)') && effectiveBp(u) >= 5);
  },
  action: (base, ctx) => {
    const candidates = state.playerField.filter(u => u && (u.trait || '').includes('(ZAFT)') && effectiveBp(u) >= 5);
    showCardPickerModal(
      base.name,
      'Choose 1 friendly (ZAFT) Unit with 5 or more AP. It gains Breach 3 during this battle.',
      candidates,
      (target) => {
        if (!target) { ctx.onDone(); return; }
        target.tempKeywords = target.tempKeywords || [];
        target.tempKeywords.push('Breach 3');
        addLog(`${base.name} — ${target.name} gains Breach 3 during this battle.`);
        if (state.networked) NET.send({ type: 'TEMP_KEYWORD_ADD', uid: target.uid, keyword: 'Breach 3' });
        render();
        ctx.onDone();
      },
      base,
      { mandatory: true }
    );
  },
});

// ─── GD03-127 · Jachin Due ───────────────────────────────────────────────
// [Deploy] Add 1 Shield to hand — handled generically by applyBaseDeployEffect().
// Then, if a friendly (ZAFT) Unit is in play, choose 1 and give it AP+3 this turn.
registerCardEffect('GD03-127', {
  deploy: (card) => {
    const candidates = state.playerField.filter(u => u && (u.trait || '').includes('(ZAFT)'));
    if (candidates.length === 0) return;
    showCardPickerModal(
      card.name,
      'Choose 1 friendly (ZAFT) Unit. It gets AP+3 during this turn.',
      candidates,
      (target) => {
        if (!target) return;
        target.tempApMod = (target.tempApMod || 0) + 3;
        NET.send({ type: 'FIELD_AP_MOD', uids: [target.uid], delta: 3, field: 'tempApMod' });
        addLog(`${card.name} — ${target.name} gets AP+3 during this turn.`);
        render();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── GD04-125 · Trinity Warship ──────────────────────────────────────────
// [Deploy] Add 1 Shield to hand — handled generically by applyBaseDeployEffect().
// [Activate･Main][Once per Turn]①, rest 1 friendly (CB) Unit:
//   Choose 1 enemy Unit Lv.5 or lower. Deal 1 damage to it.
// ACTIVATE_BASE already spends 1 RES and sets baseAbilityUsedThisTurn.
registerCardEffect('GD04-125', {
  canActivate: () => {
    const hasCbUnit = state.playerField.some(u => u && !u.rested && (u.trait || '').includes('(CB)') && (parseInt(u.level) || 0) <= 5);
    const hasEnemyTarget = state.oppField.some(c => c && (parseInt(c.level) || 0) <= 5);
    return hasCbUnit && hasEnemyTarget;
  },
  activate: (card, ctx) => {
    const cbCandidates = state.playerField.filter(u => u && !u.rested && (u.trait || '').includes('(CB)') && (parseInt(u.level) || 0) <= 5);
    if (cbCandidates.length === 0) {
      addLog(`${card.name} — no valid active (CB) Units to rest. No effect.`);
      ctx.onDone();
      return;
    }
    showCardPickerModal(
      card.name,
      'Choose 1 friendly (CB) Unit (Lv.5 or lower) to rest.',
      cbCandidates,
      (cbUnit) => {
        if (!cbUnit) { ctx.onDone(); return; }
        cbUnit.rested = true;
        NET.send({ type: 'UNIT_REST', uid: cbUnit.uid, rested: true });
        addLog(`${card.name} — rested ${cbUnit.name}.`);
        render();
        setupDropZones();
        const enemyCandidates = state.oppField.filter(c => c && (parseInt(c.level) || 0) <= 5);
        if (enemyCandidates.length === 0) {
          addLog(`${card.name} — no enemy Units Lv.5 or lower to target. No effect.`);
          ctx.onDone();
          return;
        }
        showCardPickerModal(
          card.name,
          'Choose 1 enemy Unit that is Lv.5 or lower. Deal 1 damage to it.',
          enemyCandidates,
          (target) => {
            if (!target) { ctx.onDone(); return; }
            dealDamageToUnit(target, 1, { fromEnemy: true });
            ctx.onDone();
          },
          card,
          { mandatory: true }
        );
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── GD03-111 · Infiltrator Present ──────────────────────────────────────
// [Main]/[Action] Choose 1 friendly (Mafty) Unit. It gets AP+3 during this turn.
function gd03111Effect(card, onDone) {
  const candidates = state.playerField.filter(u => u && (u.trait || '').includes('(Mafty)'));
  if (candidates.length === 0) { addLog(`${card.name} — no friendly (Mafty) Units in play.`); onDone(); return; }
  showCardPickerModal(
    card.name,
    'Choose 1 friendly (Mafty) Unit. It gets AP+3 during this turn.',
    candidates,
    (target) => {
      if (target) {
        target.tempApMod = (target.tempApMod || 0) + 3;
        NET.send({ type: 'FIELD_AP_MOD', uids: [target.uid], delta: 3, field: 'tempApMod' });
        addLog(`${card.name} — ${target.name} gets AP+3 during this turn.`);
        render();
        setupDropZones();
      }
      onDone();
    },
    card,
    { mandatory: true }
  );
}
registerCardEffect('GD03-111', {
  canActivate: () => state.playerField.some(u => u && (u.trait || '').includes('(Mafty)')),
  main: (card, ctx) => gd03111Effect(card, ctx.onDone),
  action: (card, ctx) => gd03111Effect(card, ctx.onDone),
});

// ─── ST06-011 · Ruthless Tactics ─────────────────────────────────────────
// [Main]/[Action] Choose 1 to 2 friendly (Clan) Units. They get AP+2 this turn.
function st06011Effect(card, onDone) {
  const candidates = state.playerField.filter(u => u && (u.trait || '').includes('(Clan)'));
  if (candidates.length === 0) { addLog(`${card.name} — no friendly (Clan) Units in play.`); onDone(); return; }
  showCardPickerModalMulti(
    card.name,
    'Choose 1 to 2 friendly (Clan) Units. They get AP+2 during this turn.',
    candidates,
    { min: 1, max: 2, mandatory: true },
    (chosen) => {
      if (chosen && chosen.length > 0) {
        chosen.forEach(u => { u.tempApMod = (u.tempApMod || 0) + 2; });
        NET.send({ type: 'FIELD_AP_MOD', uids: chosen.map(u => u.uid), delta: 2, field: 'tempApMod' });
        addLog(`${card.name} — ${chosen.map(u => u.name).join(' and ')} each get AP+2 during this turn.`);
        render();
        setupDropZones();
      }
      onDone();
    },
    card
  );
}
registerCardEffect('ST06-011', {
  canActivate: () => state.playerField.some(u => u && (u.trait || '').includes('(Clan)')),
  main: (card, ctx) => st06011Effect(card, ctx.onDone),
  action: (card, ctx) => st06011Effect(card, ctx.onDone),
});

// ─── GD04-112 · Inspector ────────────────────────────────────────────────
// [Main] Deal 1 damage to all Units that are Lv.2 or lower (both sides).
// Tokens count as Lv.0. Requires at least 1 qualifying unit on each side.
registerCardEffect('GD04-112', {
  canActivate: () => {
    const lv2 = (u) => u && (isUnitToken(u) ? 0 : (parseInt(u.level) || 0)) <= 2;
    return state.playerField.some(lv2) || state.oppField.some(lv2);
  },
  main: (card, ctx) => {
    const lv2 = (u) => u && (isUnitToken(u) ? 0 : (parseInt(u.level) || 0)) <= 2;
    const playerTargets = state.playerField.filter(lv2);
    const oppTargets    = state.oppField.filter(lv2);
    addLog(`${card.name} — dealing 1 damage to all Units Lv.2 or lower.`);
    playerTargets.forEach(u => dealDamageToUnit(u, 1));
    oppTargets.forEach(u => dealDamageToUnit(u, 1, { fromEnemy: true }));
    render();
    setupDropZones();
    ctx.onDone();
  },
});

// ─── GD01-112 · Extreme Hatred ───────────────────────────────────────────
// [Main] Choose 2 active friendly Units. Rest them. If you do, choose 1 enemy Unit. Deal 3 damage.
registerCardEffect('GD01-112', {
  canActivate: () => state.playerField.filter(u => u && !u.rested).length >= 2,
  main: (card, ctx) => {
    const activeCandidates = state.playerField.filter(u => u && !u.rested);
    if (activeCandidates.length < 2) { addLog(`${card.name} — need at least 2 active Units.`); ctx.onDone(); return; }
    showCardPickerModalMulti(
      card.name,
      'Choose 2 of your active Units. They will be rested.',
      activeCandidates,
      { min: 2, max: 2, mandatory: true },
      (chosen) => {
        if (!chosen || chosen.length < 2) { ctx.onDone(); return; }
        chosen.forEach(u => {
          u.rested = true;
          NET.send({ type: 'UNIT_REST', uid: u.uid, rested: true });
        });
        addLog(`${card.name} — rested ${chosen.map(u => u.name).join(' and ')}.`);
        render(); setupDropZones();
        const enemyCandidates = state.oppField.filter(c => c != null);
        if (enemyCandidates.length === 0) { addLog(`${card.name} — no enemy Units to target.`); ctx.onDone(); return; }
        showCardPickerModal(
          card.name,
          'Choose 1 enemy Unit. Deal 3 damage to it.',
          enemyCandidates,
          (target) => {
            if (target) dealDamageToUnit(target, 3, { fromEnemy: true });
            ctx.onDone();
          },
          card,
          { mandatory: true }
        );
      },
      card
    );
  },
});

// ─── GD01-111 · Battle of Aces ───────────────────────────────────────────
// [Burst] Choose 1 enemy Unit. Deal 2 damage to it.
// [Main]/[Action] Choose 1 damaged enemy Unit. Deal 3 damage to it.
function gd01111MainEffect(card, onDone) {
  const candidates = state.oppField.filter(c => c && c.damaged);
  if (candidates.length === 0) { addLog(`${card.name} — no damaged enemy Units in play.`); onDone(); return; }
  showCardPickerModal(
    card.name,
    'Choose 1 damaged enemy Unit. Deal 3 damage to it.',
    candidates,
    (target) => {
      if (target) dealDamageToUnit(target, 3, { fromEnemy: true });
      onDone();
    },
    card,
    { mandatory: true }
  );
}
registerCardEffect('GD01-111', {
  canActivate: () => state.oppField.some(c => c && c.damaged),
  burst: (card, ctx) => {
    const onDone = ctx?.onDone ?? (() => {});
    const candidates = state.oppField.filter(c => c != null);
    if (candidates.length === 0) { addLog(`${card.name} — no enemy Units in play.`); onDone(); return; }
    showCardPickerModal(
      card.name,
      'Choose 1 enemy Unit. Deal 2 damage to it.',
      candidates,
      (target) => {
        if (target) dealDamageToUnit(target, 2, { fromEnemy: true });
        onDone();
      },
      card,
      { mandatory: true }
    );
  },
  main: (card, ctx) => gd01111MainEffect(card, ctx.onDone),
  action: (card, ctx) => gd01111MainEffect(card, ctx.onDone),
});

// ─── GD04-110 · Financier ────────────────────────────────────────────────
// [Main]/[Action] Deploy 1 EX Base. If a non-EX base exists, trash it first
// (no destroyed triggers). EX Base tokens are replaced without going to trash.
function gd04110Effect(card, onDone) {
  const oldBase = state.playerBase;
  if (oldBase && !isRemovedFromGameBase(oldBase)) {
    state.trashCount++;
    state.playerTrash.push(toTrashCard(oldBase));
    updateTempState(oldBase.cardNo, oldBase.copyVer, 'trash');
    addLog(`${oldBase.name} sent to trash (no destroyed effect).`);
    broadcastCardToast(`${oldBase.name} was sent to trash.`);
    broadcastCounters();
  }
  const exBase = makeBaseCardFromEntry(getCardAppearanceEntry('exBase'), 'EX Base');
  exBase.uid     = `${exBase.cardNo || 'EXB-001'}_${Date.now()}`;
  exBase.rested  = false;
  exBase.damaged = false;
  state.playerBase = exBase;
  addLog(`${card.name} — EX Base deployed.`);
  NET.send({ type: 'DEPLOY_BASE', card: state.playerBase, counters: myCounters() });
  render();
  setupDropZones();
  onDone();
}
registerCardEffect('GD04-110', {
  main: (card, ctx) => gd04110Effect(card, ctx.onDone),
  action: (card, ctx) => gd04110Effect(card, ctx.onDone),
});

// ─── GD04-109 · Overwhelming Pressure ────────────────────────────────────
// [Main]/[Action] Choose 1 enemy Unit Lv.6 or lower. Deal 4 damage to it.
function gd04109Effect(card, onDone) {
  const candidates = state.oppField.filter(c => c && (parseInt(c.level) || 0) <= 6);
  if (candidates.length === 0) { addLog(`${card.name} — no enemy Units Lv.6 or lower in play.`); onDone(); return; }
  showCardPickerModal(
    card.name,
    'Choose 1 enemy Unit (Lv.6 or lower). Deal 4 damage to it.',
    candidates,
    (target) => {
      if (target) dealDamageToUnit(target, 4, { fromEnemy: true });
      onDone();
    },
    card,
    { mandatory: true }
  );
}
registerCardEffect('GD04-109', {
  canActivate: () => state.oppField.some(c => c && (parseInt(c.level) || 0) <= 6),
  main: (card, ctx) => gd04109Effect(card, ctx.onDone),
  action: (card, ctx) => gd04109Effect(card, ctx.onDone),
});

// ─── GD03-110 · Eliminate Target ─────────────────────────────────────────
// [Main]/[Action] Choose 1 Pilot paired with an enemy Unit (Lv.5 or lower). Destroy it.
// Unit stays on field but loses pilot's stat boosts. If raw HP ≤ 0 after, unit is destroyed.
function gd03110Effect(card, onDone) {
  const candidates = state.oppField.filter(c => c && c.pilot && (parseInt(c.level) || 0) <= 5);
  if (candidates.length === 0) { addLog(`${card.name} — no enemy Units (Lv.5 or lower) with a Pilot.`); onDone(); return; }
  showCardPickerModal(
    card.name,
    'Choose 1 enemy Unit (Lv.5 or lower) with a Pilot. That Pilot is destroyed.',
    candidates,
    (target) => {
      if (!target) { onDone(); return; }
      const slot = state.oppField.findIndex(c => c && c.uid === target.uid);
      const pilot = target.pilot;
      target.pilot = null;
      target.pilotKeywords = [];
      state.oppTrash.push(toTrashCard(pilot));
      addLog(`${card.name} — ${pilot.name} is destroyed!`);
      NET.send({ type: 'PILOT_DESTROY', unitUid: target.uid });
      if ((parseInt(target.hp) || 0) <= 0) {
        addLog(`${target.name} loses its HP boost and is destroyed!`);
        if (slot !== -1) state.oppField[slot] = null;
        state.oppTrash.push(toTrashCard(target));
        NET.send({ type: 'OPP_UNIT_DESTROYED', unitUid: target.uid });
      }
      render();
      setupDropZones();
      onDone();
    },
    card,
    { mandatory: true }
  );
}
registerCardEffect('GD03-110', {
  canActivate: () => state.oppField.some(c => c && c.pilot && (parseInt(c.level) || 0) <= 5),
  main: (card, ctx) => gd03110Effect(card, ctx.onDone),
  action: (card, ctx) => gd03110Effect(card, ctx.onDone),
});

// ─── ST08-013 · Lady Luck ────────────────────────────────────────────────
// [Main]/[Action] Choose 1 enemy Unit. Deal 1 damage. If a friendly (Mafty) Link
// Unit is in play, deal 2 damage instead.
function st08013Effect(card, onDone) {
  const hasMaftyLink = state.playerField.some(u => u && u.pilot && (u.trait || '').includes('(Mafty)'));
  const damage = hasMaftyLink ? 2 : 1;
  const candidates = state.oppField.filter(c => c != null);
  if (candidates.length === 0) { addLog(`${card.name} — no enemy Units in play.`); onDone(); return; }
  showCardPickerModal(
    card.name,
    `Choose 1 enemy Unit. Deal ${damage} damage to it.${hasMaftyLink ? ' (boosted by friendly (Mafty) Link Unit)' : ''}`,
    candidates,
    (target) => {
      if (target) dealDamageToUnit(target, damage, { fromEnemy: true });
      onDone();
    },
    card,
    { mandatory: true }
  );
}
registerCardEffect('ST08-013', {
  canActivate: () => state.oppField.some(c => c != null),
  main: (card, ctx) => st08013Effect(card, ctx.onDone),
  action: (card, ctx) => st08013Effect(card, ctx.onDone),
});

// ─── GD03-112 · Warped Intent ────────────────────────────────────────────
// [Burst] Add this card to your hand. (handled generically by the engine)
// [Main]/[Action] All Units paired with a Pilot get AP+2 during this turn (both sides).
function gd03112Effect(card, onDone) {
  const playerLinked = state.playerField.filter(u => u && u.pilot);
  const oppLinked    = state.oppField.filter(u => u && u.pilot);
  const allLinked    = [...playerLinked, ...oppLinked];
  if (allLinked.length === 0) { addLog(`${card.name} — no Link Units in play.`); onDone(); return; }
  allLinked.forEach(u => { u.tempApMod = (u.tempApMod || 0) + 2; });
  NET.send({ type: 'FIELD_AP_MOD', uids: allLinked.map(u => u.uid), delta: 2, field: 'tempApMod' });
  addLog(`${card.name} — all Link Units get AP+2 during this turn.`);
  render();
  setupDropZones();
  onDone();
}
registerCardEffect('GD03-112', {
  canActivate: () => state.playerField.some(u => u && u.pilot) || state.oppField.some(u => u && u.pilot),
  main: (card, ctx) => gd03112Effect(card, ctx.onDone),
  action: (card, ctx) => gd03112Effect(card, ctx.onDone),
});

// ─── GD02-107 · All-Range Attack ─────────────────────────────────────────
// [Burst] Choose 1 enemy Unit. Deal 1 damage to it.
// [Main] Deal 1 damage to all enemy Units other than Link Units.
registerCardEffect('GD02-107', {
  canActivate: () => state.oppField.some(c => c != null),
  burst: (card, ctx) => {
    const onDone = ctx?.onDone ?? (() => {});
    const candidates = state.oppField.filter(c => c != null);
    if (candidates.length === 0) { addLog(`${card.name} — no enemy Units in play.`); onDone(); return; }
    showCardPickerModal(
      card.name,
      'Choose 1 enemy Unit. Deal 1 damage to it.',
      candidates,
      (target) => {
        if (target) dealDamageToUnit(target, 1, { fromEnemy: true });
        onDone();
      },
      card,
      { mandatory: true }
    );
  },
  main: (card, ctx) => {
    const targets = state.oppField.filter(c => c && !c.pilot);
    if (targets.length === 0) { addLog(`${card.name} — no valid enemy Units (all are Link Units or field is empty).`); ctx.onDone(); return; }
    addLog(`${card.name} — dealing 1 damage to all non-Link enemy Units.`);
    targets.forEach(c => dealDamageToUnit(c, 1, { fromEnemy: true }));
    render();
    setupDropZones();
    ctx.onDone();
  },
});

// ─── GD03-109 · Improved Technique ───────────────────────────────────────
// [Burst] Activate this card's [Main].
// [Main]/[Action] Choose 1 enemy Unit Lv.4 or lower. Deal 3 damage.
//   If 2+ "Improved Technique" copies in trash (excl. this card), any enemy Unit.
// selfInTrash: true for main/action (card already trashed), false for burst.
function gd03109Effect(card, onDone, selfInTrash = false) {
  const trashCopies = state.playerTrash.filter(c => (c.name || '').includes('Improved Technique')).length;
  const unrestricted = (selfInTrash ? trashCopies - 1 : trashCopies) >= 2;
  const candidates = unrestricted
    ? state.oppField.filter(c => c != null)
    : state.oppField.filter(c => c && (parseInt(c.level) || 0) <= 4);
  if (candidates.length === 0) { addLog(`${card.name} — no valid enemy targets.`); onDone(); return; }
  showCardPickerModal(
    card.name,
    unrestricted
      ? 'Choose 1 enemy Unit. Deal 3 damage to it.'
      : 'Choose 1 enemy Unit (Lv.4 or lower). Deal 3 damage to it.',
    candidates,
    (target) => {
      if (target) dealDamageToUnit(target, 3, { fromEnemy: true });
      onDone();
    },
    card,
    { mandatory: true }
  );
}
registerCardEffect('GD03-109', {
  canActivate: () => {
    const trashCopies = state.playerTrash.filter(c => (c.name || '').includes('Improved Technique')).length;
    const unrestricted = trashCopies >= 2; // card still in hand at this point
    return unrestricted
      ? state.oppField.some(c => c != null)
      : state.oppField.some(c => c && (parseInt(c.level) || 0) <= 4);
  },
  burst: (card, ctx) => gd03109Effect(card, ctx?.onDone ?? (() => {}), false),
  main:   (card, ctx) => gd03109Effect(card, ctx.onDone, true),
  action: (card, ctx) => gd03109Effect(card, ctx.onDone, true),
});

// ─── GD02-108 · That One Looks A Lot Stronger? ───────────────────────────
// [Main] Choose 1 friendly (Clan) Unit. During this turn, it may attack active
// enemy Units that are Lv.4 or lower. Stored as tempKeyword 'CanAttackActiveLv4'
// so it is cleared automatically at end of turn by UNREST_ALL.
registerCardEffect('GD02-108', {
  canActivate: () => state.playerField.some(u => u && (u.trait || '').includes('(Clan)')),
  main: (card, ctx) => {
    const candidates = state.playerField.filter(u => u && (u.trait || '').includes('(Clan)'));
    if (candidates.length === 0) { addLog(`${card.name} — no friendly (Clan) Units in play.`); ctx.onDone(); return; }
    showCardPickerModal(
      card.name,
      'Choose 1 friendly (Clan) Unit. It may attack active enemy Units (Lv.4 or lower) this turn.',
      candidates,
      (target) => {
        if (target) {
          target.tempKeywords = target.tempKeywords || [];
          if (!target.tempKeywords.includes('CanAttackActiveLv4')) target.tempKeywords.push('CanAttackActiveLv4');
          NET.send({ type: 'TEMP_KEYWORD_ADD', uid: target.uid, keyword: 'CanAttackActiveLv4' });
          addLog(`${card.name} — ${target.name} may now attack active enemy Units (Lv.4 or lower) this turn.`);
          render();
          setupDropZones();
        }
        ctx.onDone();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── GD03-113 · Human Karma ──────────────────────────────────────────────
// [Main]/[Action] Choose 1 active friendly Unit. Rest it. If you do, choose 1 enemy
// Unit whose Lv. is equal to or lower than the rested Unit. Deal 3 damage to it.
function gd03113Effect(card, onDone) {
  const unitLevel = (u) => isUnitToken(u) ? 0 : (parseInt(u.level) || 0);
  const activeCandidates = state.playerField.filter(u => u && !u.rested);
  if (activeCandidates.length === 0) { addLog(`${card.name} — no active friendly Units.`); onDone(); return; }
  showCardPickerModal(
    card.name,
    'Choose 1 active friendly Unit. It will be rested.',
    activeCandidates,
    (rested) => {
      if (!rested) { onDone(); return; }
      rested.rested = true;
      NET.send({ type: 'UNIT_REST', uid: rested.uid, rested: true });
      addLog(`${card.name} — rested ${rested.name} (Lv.${unitLevel(rested)}).`);
      render(); setupDropZones();
      const restedLv = unitLevel(rested);
      const enemyCandidates = state.oppField.filter(c => c && unitLevel(c) <= restedLv);
      if (enemyCandidates.length === 0) {
        addLog(`${card.name} — no enemy Units with Lv.${restedLv} or lower to target.`);
        onDone();
        return;
      }
      showCardPickerModal(
        card.name,
        `Choose 1 enemy Unit (Lv.${restedLv} or lower). Deal 3 damage to it.`,
        enemyCandidates,
        (target) => {
          if (target) dealDamageToUnit(target, 3, { fromEnemy: true });
          onDone();
        },
        card,
        { mandatory: true }
      );
    },
    card,
    { mandatory: true }
  );
}
registerCardEffect('GD03-113', {
  canActivate: () => state.playerField.some(u => u && !u.rested) && state.oppField.some(c => c != null),
  main: (card, ctx) => gd03113Effect(card, ctx.onDone),
  action: (card, ctx) => gd03113Effect(card, ctx.onDone),
});

// ─── GD01-115 · Zeon Remnant Forces ──────────────────────────────────────
// [Main]/[Action] Choose 1 enemy Unit. Deal 1 damage to it.
function gd01115Effect(card, onDone) {
  const candidates = state.oppField.filter(c => c != null);
  if (candidates.length === 0) { addLog(`${card.name} — no enemy Units in play.`); onDone(); return; }
  showCardPickerModal(
    card.name,
    'Choose 1 enemy Unit. Deal 1 damage to it.',
    candidates,
    (target) => {
      if (target) dealDamageToUnit(target, 1, { fromEnemy: true });
      onDone();
    },
    card,
    { mandatory: true }
  );
}
registerCardEffect('GD01-115', {
  canActivate: () => state.oppField.some(c => c != null),
  main: (card, ctx) => gd01115Effect(card, ctx.onDone),
  action: (card, ctx) => gd01115Effect(card, ctx.onDone),
});

// ─── ST03-013 · Close Combat ─────────────────────────────────────────────
// [Burst] Activate this card's [Main].
// [Main]/[Action] Choose 1 enemy Unit. Deal 2 damage to it.
function st03013Effect(card, onDone) {
  const candidates = state.oppField.filter(c => c != null);
  if (candidates.length === 0) { addLog(`${card.name} — no enemy Units in play.`); onDone(); return; }
  showCardPickerModal(
    card.name,
    'Choose 1 enemy Unit. Deal 2 damage to it.',
    candidates,
    (target) => {
      if (target) dealDamageToUnit(target, 2, { fromEnemy: true });
      onDone();
    },
    card,
    { mandatory: true }
  );
}
registerCardEffect('ST03-013', {
  canActivate: () => state.oppField.some(c => c != null),
  burst: (card, ctx) => st03013Effect(card, ctx?.onDone ?? (() => {})),
  main: (card, ctx) => st03013Effect(card, ctx.onDone),
  action: (card, ctx) => st03013Effect(card, ctx.onDone),
});

// ─── GD02-109 · Undying Persistence ──────────────────────────────────────
// [Main]/[Action] Choose 1 enemy Unit. Deal 1 damage to it.
function gd02109Effect(card, onDone) {
  const candidates = state.oppField.filter(c => c != null);
  if (candidates.length === 0) { addLog(`${card.name} — no enemy Units in play.`); onDone(); return; }
  showCardPickerModal(
    card.name,
    'Choose 1 enemy Unit. Deal 1 damage to it.',
    candidates,
    (target) => {
      if (target) dealDamageToUnit(target, 1, { fromEnemy: true });
      onDone();
    },
    card,
    { mandatory: true }
  );
}
registerCardEffect('GD02-109', {
  canActivate: () => state.oppField.some(c => c != null),
  main: (card, ctx) => gd02109Effect(card, ctx.onDone),
  action: (card, ctx) => gd02109Effect(card, ctx.onDone),
});

// ─── GD04-111 · Trinity ──────────────────────────────────────────────────
// [Main]/[Action] Choose 1 to 3 friendly (CB) Units. They get AP+2 this turn.
function gd04111Effect(card, onDone) {
  const candidates = state.playerField.filter(u => u && (u.trait || '').includes('(CB)'));
  if (candidates.length === 0) { addLog(`${card.name} — no friendly (CB) Units in play.`); onDone(); return; }
  showCardPickerModalMulti(
    card.name,
    'Choose 1 to 3 friendly (CB) Units. They get AP+2 during this turn.',
    candidates,
    { min: 1, max: 3, mandatory: true },
    (chosen) => {
      if (chosen && chosen.length > 0) {
        chosen.forEach(u => { u.tempApMod = (u.tempApMod || 0) + 2; });
        NET.send({ type: 'FIELD_AP_MOD', uids: chosen.map(u => u.uid), delta: 2, field: 'tempApMod' });
        addLog(`${card.name} — ${chosen.map(u => u.name).join(', ')} each get AP+2 during this turn.`);
        render();
        setupDropZones();
      }
      onDone();
    },
    card
  );
}
registerCardEffect('GD04-111', {
  canActivate: () => state.playerField.some(u => u && (u.trait || '').includes('(CB)')),
  main: (card, ctx) => gd04111Effect(card, ctx.onDone),
  action: (card, ctx) => gd04111Effect(card, ctx.onDone),
});

// ─── ST08-012 · Words for Hathaway ──────────────────────────────────────
// [Main] Choose 1 friendly Link Unit. It gains <Breach 1> during this turn.
registerCardEffect('ST08-012', {
  canActivate: () => state.playerField.some(u => u && u.pilot),
  main: (card, ctx) => {
    const candidates = state.playerField.filter(u => u && u.pilot);
    if (candidates.length === 0) { addLog(`${card.name} — no friendly Link Units in play.`); ctx.onDone(); return; }
    showCardPickerModal(
      card.name,
      'Choose 1 friendly Link Unit. It gains <Breach 1> during this turn.',
      candidates,
      (target) => {
        if (target) {
          target.tempKeywords = target.tempKeywords || [];
          if (!target.tempKeywords.includes('Breach 1')) target.tempKeywords.push('Breach 1');
          NET.send({ type: 'TEMP_KEYWORD_ADD', uid: target.uid, keyword: 'Breach 1' });
          addLog(`${card.name} — ${target.name} gains <Breach 1> during this turn.`);
          render();
          setupDropZones();
        }
        ctx.onDone();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── ST04-014 · The Magic Bullet of Dusk ─────────────────────────────────
// [Main]/[Action] Choose 1 friendly Unit Lv.2 or lower. It gains <First Strike> this turn.
// Tokens are always treated as Lv.0 for level checks.
function st04014Effect(card, onDone) {
  const unitLevel = (u) => isUnitToken(u) ? 0 : (parseInt(u.level) || 0);
  const candidates = state.playerField.filter(u => u && unitLevel(u) <= 2);
  if (candidates.length === 0) { addLog(`${card.name} — no friendly Units Lv.2 or lower in play.`); onDone(); return; }
  showCardPickerModal(
    card.name,
    'Choose 1 friendly Unit that is Lv.2 or lower. It gains <First Strike> during this turn.',
    candidates,
    (target) => {
      if (target) {
        target.tempKeywords = target.tempKeywords || [];
        if (!target.tempKeywords.includes('First Strike')) target.tempKeywords.push('First Strike');
        NET.send({ type: 'TEMP_KEYWORD_ADD', uid: target.uid, keyword: 'First Strike' });
        addLog(`${card.name} — ${target.name} gains <First Strike> during this turn.`);
        render();
        setupDropZones();
      }
      onDone();
    },
    card,
    { mandatory: true }
  );
}
registerCardEffect('ST04-014', {
  canActivate: () => state.playerField.some(u => u && (isUnitToken(u) ? 0 : (parseInt(u.level) || 0)) <= 2),
  main: (card, ctx) => st04014Effect(card, ctx.onDone),
  action: (card, ctx) => st04014Effect(card, ctx.onDone),
});

// ─── GD01-113 · The Desert Tiger ─────────────────────────────────────────
// [Main]/[Action] Choose 1 friendly (ZAFT) Unit. It gets AP+3 during this turn.
function gd01113Effect(card, onDone) {
  const candidates = state.playerField.filter(u => u && (u.trait || '').includes('(ZAFT)'));
  if (candidates.length === 0) { addLog(`${card.name} — no friendly (ZAFT) Units in play.`); onDone(); return; }
  showCardPickerModal(
    card.name,
    'Choose 1 friendly (ZAFT) Unit. It gets AP+3 during this turn.',
    candidates,
    (target) => {
      if (target) {
        target.tempApMod = (target.tempApMod || 0) + 3;
        NET.send({ type: 'FIELD_AP_MOD', uids: [target.uid], delta: 3, field: 'tempApMod' });
        addLog(`${card.name} — ${target.name} gets AP+3 during this turn.`);
        render();
        setupDropZones();
      }
      onDone();
    },
    card,
    { mandatory: true }
  );
}
registerCardEffect('GD01-113', {
  canActivate: () => state.playerField.some(u => u && (u.trait || '').includes('(ZAFT)')),
  main: (card, ctx) => gd01113Effect(card, ctx.onDone),
  action: (card, ctx) => gd01113Effect(card, ctx.onDone),
});

// ─── GD01-116 · Stealth Stratagem ────────────────────────────────────────
// [Main]/[Action] Choose 1 enemy Unit with 2 or less AP. Deal 2 damage to it.
function gd01116Effect(card, onDone) {
  const candidates = state.oppField.filter(c => c && effectiveBp(c) <= 2);
  if (candidates.length === 0) { addLog(`${card.name} — no enemy Units with 2 or less AP.`); onDone(); return; }
  showCardPickerModal(
    card.name,
    'Choose 1 enemy Unit with 2 or less AP. Deal 2 damage to it.',
    candidates,
    (target) => {
      if (target) dealDamageToUnit(target, 2, { fromEnemy: true });
      onDone();
    },
    card,
    { mandatory: true }
  );
}
registerCardEffect('GD01-116', {
  canActivate: () => state.oppField.some(c => c && effectiveBp(c) <= 2),
  main: (card, ctx) => gd01116Effect(card, ctx.onDone),
  action: (card, ctx) => gd01116Effect(card, ctx.onDone),
});

// ─── ST03-012 · Indignation ──────────────────────────────────────────────
// [Main]/[Action] Choose 1 friendly Unit. It gets AP+2 during this turn.
function st03012Effect(card, onDone) {
  const candidates = state.playerField.filter(u => u != null);
  if (candidates.length === 0) { addLog(`${card.name} — no friendly Units in play.`); onDone(); return; }
  showCardPickerModal(
    card.name,
    'Choose 1 friendly Unit. It gets AP+2 during this turn.',
    candidates,
    (target) => {
      if (target) {
        target.tempApMod = (target.tempApMod || 0) + 2;
        NET.send({ type: 'FIELD_AP_MOD', uids: [target.uid], delta: 2, field: 'tempApMod' });
        addLog(`${card.name} — ${target.name} gets AP+2 during this turn.`);
        render();
        setupDropZones();
      }
      onDone();
    },
    card,
    { mandatory: true }
  );
}
registerCardEffect('ST03-012', {
  canActivate: () => state.playerField.some(u => u != null),
  main: (card, ctx) => st03012Effect(card, ctx.onDone),
  action: (card, ctx) => st03012Effect(card, ctx.onDone),
});

// ─── GD01-114 · Assault on Torrington Base ───────────────────────────────
// [Action] Choose 2 friendly Units. They get AP+1 during this turn.
// Requires at least 2 friendly units on the field to appear in the Action Step.
registerCardEffect('GD01-114', {
  canActivate: () => state.playerField.filter(u => u != null).length >= 2,
  action: (card, ctx) => {
    const candidates = state.playerField.filter(u => u != null);
    showCardPickerModalMulti(
      card.name,
      'Choose 2 friendly Units. They each get AP+1 during this turn.',
      candidates,
      { min: 2, max: 2, mandatory: true },
      (chosen) => {
        if (!chosen || chosen.length < 2) { ctx.onDone(); return; }
        chosen.forEach(u => { u.tempApMod = (u.tempApMod || 0) + 1; });
        NET.send({ type: 'FIELD_AP_MOD', uids: chosen.map(u => u.uid), delta: 1, field: 'tempApMod' });
        addLog(`${card.name} — ${chosen.map(u => u.name).join(' and ')} each get AP+1 during this turn.`);
        render();
        setupDropZones();
        ctx.onDone();
      },
      card
    );
  },
});

// ─── GD04-126 · Izuma Colony ─────────────────────────────────────────────
// [Deploy] Add 1 Shield to hand — handled generically by applyBaseDeployEffect().
// Passive: when this base takes battle damage from an enemy unit with 3 or less
// AP, deal 1 effect damage to that unit.
registerCardEffect('GD04-126', {
  onBaseTakesBattleDamage: (base, attacker, atkBp) => {
    if (atkBp > 3) return;
    addLog(`${base.name} — passive: ${attacker.name} has ${atkBp} AP (3 or less). Dealing 1 damage to it.`);
    dealDamageToUnit(attacker, 1, { fromEnemy: true });
  },
});

// ─── GD03-045 · Balient ──────────────────────────────────────────────────────
// While you have a Unit token in play, this Unit gets AP+1.
// passiveUnitApBonus is evaluated live inside effectiveBp(), so the bonus
// appears/disappears automatically as tokens enter or leave the owner's field.
registerCardEffect('GD03-045', {
  passiveUnitApBonus: (card, ownerSide) => {
    const field = ownerSide === 'player' ? state.playerField : state.oppField;
    return field.some(c => c && isUnitToken(c)) ? 1 : 0;
  },
});

// ─── GD04-034 · Gundam Kyrios ────────────────────────────────────────────────
// <First Strike> (native — handled by game.js).
// [During Link] This Unit gets AP+2 for each of your rested (CB) Units.
// passiveUnitApBonus is evaluated live in effectiveBp() so it automatically
// reflects the rested-CB count at the moment AP is read (e.g. in runDamageStep).
registerCardEffect('GD04-034', {
  passiveUnitApBonus: (card, ownerSide) => {
    if (!isLinkUnit(card)) return 0;
    const field = ownerSide === 'player' ? state.playerField : state.oppField;
    return field.filter(c => c && c.rested && (c.trait || '').includes('(CB)')).length * 2;
  },
});

// ─── GD04-037 · Gundam Kyrios (Trans-Am) ─────────────────────────────────────
// Passive: While you control a red Pilot with (Super Soldier) trait → <First Strike>.
// Passive: While you control a green Pilot with (Super Soldier) trait → <Breach 3>.
// Both conditions are checked live so the keywords appear/disappear as pilots change.
// conditionalBreachValue returns 0 (not undefined) when inactive to suppress the
// literal "Breach 3" in the effect text that getBreachValue would otherwise parse.
registerCardEffect('GD04-037', {
  passiveKeywords: (card, ownerSide) => {
    const field = ownerSide === 'player' ? state.playerField : state.oppField;
    const hasRedSuperSoldierPilot = field.some(c =>
      c?.pilot &&
      (c.pilot.color || '').toLowerCase() === 'red' &&
      (c.pilot.trait || '').includes('(Super Soldier)')
    );
    return hasRedSuperSoldierPilot ? ['First Strike'] : [];
  },
  conditionalBreachValue: (card) => {
    const ownerSide = state.playerField.some(c => c && c.uid === card.uid) ? 'player' : 'opp';
    const field = ownerSide === 'player' ? state.playerField : state.oppField;
    const hasGreenSuperSoldierPilot = field.some(c =>
      c?.pilot &&
      (c.pilot.color || '').toLowerCase() === 'green' &&
      (c.pilot.trait || '').includes('(Super Soldier)')
    );
    return hasGreenSuperSoldierPilot ? 3 : 0;
  },
});

// ─── GD04-045 · Gundam Throne Zwei ───────────────────────────────────────────
// [When Linked] Choose 1 of your (CB) Units. During this turn, it may choose a
// damaged active enemy Unit as its attack target.
// 'CanAttackActiveDamaged' is a synthetic keyword checked at game.js line 6365
// inside setupDropZones' target-building loop — tempKeywords carries it so it's
// mirrored to the peer via TEMP_KEYWORD_ADD and cleared by UNREST_ALL.
// Gate: isLinkUnit(ctx.unit) ensures "When Linked" only fires when the pilot
// satisfies the unit's Link requirement.
registerCardEffect('GD04-045', {
  pair: (card, ctx) => {
    if (!isLinkUnit(ctx?.unit || card)) return;
    const candidates = state.playerField.filter(c => c && (c.trait || '').includes('(CB)'));
    if (candidates.length === 0) return;
    showCardPickerModal(
      card.name,
      'Choose 1 of your (CB) Units. It may target a damaged active enemy Unit this turn.',
      candidates,
      (target) => {
        if (!target) return;
        target.tempKeywords = target.tempKeywords || [];
        if (!target.tempKeywords.includes('CanAttackActiveDamaged')) target.tempKeywords.push('CanAttackActiveDamaged');
        NET.send({ type: 'TEMP_KEYWORD_ADD', uid: target.uid, keyword: 'CanAttackActiveDamaged' });
        addLog(`${card.name} — [When Linked] ${target.name} can target damaged active enemy Units this turn.`);
        render(); setupDropZones();
      },
      card, { mandatory: true }
    );
  },
});

// ─── GD03-048 · GFreD ────────────────────────────────────────────────────────
// [Burst] If there are 3 or less enemy Shields, deploy 1 rested
// [GFreD]((Zeon)·AP4·HP3) Unit token (T-020).
// state.oppShield is the opponent's shield counter (number, not array).
registerCardEffect('GD03-048', {
  burst: (card, ctx) => {
    if (state.oppShield > 3) {
      addLog(`${card.name} — [Burst] enemy has ${state.oppShield} Shields (need 3 or fewer). No effect.`);
      return;
    }
    const token = deployTokenToField('T-020');
    if (!token) { addLog(`${card.name} — [Burst] field is full, GFreD token not deployed.`); return; }
    token.rested = true;
    NET.send({ type: 'UNIT_REST', uid: token.uid, rested: true });
    addLog(`${card.name} — [Burst] enemy has ${state.oppShield} Shields: deployed rested GFreD token.`);
    render();
    setupDropZones();
  },
});

// ─── GD03-044 · Daughtress Flyer ─────────────────────────────────────────────
// [Deploy] Deploy 1 rested [Daughtress] ((New UNE)·AP0·HP1) Unit token (T-012).
// No condition — unconditional deploy on play.
registerCardEffect('GD03-044', {
  deploy: (card) => {
    const token = deployTokenToField('T-012');
    if (!token) return;
    token.rested = true;
    NET.send({ type: 'UNIT_REST', uid: token.uid, rested: true });
    addLog(`${card.name} — deployed rested Daughtress token.`);
    render();
    setupDropZones();
  },
});

// ─── GD01-049 · Blitz Gundam ─────────────────────────────────────────────────
// [Deploy] Choose 1 of your (ZAFT) Units with 5 or more AP. It gains
// <First Strike> during this turn.
registerCardEffect('GD01-049', {
  deploy: (card) => {
    const candidates = state.playerField.filter(c => c && (c.trait || '').includes('(ZAFT)') && effectiveBp(c) >= 5);
    if (candidates.length === 0) {
      addLog(`${card.name} — no friendly (ZAFT) Units with 5 or more AP.`); return;
    }
    showCardPickerModal(
      card.name,
      'Choose 1 of your (ZAFT) Units with 5 or more AP. It gains <First Strike> during this turn.',
      candidates,
      (target) => {
        if (!target) return;
        target.tempKeywords = target.tempKeywords || [];
        if (!target.tempKeywords.includes('First Strike')) target.tempKeywords.push('First Strike');
        NET.send({ type: 'TEMP_KEYWORD_ADD', uid: target.uid, keyword: 'First Strike' });
        addLog(`${card.name} — ${target.name} gains <First Strike> during this turn.`);
        render();
      },
      card, { mandatory: true }
    );
  },
});

// ─── GD02-039 · Haman Karn's Gaza C ─────────────────────────────────────────
// [When Paired] Choose 1 enemy Unit that is Lv.3 or lower. Deal 1 damage to it.
// pair fires on both pilot and unit (PAIR_PILOT dispatch, line 4452-4453 game.js).
registerCardEffect('GD02-039', {
  pair: (card, ctx) => {
    const candidates = state.oppField.filter(c => c && parseInt(c.level) <= 3);
    if (candidates.length === 0) {
      addLog(`${card.name} — no enemy Units Lv.3 or lower.`); return;
    }
    showCardPickerModal(
      card.name,
      'Choose 1 enemy Unit that is Lv.3 or lower. Deal 1 damage to it.',
      candidates,
      (target) => {
        if (!target) return;
        dealDamageToUnit(target, 1, { fromEnemy: true });
        render();
      },
      card, { mandatory: true }
    );
  },
});

// ─── GD04-043 · Zssa (Sleeves) ───────────────────────────────────────────────
// [Deploy] Choose 1 enemy Base. Deal 1 damage to it.
// Single mandatory target — no picker needed.
registerCardEffect('GD04-043', {
  deploy: (card) => {
    if (!state.oppBase) { addLog(`${card.name} — no enemy Base in play.`); return; }
    addLog(`${card.name} — dealing 1 damage to ${state.oppBase.name}.`);
    dealDamageToBase('opp', 1, { fromEnemy: true });
    render();
    setupDropZones();
  },
});

// ─── GD02-042 · Gundam Ashtaron (MA Mode) ────────────────────────────────────
// [Deploy] Choose 1 of your (New UNE) Units. It gains <High-Maneuver> this turn.
// Card itself is always a valid target (it's on the field when deploy fires).
registerCardEffect('GD02-042', {
  deploy: (card) => {
    const candidates = state.playerField.filter(c => c && (c.trait || '').includes('(New UNE)'));
    if (candidates.length === 0) return;
    showCardPickerModal(
      card.name,
      'Choose 1 of your (New UNE) Units. It gains <High-Maneuver> during this turn.',
      candidates,
      (target) => {
        if (!target) return;
        target.tempKeywords = target.tempKeywords || [];
        if (!target.tempKeywords.includes('High-Maneuver')) target.tempKeywords.push('High-Maneuver');
        NET.send({ type: 'TEMP_KEYWORD_ADD', uid: target.uid, keyword: 'High-Maneuver' });
        addLog(`${card.name} — ${target.name} gains <High-Maneuver> this turn.`);
        render();
      },
      card, { mandatory: true }
    );
  },
});

// ─── GD04-038 · Gundam Exia ──────────────────────────────────────────────────
// [Deploy] If 2 or more enemy Units are in play, choose 1 enemy Unit that is
// Lv.2 or lower. Deal 2 damage to it.
registerCardEffect('GD04-038', {
  deploy: (card) => {
    const enemyUnits = state.oppField.filter(c => c);
    if (enemyUnits.length < 2) return;
    const candidates = state.oppField.filter(c => c && parseInt(c.level) <= 2);
    if (candidates.length === 0) {
      addLog(`${card.name} — 2+ enemy Units in play but none are Lv.2 or lower.`); return;
    }
    showCardPickerModal(
      card.name,
      'Choose 1 enemy Unit that is Lv.2 or lower. Deal 2 damage to it.',
      candidates,
      (target) => {
        if (!target) return;
        dealDamageToUnit(target, 2, { fromEnemy: true });
        render();
      },
      card, { mandatory: true }
    );
  },
});

// ─── GD01-052 · Geara Zulu (Guards Type) ────────────────────────────────────
// [Deploy] Choose 1 enemy Unit. Deal 1 damage to it.
registerCardEffect('GD01-052', {
  deploy: (card) => {
    const candidates = state.oppField.filter(c => c);
    if (candidates.length === 0) return;
    showCardPickerModal(
      card.name,
      'Choose 1 enemy Unit. Deal 1 damage to it.',
      candidates,
      (target) => {
        if (!target) return;
        dealDamageToUnit(target, 1, { fromEnemy: true });
        render();
      },
      card, { mandatory: true }
    );
  },
});

// ─── ST06-002 · GQuuuuuuX (Omega Psycommu) ───────────────────────────────────
// [Deploy] If another friendly (Clan) Unit is in play, choose 1 enemy Unit.
// Deal 1 damage to it.
registerCardEffect('ST06-002', {
  deploy: (card) => {
    const hasOtherClan = state.playerField.some(c => c && c.uid !== card.uid && (c.trait || '').includes('(Clan)'));
    if (!hasOtherClan) return;
    const candidates = state.oppField.filter(c => c);
    if (candidates.length === 0) {
      addLog(`${card.name} — no enemy Units to target.`); return;
    }
    showCardPickerModal(
      card.name,
      'Choose 1 enemy Unit. Deal 1 damage to it.',
      candidates,
      (target) => {
        if (!target) return;
        dealDamageToUnit(target, 1, { fromEnemy: true });
        render();
      },
      card, { mandatory: true }
    );
  },
});

// ─── GD01-053 · Geara Doga (Heavy Armed Type) ────────────────────────────────
// [Activate･Main][Once per Turn]①:Choose 1 enemy Unit with 2 or less AP.
// Deal 1 damage to it.
// getUnitActivationCost parses ① and [Once per Turn] automatically;
// showUnitActivateModal spends the RES and sets activateMainUsedThisTurn
// before the activate handler runs — no manual cost payment needed here.
registerCardEffect('GD01-053', {
  canActivate: (card) => state.oppField.some(c => c && effectiveBp(c) <= 2),
  activate: (card, ctx) => {
    const candidates = state.oppField.filter(c => c && effectiveBp(c) <= 2);
    if (candidates.length === 0) {
      addLog(`${card.name} — no enemy Units with 2 or less AP.`); ctx.onDone(); return;
    }
    showCardPickerModal(
      card.name,
      'Choose 1 enemy Unit with 2 or less AP. Deal 1 damage to it.',
      candidates,
      (target) => {
        if (target) dealDamageToUnit(target, 1, { fromEnemy: true });
        render();
        ctx.onDone();
      },
      card, { mandatory: true }
    );
  },
});

// ─── GD02-043 · Daughtress Weapon ────────────────────────────────────────────
// [Deploy] If you have another (New UNE) Unit in play, deploy 1 rested
// [Daughtress] ((New UNE)·AP0·HP1) Unit token (cardNo T-012).
// deploy fires after the card is placed on the field (local only), so card.uid
// must be excluded when checking for "another" (New UNE) unit.
registerCardEffect('GD02-043', {
  deploy: (card) => {
    const hasOtherNewUNE = state.playerField.some(c => c && c.uid !== card.uid && (c.trait || '').includes('(New UNE)'));
    if (!hasOtherNewUNE) return;
    const token = deployTokenToField('T-012');
    if (!token) return;
    token.rested = true;
    NET.send({ type: 'UNIT_REST', uid: token.uid, rested: true });
    addLog(`${card.name} — deployed rested Daughtress token.`);
    render();
    setupDropZones();
  },
});

// ─── GD02-044 · Daughtress Command ───────────────────────────────────────────
// [Destroyed] If you have another (New UNE) Unit in play, deploy 1 rested
// [Daughtress] ((New UNE)·AP0·HP1) Unit token (cardNo T-012).
// Combat destruction fires on both clients — gate to owner's client via the
// GD01-080 pattern (white.js). Effect-damage destruction already only fires
// on the player's client (game.js line 3243-3244), so ctx.defenderSide is
// undefined there and the combat gate is skipped. By the time the trigger
// fires the unit is already removed from playerField, so hasOtherNewUNE
// correctly counts only the remaining units. deployTokenToField places the
// token active; we rest it immediately after with UNIT_REST so the peer sees
// it rested from the start.
registerCardEffect('GD02-044', {
  destroyed: (card, ctx) => {
    if (ctx && ctx.defenderSide !== undefined) {
      const ownerSide = card.uid === ctx.defender?.uid ? ctx.defenderSide : ctx.attackerSide;
      if (ownerSide !== 'player') return;
    }
    const hasOtherNewUNE = state.playerField.some(c => c && (c.trait || '').includes('(New UNE)'));
    if (!hasOtherNewUNE) {
      addLog(`${card.name} — no other (New UNE) Unit in play. No token deployed.`);
      return;
    }
    addLog(`${card.name} — deploying rested Daughtress token.`);
    const token = deployTokenToField('T-012');
    if (!token) {
      addLog(`${card.name} — field is full, token not deployed.`);
      return;
    }
    token.rested = true;
    NET.send({ type: 'UNIT_REST', uid: token.uid, rested: true });
    render();
    setupDropZones();
  },
});

// ─── GD02-047 · Gaza C ───────────────────────────────────────────────────────
// [Activate･Main] Rest this Unit: Destroy this and choose 1 enemy Unit
// that is Lv.5 or lower. Deal 1 damage to it.
// The "Rest this Unit" is the cost; since the unit is immediately destroyed,
// resting separately is not meaningful. dispatch('DISCARD_CARD') covers field
// slot cleanup, updateTempState, trash bookkeeping, and the DISCARD_CARD
// network message so the peer sets their oppField[slot] = null.
registerCardEffect('GD02-047', {
  canActivate: (card) => {
    if (card.rested) return false;
    return state.oppField.some(c => c && (parseInt(c.level) || 0) <= 5);
  },
  activate: (card, ctx) => {
    const slot = state.playerField.findIndex(c => c && c.uid === card.uid);
    if (slot === -1) { ctx.onDone(); return; }
    dispatch('DISCARD_CARD', { uid: card.uid, source: 'field', slot });
    broadcastCounters();
    render();
    setupDropZones();
    const candidates = state.oppField.filter(c => c && (parseInt(c.level) || 0) <= 5);
    if (candidates.length === 0) {
      addLog(`${card.name} — no enemy Units Lv.5 or lower to target.`);
      ctx.onDone();
      return;
    }
    showCardPickerModal(
      card.name,
      'Choose 1 enemy Unit that is Lv.5 or lower. Deal 1 damage to it.',
      candidates,
      (target) => {
        if (target) dealDamageToUnit(target, 1, { fromEnemy: true });
        ctx.onDone();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── GD04-044 · Gadeel ───────────────────────────────────────────────────────
// [Attack] If you are attacking a damaged enemy Unit, this Unit gains <Breach 3>
// during this battle.
// conditionalBreachValue returns 0 to suppress unconditional text-parse detection;
// attack trigger adds 'Breach 3' to tempKeywords when the condition is met.
registerCardEffect('GD04-044', {
  conditionalBreachValue: () => 0,
  attack: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    if (ctx.isShield || ctx.isDirect) { ctx.onDone(); return; }
    if (!ctx.defender?.damaged) { ctx.onDone(); return; }
    card.tempKeywords = card.tempKeywords || [];
    if (!card.tempKeywords.includes('Breach 3')) card.tempKeywords.push('Breach 3');
    NET.send({ type: 'TEMP_KEYWORD_ADD', uid: card.uid, keyword: 'Breach 3' });
    addLog(`${card.name} — attacking a damaged enemy Unit: gains <Breach 3> during this battle.`);
    render();
    ctx.onDone();
  },
});

// ─── GD01-063 · ZnO ──────────────────────────────────────────────────────────
// During your turn, while this Unit is battling an enemy Unit that is Lv.2 or
// lower, it gains <First Strike>.
// Granted via attack trigger (fires before Block Step) so the keyword is in
// place before runDamageStep reads hasNativeKeyword(attacker, 'First Strike').
// If a Blocker intercepts after the trigger fires, First Strike already applies
// and persists through the rest of the battle (tempKeyword cleared by UNREST_ALL).
registerCardEffect('GD01-063', {
  attack: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    if (ctx.isShield || ctx.isDirect) { ctx.onDone(); return; }
    const defLevel = parseInt(ctx.defender?.level) || 0;
    if (defLevel > 2) { ctx.onDone(); return; }
    card.tempKeywords = card.tempKeywords || [];
    if (!card.tempKeywords.includes('First Strike')) card.tempKeywords.push('First Strike');
    NET.send({ type: 'TEMP_KEYWORD_ADD', uid: card.uid, keyword: 'First Strike' });
    addLog(`${card.name} — battling a Lv.${defLevel} enemy Unit: gains <First Strike>.`);
    render();
    ctx.onDone();
  },
});

// ─── GD01-058 · Galluss-K ────────────────────────────────────────────────────
// [Activate･Action][Once per Turn]①:Choose 1 Unit that is Lv.4 or higher.
// It gets AP+1 during this battle.
// "Unit" is unqualified — any unit (either field) at Lv.4+ is a valid target.
// Cost spent inside action handler (same pattern as GD01-082).
registerCardEffect('GD01-058', {
  canAction: (card, ctx) => {
    if ((state.res + state.exRes) < 1) return false;
    return state.playerField.some(c => c && parseInt(c.level) >= 4)
        || state.oppField.some(c => c && parseInt(c.level) >= 4);
  },
  action: (card, ctx) => {
    if ((state.res + state.exRes) < 1) {
      addLog(`${card.name} — need ① RES to activate (have ${state.res + state.exRes}).`);
      ctx.onDone(); return true;
    }
    const candidates = [
      ...state.playerField.filter(c => c && parseInt(c.level) >= 4),
      ...state.oppField.filter(c => c && parseInt(c.level) >= 4),
    ];
    if (candidates.length === 0) {
      addLog(`${card.name} — no Lv.4+ Units in play.`); ctx.onDone(); return true;
    }
    spendResources(1);
    broadcastCounters();
    showCardPickerModal(
      card.name,
      'Choose 1 Unit that is Lv.4 or higher. It gets AP+1 during this battle.',
      candidates,
      (target) => {
        if (!target) { ctx.onDone(); return; }
        target.battleApMod = (target.battleApMod || 0) + 1;
        NET.send({ type: 'FIELD_AP_MOD', uids: [target.uid], delta: 1, field: 'battleApMod' });
        addLog(`${card.name} — ${target.name} gets AP+1 during this battle.`);
        render(); ctx.onDone();
      },
      card, { mandatory: true }
    );
  },
});

// ─── GD01-059 · Zee Zulu ─────────────────────────────────────────────────────
// [Attack] If you are attacking the enemy base or player (shield/direct), this
// Unit gets AP+2 during this battle.
// "Attacking the enemy player" covers Base/Shield/Direct attacks — anything
// that isn't targeting an enemy Unit — see isAttackingEnemyPlayer() in white.js.
registerCardEffect('GD01-059', {
  attack: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    if (!isAttackingEnemyPlayer(ctx)) { ctx.onDone(); return; }
    card.battleApMod = (card.battleApMod || 0) + 2;
    NET.send({ type: 'FIELD_AP_MOD', uids: [card.uid], delta: 2, field: 'battleApMod' });
    addLog(`${card.name} — [Attack] attacking base/shield/player: gets AP+2 during this battle.`);
    render();
    ctx.onDone();
  },
});

// ─── GD01-048 · Zaku I Sniper Type ───────────────────────────────────────────
// [Activate･Main]<Support 1> — same pattern as GD02-049.
// [Deploy] Look at the top card of your deck. If it is a (Zeon)/(Neo Zeon)
// Unit card, you may reveal it and add it to your hand. Return any remaining
// card to the bottom of your deck.
// Peek/prompt/reveal machinery mirrors ST06-009 (Amate Yuzuriha); filter is
// (Zeon)/(Neo Zeon) Unit instead of (Clan).
registerCardEffect('GD01-048', {
  canActivate: (card) => {
    if (card.rested) return false;
    return state.playerField.some(c => c && c.uid !== card.uid);
  },
  activate: (card, ctx) => {
    const value = parseInt((card.nativeKeyword || '').match(/Support (\d+)/)?.[1]) || 0;
    setRested(card, true);
    const candidates = state.playerField.filter(c => c && c.uid !== card.uid);
    pickAndApplyApMod(
      card,
      candidates,
      value,
      'tempApMod',
      `Choose 1 other friendly Unit. It gets AP+${value} during this turn.`,
      ctx.onDone
    );
  },
  deploy: (card) => {
    const top = peekTopOfDeck(1)[0];
    if (!top) {
      addLog(`${card.name} — deck is empty, nothing to look at.`);
      return;
    }
    const { entry, card: peeked } = top;
    const isQualifying = peeked.type === 'unit' &&
      ((peeked.trait || '').includes('(Zeon)') || (peeked.trait || '').includes('(Neo Zeon)'));
    showTopDeckPeekPrompt(card, peeked, isQualifying, (addToHand) => {
      if (isQualifying && addToHand) {
        entry.state = 'hand';
        state.hand.push(peeked);
        state.deckCount = state.tempDeck.filter(isInDeck).length;
        addLog(`${card.name} — revealed ${peeked.name} and added it to hand.`);
        NET.send({ type: 'CARD_REVEAL', card: peeked, pilotName: card.name });
        broadcastCardToast(`${peeked.name} was added to hand due to ${card.name} effect.`);
      } else {
        sendDeckEntryToBottom(entry);
        addLog(`${card.name} — looked at the top card and returned it to the bottom of the deck.`);
      }
      broadcastCounters();
      render();
      setupDropZones();
    });
  },
});

// ─── ST04-009 · Miguel's Ginn ────────────────────────────────────────────────
// [During Pair][Destroyed] If you have another Link Unit in play, draw 1.
// owner-side gate: same GD01-080 pattern as GD02-044.
// [During Pair]: card.pilot is still attached at trigger time (not nulled until
// runEpilogue), so isLinkUnit(card) correctly reflects link status at death.
// "another Link Unit": card is already removed from playerField, so the check
// covers only the remaining units.
registerCardEffect('ST04-009', {
  destroyed: (card, ctx) => {
    if (ctx && ctx.defenderSide !== undefined) {
      const ownerSide = card.uid === ctx.defender?.uid ? ctx.defenderSide : ctx.attackerSide;
      if (ownerSide !== 'player') return;
    }
    if (!isLinkUnit(card)) return;
    if (!state.playerField.some(c => c && isLinkUnit(c))) return;
    const drawn = drawCardsFromTempDeck(1, true);
    if (drawn.length > 0) {
      state.hand.push(drawn[0]);
      addLog(`${card.name} — [During Pair][Destroyed] drew ${drawn[0].name}.`);
    } else {
      addLog(`${card.name} — [During Pair][Destroyed] deck is empty, no draw.`);
    }
    broadcastCounters();
    render();
  },
});

// ─── GD01-056 · Geara Doga (Sleeves) ─────────────────────────────────────────
// [Destroyed] Choose 1 enemy Unit with 5 or less AP. Deal 1 damage to it.
registerCardEffect('GD01-056', {
  destroyed: (card, ctx) => {
    if (ctx && ctx.defenderSide !== undefined) {
      const ownerSide = card.uid === ctx.defender?.uid ? ctx.defenderSide : ctx.attackerSide;
      if (ownerSide !== 'player') return;
    }
    const candidates = state.oppField.filter(c => c && effectiveBp(c) <= 5);
    if (candidates.length === 0) {
      addLog(`${card.name} — no enemy Units with 5 or less AP.`); return;
    }
    showCardPickerModal(
      card.name,
      'Choose 1 enemy Unit with 5 or less AP. Deal 1 damage to it.',
      candidates,
      (target) => {
        if (!target) return;
        dealDamageToUnit(target, 1, { fromEnemy: true });
        render();
      },
      card, { mandatory: true }
    );
  },
});

// ─── ST08-004 · Messer Type-F01 ──────────────────────────────────────────────
// [Attack] If this Unit is attacking an enemy Unit, choose 1 enemy Unit.
// Deal 1 damage to it.
// Fires via fireAttackerUnitAttackTrigger. If this kills ctx.defender,
// runDamageStep detects the missing defender and exits cleanly.
registerCardEffect('ST08-004', {
  attack: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    if (ctx.isShield || ctx.isDirect) { ctx.onDone(); return; }
    const candidates = state.oppField.filter(c => c != null);
    if (candidates.length === 0) { ctx.onDone(); return; }
    showCardPickerModal(
      card.name,
      'Choose 1 enemy Unit. Deal 1 damage to it.',
      candidates,
      (target) => {
        if (target) dealDamageToUnit(target, 1, { fromEnemy: true });
        ctx.onDone();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── GD01-050 · LaGOWE ───────────────────────────────────────────────────────
// [Attack] If this Unit has 5 or more AP and it is attacking an enemy Unit,
// choose 1 enemy Unit. Deal 2 damage to it.
registerCardEffect('GD01-050', {
  attack: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    if (ctx.isShield || ctx.isDirect) { ctx.onDone(); return; }
    if (effectiveBp(card) < 5) { ctx.onDone(); return; }
    const candidates = state.oppField.filter(c => c);
    if (candidates.length === 0) { ctx.onDone(); return; }
    showCardPickerModal(
      card.name,
      'Choose 1 enemy Unit. Deal 2 damage to it.',
      candidates,
      (target) => {
        if (target) dealDamageToUnit(target, 2, { fromEnemy: true });
        ctx.onDone();
      },
      card, { mandatory: true }
    );
  },
});

// ─── ST04-006 · Aegis Gundam ─────────────────────────────────────────────────
// [Attack] If this Unit has 5 or more AP, choose 1 enemy Unit that is Lv.5 or
// higher. Deal 3 damage to it.
// No "attacking an enemy Unit" qualifier — fires on any attack target.
registerCardEffect('ST04-006', {
  attack: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    if (effectiveBp(card) < 5) { ctx.onDone(); return; }
    const candidates = state.oppField.filter(c => c && parseInt(c.level) >= 5);
    if (candidates.length === 0) { ctx.onDone(); return; }
    showCardPickerModal(
      card.name,
      'Choose 1 enemy Unit that is Lv.5 or higher. Deal 3 damage to it.',
      candidates,
      (target) => {
        if (target) dealDamageToUnit(target, 3, { fromEnemy: true });
        ctx.onDone();
      },
      card, { mandatory: true }
    );
  },
});

// ─── GD02-045 · GINN Long-Range Reconnaissance Type ──────────────────────────
// [Attack] If this Unit has 5 or more AP and it is attacking an enemy Unit,
// draw 1.
// Fires via fireAttackerUnitAttackTrigger (game.js) which already gates to
// attackerSide === 'player'. ctx.onDone must be called on every path to keep
// the attack pipeline moving.
registerCardEffect('GD02-045', {
  attack: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    if (ctx.isShield || ctx.isDirect) { ctx.onDone(); return; }
    if (effectiveBp(card) < 5) { ctx.onDone(); return; }
    const drawn = drawCardsFromTempDeck(1, true);
    if (drawn.length > 0) {
      state.hand.push(drawn[0]);
      addLog(`${card.name} — [Attack] 5 or more AP attacking a Unit: drew ${drawn[0].name}.`);
    } else {
      addLog(`${card.name} — [Attack] deck is empty, no draw.`);
    }
    broadcastCounters();
    render();
    ctx.onDone();
  },
});

// ─── GD01-054 · Duel Gundam ──────────────────────────────────────────────────
// While this Unit has 5 or more AP, it gains <Breach 3>.
// getBreachValue() reads "Breach 3" from card.effect unconditionally, so a
// conditionalBreachValue hook (added to game.js) is used to override the
// text-based parse. tempKeywords-based grants (e.g. "gains <Breach N> this
// turn") still take priority since getBreachValue checks them first.
registerCardEffect('GD01-054', {
  conditionalBreachValue: (card) => effectiveBp(card) >= 5 ? 3 : 0,
});

// ─── GD03-038 · GuAIZ (Commander Type) ───────────────────────────────────────
// [Activate･Main]<Support 1>
// During your turn, when this Unit is rested by an effect, choose 1 of your
// (ZAFT) Units. It gets AP+2 during this turn.
// The "rested by effect" bonus is baked into the activate handler (the engine
// has no generic on-rest trigger for units; the only practical trigger is
// the Support activation itself).
registerCardEffect('GD03-038', {
  canActivate: (card) => {
    if (card.rested) return false;
    return state.playerField.some(c => c && c.uid !== card.uid);
  },
  activate: (card, ctx) => {
    const value = parseInt((card.nativeKeyword || '').match(/Support (\d+)/)?.[1]) || 0;
    setRested(card, true);
    const supportCandidates = state.playerField.filter(c => c && c.uid !== card.uid);
    if (supportCandidates.length === 0) { addLog(`${card.name} — no other friendly Units.`); ctx.onDone(); return; }
    showCardPickerModal(
      card.name,
      `Choose 1 other friendly Unit. It gets AP+${value} during this turn.`,
      supportCandidates,
      (supportTarget) => {
        if (!supportTarget) { ctx.onDone(); return; }
        applyApMod([supportTarget], value, 'tempApMod');
        addLog(`${card.name} — ${supportTarget.name} gets AP+${value} during this turn.`);
        const zaftCandidates = state.playerField.filter(c => c && (c.trait || '').includes('(ZAFT)'));
        if (zaftCandidates.length === 0) { render(); setupDropZones(); ctx.onDone(); return; }
        showCardPickerModal(
          card.name,
          'Choose 1 of your (ZAFT) Units. It gets AP+2 during this turn.',
          zaftCandidates,
          (zaftTarget) => {
            if (zaftTarget) {
              applyApMod([zaftTarget], 2, 'tempApMod');
              addLog(`${card.name} — [rested by effect] ${zaftTarget.name} gets AP+2 during this turn.`);
            }
            render(); setupDropZones(); ctx.onDone();
          },
          card
        );
      },
      card
    );
  },
});

// ─── GD02-040 · Gundam Ashtaron ──────────────────────────────────────────────
// [Activate･Main]<Support 2>
// [Deploy] Choose 1 of your other (New UNE) Units. It can't receive battle
// damage from enemy Units with 2 or less HP during this turn.
// Immunity is stored as the synthetic tempKeyword 'NoBattleDamageFromHP2',
// cleared by UNREST_ALL. game.js runDamageStep checks this before applying
// non-lethal damage to the defender.
registerCardEffect('GD02-040', {
  canActivate: (card) => {
    if (card.rested) return false;
    return state.playerField.some(c => c && c.uid !== card.uid);
  },
  activate: (card, ctx) => {
    const value = parseInt((card.nativeKeyword || '').match(/Support (\d+)/)?.[1]) || 0;
    setRested(card, true);
    const candidates = state.playerField.filter(c => c && c.uid !== card.uid);
    pickAndApplyApMod(
      card, candidates, value, 'tempApMod',
      `Choose 1 other friendly Unit. It gets AP+${value} during this turn.`,
      ctx.onDone
    );
  },
  deploy: (card) => {
    const candidates = state.playerField.filter(c => c && c.uid !== card.uid && (c.trait || '').includes('(New UNE)'));
    if (candidates.length === 0) {
      addLog(`${card.name} — no other (New UNE) Units to protect.`); return;
    }
    showCardPickerModal(
      card.name,
      'Choose 1 of your other (New UNE) Units. It can\'t receive battle damage from enemy Units with 2 or less HP this turn.',
      candidates,
      (target) => {
        if (!target) return;
        target.tempKeywords = target.tempKeywords || [];
        if (!target.tempKeywords.includes('NoBattleDamageFromHP2')) target.tempKeywords.push('NoBattleDamageFromHP2');
        NET.send({ type: 'TEMP_KEYWORD_ADD', uid: target.uid, keyword: 'NoBattleDamageFromHP2' });
        addLog(`${card.name} — ${target.name} is protected from battle damage by Units with 2 or less HP this turn.`);
        render();
      },
      card, { mandatory: true }
    );
  },
});

// ─── ST03-002 · Angelo's Geara Zulu ──────────────────────────────────────────
// [Activate･Main]<Support 2> — same pattern as GD02-049; value parsed from nativeKeyword.
registerCardEffect('ST03-002', {
  canActivate: (card) => {
    if (card.rested) return false;
    return state.playerField.some(c => c && c.uid !== card.uid);
  },
  activate: (card, ctx) => {
    const value = parseInt((card.nativeKeyword || '').match(/Support (\d+)/)?.[1]) || 0;
    setRested(card, true);
    const candidates = state.playerField.filter(c => c && c.uid !== card.uid);
    pickAndApplyApMod(
      card,
      candidates,
      value,
      'tempApMod',
      `Choose 1 other friendly Unit. It gets AP+${value} during this turn.`,
      ctx.onDone
    );
  },
});

// ─── GD01-055 · BuCUE ────────────────────────────────────────────────────────
// [Activate･Main]<Support 2> — same pattern as GD02-049; value parsed from nativeKeyword.
registerCardEffect('GD01-055', {
  canActivate: (card) => {
    if (card.rested) return false;
    return state.playerField.some(c => c && c.uid !== card.uid);
  },
  activate: (card, ctx) => {
    const value = parseInt((card.nativeKeyword || '').match(/Support (\d+)/)?.[1]) || 0;
    setRested(card, true);
    const candidates = state.playerField.filter(c => c && c.uid !== card.uid);
    pickAndApplyApMod(
      card,
      candidates,
      value,
      'tempApMod',
      `Choose 1 other friendly Unit. It gets AP+${value} during this turn.`,
      ctx.onDone
    );
  },
});

// ─── ST03-004 · Gaza D (Sleeves) ─────────────────────────────────────────────
// [Activate･Main]<Support 2> — same pattern as GD02-049; value parsed from nativeKeyword.
registerCardEffect('ST03-004', {
  canActivate: (card) => {
    if (card.rested) return false;
    return state.playerField.some(c => c && c.uid !== card.uid);
  },
  activate: (card, ctx) => {
    const value = parseInt((card.nativeKeyword || '').match(/Support (\d+)/)?.[1]) || 0;
    setRested(card, true);
    const candidates = state.playerField.filter(c => c && c.uid !== card.uid);
    pickAndApplyApMod(
      card,
      candidates,
      value,
      'tempApMod',
      `Choose 1 other friendly Unit. It gets AP+${value} during this turn.`,
      ctx.onDone
    );
  },
});

// ─── ST06-003 · Gaia's Rick Dom (GQ) ─────────────────────────────────────────
// [Activate･Main]<Support 1> — identical effect to GD02-049 Daughtress.
registerCardEffect('ST06-003', {
  canActivate: (card) => {
    if (card.rested) return false;
    return state.playerField.some(c => c && c.uid !== card.uid);
  },
  activate: (card, ctx) => {
    const value = parseInt((card.nativeKeyword || '').match(/Support (\d+)/)?.[1]) || 0;
    setRested(card, true);
    const candidates = state.playerField.filter(c => c && c.uid !== card.uid);
    pickAndApplyApMod(
      card,
      candidates,
      value,
      'tempApMod',
      `Choose 1 other friendly Unit. It gets AP+${value} during this turn.`,
      ctx.onDone
    );
  },
});

// ─── GD01-061 · ZuOOT ────────────────────────────────────────────────────────
// [Activate･Main]<Support 1> — identical effect to GD02-049 Daughtress.
registerCardEffect('GD01-061', {
  canActivate: (card) => {
    if (card.rested) return false;
    return state.playerField.some(c => c && c.uid !== card.uid);
  },
  activate: (card, ctx) => {
    const value = parseInt((card.nativeKeyword || '').match(/Support (\d+)/)?.[1]) || 0;
    setRested(card, true);
    const candidates = state.playerField.filter(c => c && c.uid !== card.uid);
    pickAndApplyApMod(
      card,
      candidates,
      value,
      'tempApMod',
      `Choose 1 other friendly Unit. It gets AP+${value} during this turn.`,
      ctx.onDone
    );
  },
});

// ─── GD01-046 · Buster Gundam ────────────────────────────────────────────────
// [Activate･Main]<Support 3>
// [During Pair･(Coordinator) Pilot][Once per Turn] When this Unit's <Support>
// increases a (ZAFT) Unit's AP, set this Unit as active.
// Picker is inline (not pickAndApplyApMod) so the chosen target is visible
// for the unrest bonus check. passiveUsedThisTurn gates the once-per-turn.
registerCardEffect('GD01-046', {
  canActivate: (card) => {
    if (card.rested) return false;
    return state.playerField.some(c => c && c.uid !== card.uid);
  },
  activate: (card, ctx) => {
    const value = parseInt((card.nativeKeyword || '').match(/Support (\d+)/)?.[1]) || 0;
    setRested(card, true);
    const candidates = state.playerField.filter(c => c && c.uid !== card.uid);
    if (candidates.length === 0) {
      addLog(`${card.name} — no other friendly Units. No effect.`); ctx.onDone(); return;
    }
    showCardPickerModal(
      card.name,
      `Choose 1 other friendly Unit. It gets AP+${value} during this turn.`,
      candidates,
      (target) => {
        if (!target) { ctx.onDone(); return; }
        applyApMod([target], value, 'tempApMod');
        addLog(`${card.name} — ${target.name} gets AP+${value} during this turn.`);
        if (!card.passiveUsedThisTurn
            && isLinkUnit(card)
            && (card.pilot?.trait || '').includes('(Coordinator)')
            && (target.trait || '').includes('(ZAFT)')) {
          card.passiveUsedThisTurn = true;
          setRested(card, false);
          addLog(`${card.name} — [During Pair·(Coordinator) Pilot] boosted a (ZAFT) Unit: set as active.`);
        }
        render(); setupDropZones(); ctx.onDone();
      },
      card
    );
  },
});

// ─── GD02-049 · Daughtress ───────────────────────────────────────────────────
// [Activate･Main]<Support 1> (Rest this Unit. 1 other friendly Unit gets
// AP+1 during this turn.)
// 'activate' trigger fires from showUnitActivateModal via triggerCardEffect(unit,
// 'activate', { unit, onDone }). The resting is the cost and must be applied
// in the handler (the generic UNIT_REST/RES machinery doesn't handle it).
// Follows the GD04-089 (Nena Trinity pilot Support 2) pattern exactly, but
// applied to the unit card itself rather than to the pilot's paired unit.
registerCardEffect('GD02-049', {
  canActivate: (card) => {
    if (card.rested) return false;
    return state.playerField.some(c => c && c.uid !== card.uid);
  },
  activate: (card, ctx) => {
    const value = parseInt((card.nativeKeyword || '').match(/Support (\d+)/)?.[1]) || 0;
    setRested(card, true);
    const candidates = state.playerField.filter(c => c && c.uid !== card.uid);
    pickAndApplyApMod(
      card,
      candidates,
      value,
      'tempApMod',
      `Choose 1 other friendly Unit. It gets AP+${value} during this turn.`,
      ctx.onDone
    );
  },
});

// GD01-095 "Dearka Elthman" — Pilot
// [When Linked] Discard 1. If you do, draw 1.
// pair trigger + isLinkUnit guard — fires only when this pilot actually
// satisfies the linked Unit's link requirement (not just paired).
registerCardEffect('GD01-095', {
  pair: (card, ctx) => {
    if (!isLinkUnit(ctx.unit)) return; // [When Linked]
    if (state.hand.length === 0) {
      addLog(`${card.name} — hand is empty, no discard. No draw.`);
      return;
    }
    showCardPickerModal(
      card.name,
      'Choose 1 card from your hand to discard.',
      [...state.hand],
      (target) => {
        if (target) {
          state.hand = state.hand.filter(c => c.uid !== target.uid);
          updateTempState(target.cardNo, target.copyVer, 'trash');
          state.trashCount++;
          state.playerTrash.push(toTrashCard(target));
          broadcastLog(`${card.name} — discarded ${target.name}.`);
          broadcastCardToast(`${target.name} was discarded due to ${card.name} effect.`);

          const drawn = drawCardsFromTempDeck(1, true);
          if (drawn.length > 0) {
            state.hand.push(drawn[0]);
            addLog(`${card.name} — drew ${drawn[0].name}.`);
          } else {
            addLog(`${card.name} — deck is empty, no draw.`);
          }
        } else {
          addLog(`${card.name} — discard skipped. No draw.`);
        }
        broadcastCounters();
        render();
        setupDropZones();
      },
      card,
      { mandatory: true }
    );
  },
});

// GD02-093 "Olba Frost" — Pilot
// During your turn, when the Unit paired with this Pilot destroys an enemy
// Unit paired with a (Newtype) Pilot via battle damage, draw 1.
// onLinkedUnitDestroysEnemy — standing (not turn-scoped) hook checked live
// off the destroyer's pilot inside notifyUnitDestroyedByBattle(); see
// js/effects/registry.js. Gated to the local client's own kill (destroyerSide
// === 'player') made during their own turn (state.myTurn).
registerCardEffect('GD02-093', {
  onLinkedUnitDestroysEnemy: (pilot, unit, destroyedUnit, destroyerSide) => {
    if (destroyerSide !== 'player' || !state.myTurn) return;
    if (!destroyedUnit?.pilot || !(destroyedUnit.pilot.trait || '').includes('(Newtype)')) return;
    const drawn = drawCardsFromTempDeck(1, true);
    if (drawn.length === 0) {
      addLog(`${pilot.name} — deck is empty, no draw.`);
      return;
    }
    state.hand.push(drawn[0]);
    addLog(`${pilot.name} — ${unit.name} destroyed ${destroyedUnit.name} (paired with a (Newtype) Pilot) in battle. Drew ${drawn[0].name}.`);
    broadcastCounters();
    render();
  },
});

// GD01-094 "Yzak Jule" — Pilot
// [Once per Turn] When an enemy Link Unit is destroyed with damage while the
// Unit paired with this Pilot is attacking, draw 1.
// onLinkedUnitDestroysEnemy — standing (not turn-scoped) hook checked live
// off the destroyer's pilot inside notifyUnitDestroyedByBattle(); see
// js/effects/registry.js. destroyerIsAttacker enforces "while attacking" —
// a counter-kill landed while defending does not qualify. isLinkUnit on the
// destroyed Unit enforces "Link Unit" (a Pilot pairing alone isn't enough —
// it must satisfy its own link requirement). pilot.passiveUsedThisTurn is
// the standard Once-per-Turn flag, cleared by UNREST_ALL.
registerCardEffect('GD01-094', {
  onLinkedUnitDestroysEnemy: (pilot, unit, destroyedUnit, destroyerSide, destroyerIsAttacker) => {
    if (destroyerSide !== 'player' || !destroyerIsAttacker) return;
    if (pilot.passiveUsedThisTurn) return; // [Once per Turn]
    if (!isLinkUnit(destroyedUnit)) return; // enemy Link Unit
    pilot.passiveUsedThisTurn = true;
    const drawn = drawCardsFromTempDeck(1, true);
    if (drawn.length === 0) {
      addLog(`${pilot.name} — [Once per Turn] deck is empty, no draw.`);
      return;
    }
    state.hand.push(drawn[0]);
    addLog(`${pilot.name} — [Once per Turn] ${unit.name} destroyed enemy Link Unit ${destroyedUnit.name} while attacking. Drew ${drawn[0].name}.`);
    broadcastCounters();
    render();
  },
});

// ST06-009 "Amate Yuzuriha (Machu)" — Pilot
// [When Linked] Look at the top card of your deck. If it is a (Clan) card,
// you may reveal it and add it to your hand. Return any remaining card to
// the bottom of your deck.
// pair trigger + isLinkUnit guard — fires only when this pilot actually
// satisfies the linked Unit's link requirement, same convention as
// GD01-095. The peek itself (peekTopOfDeck) and the prompt
// (showTopDeckPeekPrompt) are both local-only — nothing is sent to the peer
// unless the player actually takes the card, at which point a CARD_REVEAL
// network message informs the opponent (mirroring the card text's "reveal
// it" — keeping a card unrevealed means the opponent never learns what it
// was, win or lose).
registerCardEffect('ST06-009', {
  pair: (card, ctx) => {
    if (!isLinkUnit(ctx.unit)) return; // [When Linked]
    const top = peekTopOfDeck(1)[0];
    if (!top) {
      addLog(`${card.name} — deck is empty, nothing to look at.`);
      return;
    }
    const { entry, card: peeked } = top;
    const isClan = (peeked.trait || '').includes('(Clan)');
    showTopDeckPeekPrompt(card, peeked, isClan, (addToHand) => {
      if (isClan && addToHand) {
        entry.state = 'hand';
        state.hand.push(peeked);
        state.deckCount = state.tempDeck.filter(isInDeck).length;
        addLog(`${card.name} — revealed ${peeked.name} and added it to hand.`);
        NET.send({ type: 'CARD_REVEAL', card: peeked, pilotName: card.name });
        broadcastCardToast(`${peeked.name} was added to hand due to ${card.name} effect.`);
      } else {
        sendDeckEntryToBottom(entry);
        addLog(`${card.name} — looked at the top card and returned it to the bottom of the deck.`);
      }
      broadcastCounters();
      render();
      setupDropZones();
    });
  },
});

// ST04-011 "Athrun Zala" — Pilot
// [When Linked] During this turn, this Unit may choose an active enemy Unit
// that is Lv.5 or lower as its attack target.
// pair trigger + isLinkUnit guard, same convention as GD01-095/ST06-009.
// Stored as tempKeyword 'CanAttackActiveLv5' (mirrors GD02-108's
// 'CanAttackActiveLv4') so it's cleared automatically at end of turn by
// UNREST_ALL; the level threshold is checked in onCardClick's attack-target
// eligibility logic in game.js.
registerCardEffect('ST04-011', {
  pair: (card, ctx) => {
    if (!isLinkUnit(ctx.unit)) return; // [When Linked]
    const unit = ctx.unit;
    unit.tempKeywords = unit.tempKeywords || [];
    if (!unit.tempKeywords.includes('CanAttackActiveLv5')) unit.tempKeywords.push('CanAttackActiveLv5');
    NET.send({ type: 'TEMP_KEYWORD_ADD', uid: unit.uid, keyword: 'CanAttackActiveLv5' });
    addLog(`${card.name} — ${unit.name} may attack active enemy Units (Lv.5 or lower) this turn.`);
    render();
    setupDropZones();
  },
});

// GD03-093 "Carris Nautilus" — Pilot
// While no enemy Base is in play, this Unit gets AP+1.
// passiveApBonus — live, not turn-scoped: evaluated on every effectiveBp()
// read via getPilotPassiveApBonus(), so the bonus appears/disappears
// automatically as the enemy's Base is deployed/destroyed, with no state
// mutation needed. enemyBase is null for both "no Base at all" and a
// destroyed EX Base (EX Bases occupy the same Base state slot as a normal
// Base), so checking it covers "including ex base" per the card's wording.
registerCardEffect('GD03-093', {
  passiveApBonus: (pilot, unit, ownerBase, enemyBase) => enemyBase ? 0 : 1,
});

// GD04-091 "Deux Murasame" — Pilot
// [Destroyed] Choose 1 undamaged enemy Unit. Deal 1 damage to it.
// Fires whether this pilot is destroyed alongside its unit (effect damage
// or battle damage — both already call triggerCardEffect(pilot, 'destroyed',
// { ownerSide, unit })) or destroyed on its own, apart from its unit (e.g.
// GD03-110's PILOT_DESTROY path — its network.js remote handler already
// fires this same trigger on the pilot's actual owner, so no extra wiring
// is needed there). Battle destruction runs identically on both clients, so
// ctx.ownerSide gates this to only resolve on the pilot's own owner —
// mirrors GD03-099/GD03-100 in white.js and GD02-126 above.
registerCardEffect('GD04-091', {
  destroyed: (pilot, ctx) => {
    if (!ctx || ctx.ownerSide !== 'player') return;
    const candidates = state.oppField.filter(c => c && !c.damaged);
    if (candidates.length === 0) {
      addLog(`${pilot.name} — no undamaged enemy Units. No effect.`);
      return;
    }
    showCardPickerModal(
      pilot.name,
      'Choose 1 undamaged enemy Unit. Deal 1 damage to it.',
      candidates,
      (target) => {
        if (!target) return;
        dealDamageToUnit(target, 1, { fromEnemy: true });
      },
      pilot,
      { mandatory: true }
    );
  },
});

// ST08-010 "Hathaway Noa" — Pilot
// [When Paired] If this is a (Mafty) Unit, choose 1 of your (Mafty) Units.
// During this turn, it may choose a damaged active enemy Unit as its attack
// target.
// pair trigger, no isLinkUnit guard — "[When Paired]" applies to any
// pairing, Link or not. "If this is a (Mafty) Unit" gates on ctx.unit's own
// trait, not the chosen unit's. Candidates include ctx.unit itself per the
// card text ("choose 1 of your (Mafty) Units" has no exclusion). Grants the
// new 'CanAttackActiveDamaged' tempKeyword (mirrors ST04-011's
// CanAttackActiveLv5 pattern), checked alongside the Lv-threshold grants in
// onCardClick's attack-target eligibility logic in game.js.
registerCardEffect('ST08-010', {
  pair: (card, ctx) => {
    const unit = ctx.unit;
    const isMafty = (unit?.trait || '').includes('(Mafty)');
    if (!isMafty) return; // "If this is a (Mafty) Unit"
    const candidates = state.playerField.filter(c => c && (c.trait || '').includes('(Mafty)'));
    if (candidates.length === 0) {
      addLog(`${card.name} — no (Mafty) Units to choose. No effect.`);
      return;
    }
    showCardPickerModal(
      card.name,
      'Choose 1 of your (Mafty) Units. During this turn, it may choose a damaged active enemy Unit as its attack target.',
      candidates,
      (target) => {
        if (!target) return;
        target.tempKeywords = target.tempKeywords || [];
        if (!target.tempKeywords.includes('CanAttackActiveDamaged')) target.tempKeywords.push('CanAttackActiveDamaged');
        NET.send({ type: 'TEMP_KEYWORD_ADD', uid: target.uid, keyword: 'CanAttackActiveDamaged' });
        addLog(`${card.name} — ${target.name} may attack a damaged active enemy Unit this turn.`);
        render();
        setupDropZones();
      },
      card,
      { mandatory: true }
    );
  },
});

// GD04-090 "Hallelujah Haptism" — Pilot
// [During Link][Once per Turn] During your turn, when this Unit destroys an
// enemy Unit with battle damage, look at the top card of your deck. If it is
// a (CB) card, you may reveal it and add it to your hand. Return any
// remaining card to the bottom of your deck.
// onLinkedUnitDestroysEnemy — standing (not turn-scoped) hook checked live
// off the destroyer's pilot inside notifyUnitDestroyedByBattle(); see
// js/effects/registry.js. isLinkUnit(unit) enforces [During Link];
// pilot.passiveUsedThisTurn is the standard [Once per Turn] flag, cleared by
// UNREST_ALL, set before the peek (consistent with GD01-094: the trigger
// "using up" the once-per-turn even if the deck happens to be empty). No
// destroyerIsAttacker check — the card text doesn't restrict to "while
// attacking", so a counter-kill also qualifies. Reuses ST06-009's
// peek/prompt/reveal machinery (peekTopOfDeck, showTopDeckPeekPrompt,
// sendDeckEntryToBottom, CARD_REVEAL), filtering on (CB) instead of (Clan).
registerCardEffect('GD04-090', {
  onLinkedUnitDestroysEnemy: (pilot, unit, destroyedUnit, destroyerSide) => {
    if (destroyerSide !== 'player' || !state.myTurn) return;
    if (!isLinkUnit(unit)) return; // [During Link]
    if (pilot.passiveUsedThisTurn) return; // [Once per Turn]
    pilot.passiveUsedThisTurn = true;
    const top = peekTopOfDeck(1)[0];
    if (!top) {
      addLog(`${pilot.name} — [Once per Turn] deck is empty, nothing to look at.`);
      return;
    }
    const { entry, card: peeked } = top;
    const isCB = (peeked.trait || '').includes('(CB)');
    showTopDeckPeekPrompt(pilot, peeked, isCB, (addToHand) => {
      if (isCB && addToHand) {
        entry.state = 'hand';
        state.hand.push(peeked);
        state.deckCount = state.tempDeck.filter(isInDeck).length;
        addLog(`${pilot.name} — revealed ${peeked.name} and added it to hand.`);
        NET.send({ type: 'CARD_REVEAL', card: peeked, pilotName: pilot.name });
        broadcastCardToast(`${peeked.name} was added to hand due to ${pilot.name} effect.`);
      } else {
        sendDeckEntryToBottom(entry);
        addLog(`${pilot.name} — looked at the top card and returned it to the bottom of the deck.`);
      }
      broadcastCounters();
      render();
      setupDropZones();
    });
  },
});

// GD04-092 "Michael Trinity" — Pilot
// [When Linked] Choose 1 damaged enemy Unit. Deal 1 damage to it.
// pair trigger + isLinkUnit guard, same convention as ST06-009/ST04-011.
// ".damaged" flag precedent: GD04-091's "undamaged" filter above uses
// !c.damaged; this is the inverse.
registerCardEffect('GD04-092', {
  pair: (pilot, ctx) => {
    if (!isLinkUnit(ctx.unit)) return; // [When Linked]
    const candidates = state.oppField.filter(c => c && c.damaged);
    if (candidates.length === 0) {
      addLog(`${pilot.name} — no damaged enemy Units. No effect.`);
      return;
    }
    showCardPickerModal(
      pilot.name,
      'Choose 1 damaged enemy Unit. Deal 1 damage to it.',
      candidates,
      (target) => {
        if (!target) return;
        dealDamageToUnit(target, 1, { fromEnemy: true });
        addLog(`${pilot.name} — dealt 1 damage to ${target.name}.`);
        render();
        setupDropZones();
      },
      pilot,
      { mandatory: true }
    );
  },
});

// GD01-093 "Marida Cruz" — Pilot
// [During Link][Attack] Choose 1 enemy Unit whose Lv. is equal to or lower
// than this Unit. Deal 1 damage to it.
// attack trigger — fires during the Attack Step via fireAttackerPilotAttackTrigger
// in game.js (triggerCardEffect(attacker.pilot, 'attack', ...)). ctx.attackerSide
// gates to the attacker's own client; isLinkUnit(ctx.attacker) enforces
// [During Link]. ctx.onDone MUST be called on every path to advance the
// attack pipeline (mirrors GD04-099 in white.js).
registerCardEffect('GD01-093', {
  attack: (pilot, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    if (!isLinkUnit(ctx.attacker)) { ctx.onDone?.(); return; } // [During Link]
    const unitLevel = parseInt(ctx.attacker?.level) || 0;
    const candidates = state.oppField.filter(c => c && (parseInt(c.level) || 0) <= unitLevel);
    if (candidates.length === 0) {
      addLog(`${pilot.name} — no enemy Unit Lv.${unitLevel} or lower. No effect.`);
      ctx.onDone?.();
      return;
    }
    showCardPickerModal(
      pilot.name,
      `Choose 1 enemy Unit whose Lv. is equal to or lower than this Unit (Lv.${unitLevel}). Deal 1 damage to it.`,
      candidates,
      (target) => {
        if (target) {
          dealDamageToUnit(target, 1, { fromEnemy: true });
          addLog(`${pilot.name} — dealt 1 damage to ${target.name}.`);
        }
        ctx.onDone?.();
      },
      pilot,
      { mandatory: true }
    );
  },
});

// GD04-089 "Nena Trinity" — Pilot
// [Activate･Main]<Support 2> (Rest this Unit. 1 other friendly Unit gets
// AP+(specified amount) during this turn.)
// 'activate' trigger — fired by showPilotActivateModal (ctx = {unit, onDone}).
// canActivatePilot() in game.js calls cardEffectCanActivate(unit.pilot,
// undefined) with NO ctx, so canActivate below re-finds the paired unit by
// searching state.playerField for c.pilot.uid === pilot.uid. Support's own
// precondition — "the Unit this keyword is on must be active" — isn't
// enforced anywhere in the generic engine (unlike a Base's restBase cost,
// there's no built-in "Rest this Unit" cost check for pilots), so it's
// checked here, and naturally re-enforces itself afterward since resting the
// Unit as the cost makes canActivate return false again until it's unrested.
// The "1 other friendly Unit" requirement excludes the resting Unit itself.
// Support's value (2) is parsed from nativeKeyword ("[Support 2]") rather
// than hardcoded, so any future Support N pilot could reuse this template
// unchanged. pickAndApplyApMod (white.js) applies the temp AP boost to
// 'tempApMod', cleared at end of turn by UNREST_ALL like any other grant.
function findPairedUnit(pilot) {
  return state.playerField.find(c => c && c.pilot && c.pilot.uid === pilot.uid) || null;
}
registerCardEffect('GD04-089', {
  canActivate: (pilot) => {
    const unit = findPairedUnit(pilot);
    if (!unit || unit.rested) return false; // Support requires an active Unit
    return state.playerField.some(c => c && c.uid !== unit.uid);
  },
  activate: (pilot, ctx) => {
    const unit = ctx.unit;
    const value = parseInt((pilot.nativeKeyword || '').match(/Support (\d+)/)?.[1]) || 0;
    setRested(unit, true);
    const candidates = state.playerField.filter(c => c && c.uid !== unit.uid);
    pickAndApplyApMod(
      pilot,
      candidates,
      value,
      'tempApMod',
      `Choose 1 other friendly Unit. It gets AP+${value} during this turn.`,
      ctx.onDone
    );
  },
});

// GD03-092 "Nyaan" — Pilot
// [When Linked] Place the top card of your deck into your trash. If you
// placed a (Zeon)/(Clan) card with this effect, choose 1 enemy Unit. Deal 1
// damage to it.
// pair trigger + isLinkUnit guard, same convention as ST06-009/GD04-092.
// Unlike ST06-009's "look at" (which stays secret unless taken), this mill
// is unconditional and always public — the milled card is immediately
// pushed to playerTrash/trashCount (same bookkeeping toTrashCard() calls
// use elsewhere), and a new DECK_MILL network message (mirrors
// SHIELD_RESOLVE) tells the opponent which card it was so their oppTrash
// mirror shows it correctly (see js/network.js and the onDeckMill listener
// in game.js).
registerCardEffect('GD03-092', {
  pair: (pilot, ctx) => {
    if (!isLinkUnit(ctx.unit)) return; // [When Linked]
    const top = peekTopOfDeck(1)[0];
    if (!top) {
      addLog(`${pilot.name} — deck is empty, nothing to place into trash.`);
      return;
    }
    const { entry, card: milled } = top;
    entry.state = 'trash';
    state.trashCount += 1;
    state.playerTrash.push(toTrashCard(milled));
    state.deckCount = state.tempDeck.filter(isInDeck).length;
    addLog(`${pilot.name} — placed ${milled.name} into the trash.`);
    NET.send({ type: 'DECK_MILL', card: milled });
    broadcastCardToast(`${milled.name} was sent to trash.`);

    const finish = () => { broadcastCounters(); render(); setupDropZones(); };
    const isQualifying = (milled.trait || '').includes('(Zeon)') || (milled.trait || '').includes('(Clan)');
    if (!isQualifying) { finish(); return; }

    const candidates = state.oppField.filter(c => c != null);
    if (candidates.length === 0) {
      addLog(`${pilot.name} — no enemy Units to target. No effect.`);
      finish();
      return;
    }
    showCardPickerModal(
      pilot.name,
      'Choose 1 enemy Unit. Deal 1 damage to it.',
      candidates,
      (target) => {
        if (target) {
          dealDamageToUnit(target, 1, { fromEnemy: true });
          addLog(`${pilot.name} — dealt 1 damage to ${target.name}.`);
        }
        finish();
      },
      pilot,
      { mandatory: true }
    );
  },
});

// GD03-091 "Rau Le Creuset" — Pilot
// [When Linked] Choose 1 (ZAFT) Base card from your trash. Add it to your
// hand.
// pair trigger + isLinkUnit guard, same convention as ST06-009/GD04-092/
// GD03-092. Candidates come from state.playerTrash (entries carry
// cardNo/copyVer/uid via toTrashCard()); the splice-out-of-trash pattern
// mirrors ACTIVATE_BASE's TRASH_EXILE handling for GD04-130's cost. Since
// only the owner has authoritative trash data, a new TRASH_TO_HAND network
// message (mirrors TRASH_EXILE) tells the opponent which uid to drop from
// their oppTrash mirror.
registerCardEffect('GD03-091', {
  pair: (pilot, ctx) => {
    if (!isLinkUnit(ctx.unit)) return; // [When Linked]
    const candidates = state.playerTrash.filter(c =>
      c && c.type === 'base' && (c.trait || '').includes('(ZAFT)')
    );
    if (candidates.length === 0) {
      addLog(`${pilot.name} — no (ZAFT) Base card in trash. No effect.`);
      return;
    }
    showCardPickerModal(
      pilot.name,
      'Choose 1 (ZAFT) Base card from your trash. Add it to your hand.',
      candidates,
      (chosen) => {
        if (!chosen) return;
        const idx = state.playerTrash.findIndex(c => c.uid === chosen.uid);
        if (idx === -1) return;
        const [card] = state.playerTrash.splice(idx, 1);
        state.trashCount = Math.max(0, state.trashCount - 1);
        state.hand.push(card);
        updateTempState(card.cardNo, card.copyVer, 'hand');
        addLog(`${pilot.name} — added ${card.name} from the trash to hand.`);
        NET.send({ type: 'TRASH_TO_HAND', uid: card.uid });
        broadcastCounters();
        render();
        setupDropZones();
      },
      pilot,
      { mandatory: true }
    );
  },
});

// GD02-092 "Shagia Frost" — Pilot
// [During Link][Attack] Choose 1 of your (New UNE) Units. It gets AP+2
// during this turn.
// attack trigger + isLinkUnit guard, same template as GD01-093/GD04-099.
// Uses pickAndApplyApMod (white.js) for the "choose 1 friendly Unit, AP+N
// this turn" portion, same as GD04-089's Support handling. ctx.onDone is
// always called (directly or via pickAndApplyApMod) to keep the
// Attack/Block/Action/Damage pipeline moving.
registerCardEffect('GD02-092', {
  attack: (pilot, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    if (!isLinkUnit(ctx.attacker)) { ctx.onDone?.(); return; } // [During Link]
    const candidates = state.playerField.filter(c => c && (c.trait || '').includes('(New UNE)'));
    pickAndApplyApMod(
      pilot,
      candidates,
      2,
      'tempApMod',
      'Choose 1 of your (New UNE) Units. It gets AP+2 during this turn.',
      ctx.onDone
    );
  },
});

// GD02-091 "Haman Karn" — Pilot
// [When Paired] If this Unit is red, choose 1 enemy Unit whose Lv. is equal
// to or lower than this Unit. Deal 1 damage to it.
// Plain pair trigger — no isLinkUnit guard, since the card text says "When
// Paired" not "When Linked" (same distinction observed for ST08-010/
// GD01-096). "This Unit" refers to ctx.unit (the paired Unit), whose color
// gates the effect and whose level bounds the enemy candidate pool — same
// color-check style as GD01-096 (white.js).
registerCardEffect('GD02-091', {
  pair: (pilot, ctx) => {
    const unit = ctx.unit;
    if (!unit || (unit.color || '').toLowerCase() !== 'red') {
      addLog(`${pilot.name} — paired Unit is not red. No effect.`);
      return;
    }
    const unitLevel = parseInt(unit.level) || 0;
    const candidates = state.oppField.filter(c => c && (parseInt(c.level) || 0) <= unitLevel);
    if (candidates.length === 0) {
      addLog(`${pilot.name} — no enemy Unit Lv.${unitLevel} or lower. No effect.`);
      return;
    }
    showCardPickerModal(
      pilot.name,
      `Choose 1 enemy Unit whose Lv. is equal to or lower than this Unit (Lv.${unitLevel}). Deal 1 damage to it.`,
      candidates,
      (target) => {
        if (!target) return;
        dealDamageToUnit(target, 1, { fromEnemy: true });
        addLog(`${pilot.name} — dealt 1 damage to ${target.name}.`);
        render();
        setupDropZones();
      },
      pilot,
      { mandatory: true }
    );
  },
});

// ST03-010 "Full Frontal" — Pilot
// [When Paired] You may deploy 1 (Neo Zeon)/(Zeon) Unit card that is Lv.4 or
// lower from your hand.
// First "deploy a Unit from hand via an effect" implementation in the
// codebase — no prior precedent existed (only base/token deploy helpers
// did). Deliberately does NOT go through dispatch('PLAY_CARD', ...)/
// registerAction('PLAY_CARD', ...) (game.js ~6588) since that handler
// unconditionally calls spendResources(effectiveHandCost(card)) — per the
// card text (no "Pay its cost to deploy it" wording, unlike trash-deploy
// effects elsewhere in the DB), this deploy is free. Instead replicates
// PLAY_CARD's state mutations directly (hand removal, field placement,
// deploy sickness via tempCantAttack, updateTempState, the 'deploy' trigger,
// the Superpower Bloc/(UN) flag) and sends the same NET PLAY_CARD message
// deployTokenToField (game.js ~6611) uses for its own non-dispatch deploy.
// "You may" → optional picker (mandatory: false), skip resolves to null.
registerCardEffect('ST03-010', {
  pair: (pilot, ctx) => {
    if (findNextFieldSlot() === -1) {
      addLog(`${pilot.name} — field is full. No effect.`);
      return;
    }
    const candidates = state.hand.filter(c =>
      c && c.type === 'unit' &&
      ((c.trait || '').includes('(Neo Zeon)') || (c.trait || '').includes('(Zeon)')) &&
      (parseInt(c.level) || 0) <= 4
    );
    if (candidates.length === 0) {
      addLog(`${pilot.name} — no (Neo Zeon)/(Zeon) Unit card Lv.4 or lower in hand. No effect.`);
      return;
    }
    showCardPickerModal(
      pilot.name,
      'You may deploy 1 (Neo Zeon)/(Zeon) Unit card that is Lv.4 or lower from your hand.',
      candidates,
      (chosen) => {
        if (!chosen) return;
        const slot = findNextFieldSlot();
        if (slot === -1) {
          addLog(`${pilot.name} — field became full. No effect.`);
          return;
        }
        state.hand = state.hand.filter(c => c.uid !== chosen.uid);
        state.playerField[slot] = chosen;
        chosen.tempCantAttack = true; // deploy sickness — cleared by UNREST_ALL on next turn
        updateTempState(chosen.cardNo, chosen.copyVer, 'field');
        addLog(`${pilot.name} — deployed ${chosen.name} from hand.`);
        triggerCardEffect(chosen, 'deploy', { slot });
        const trait = chosen.trait || '';
        if (trait.includes('Superpower Bloc') || trait.includes('(UN)')) {
          state.superpowerDeployedThisTurn = true;
        }
        NET.send({ type: 'PLAY_CARD', slot, card: chosen, counters: myCounters() });
        render();
        setupDropZones();
      },
      pilot,
      { mandatory: false }
    );
  },
});

// ─── GD03-043 · Messer Type-F02 ──────────────────────────────────────────────
// [When Paired] Choose 1 enemy Unit. Deal 1 damage to it.
// pair trigger on the unit (fires when any pilot is paired to it; no link check
// since the text says "When Paired" not "When Linked").
registerCardEffect('GD03-043', {
  pair: (card, ctx) => {
    const candidates = state.oppField.filter(c => c);
    if (candidates.length === 0) {
      addLog(`${card.name} — [When Paired] no enemy Units. No effect.`);
      return;
    }
    showCardPickerModal(
      card.name,
      'Choose 1 enemy Unit. Deal 1 damage to it.',
      candidates,
      (target) => {
        if (!target) return;
        dealDamageToUnit(target, 1, { fromEnemy: true });
        addLog(`${card.name} — [When Paired] dealt 1 damage to ${target.name}.`);
        render(); setupDropZones();
      },
      card, { mandatory: true }
    );
  },
});

// ─── GD03-039 · Red Gundam ────────────────────────────────────────────────────
// [Deploy] Choose 1 other active friendly (Clan) Unit. Rest it.
// If you do, choose 1 enemy Unit with 2 or less AP. Deal 2 damage to it.
registerCardEffect('GD03-039', {
  deploy: (card) => {
    const clanCandidates = state.playerField.filter(c =>
      c && c.uid !== card.uid && !c.rested && (c.trait || '').includes('(Clan)')
    );
    if (clanCandidates.length === 0) {
      addLog(`${card.name} — [Deploy] no other active friendly (Clan) Units. No effect.`);
      return;
    }
    showCardPickerModal(
      card.name,
      'Choose 1 other active friendly (Clan) Unit to rest.',
      clanCandidates,
      (rested) => {
        if (!rested) return;
        setRested(rested, true);
        addLog(`${card.name} — [Deploy] rested ${rested.name}.`);
        const enemyCandidates = state.oppField.filter(c => c && effectiveBp(c) <= 2);
        if (enemyCandidates.length === 0) {
          addLog(`${card.name} — no enemy Units with 2 or less AP. No damage.`);
          render(); setupDropZones(); return;
        }
        showCardPickerModal(
          card.name,
          'Choose 1 enemy Unit with 2 or less AP. Deal 2 damage to it.',
          enemyCandidates,
          (target) => {
            if (!target) { render(); setupDropZones(); return; }
            dealDamageToUnit(target, 2, { fromEnemy: true });
            addLog(`${card.name} — dealt 2 damage to ${target.name}.`);
            render(); setupDropZones();
          },
          card, { mandatory: true }
        );
      },
      card, { mandatory: true }
    );
  },
});

// ─── GD04-039 · Rozen Zulu ───────────────────────────────────────────────────
// If there are 8 or more (Neo Zeon) cards in your trash, this card's cost -4.
// [Deploy] Choose 1 enemy Unit. Deal 1 damage to it.
//          If it has <Repair>, deal 3 damage instead.
registerCardEffect('GD04-039', {
  handCostReduction: () =>
    state.playerTrash.filter(c => (c.trait || '').includes('(Neo Zeon)')).length >= 8 ? 4 : 0,
  deploy: (card) => {
    const candidates = state.oppField.filter(c => c);
    if (candidates.length === 0) {
      addLog(`${card.name} — [Deploy] no enemy Units. No effect.`);
      return;
    }
    showCardPickerModal(
      card.name,
      'Choose 1 enemy Unit. Deal 1 damage (3 if it has <Repair>).',
      candidates,
      (target) => {
        if (!target) return;
        const dmg = hasNativeKeyword(target, 'Repair') ? 3 : 1;
        dealDamageToUnit(target, dmg, { fromEnemy: true });
        addLog(`${card.name} — [Deploy] dealt ${dmg} damage to ${target.name}${dmg === 3 ? ' (has <Repair>)' : ''}.`);
        render(); setupDropZones();
      },
      card, { mandatory: true }
    );
  },
});

// ─── GD02-046 · Sayla's Light-Type Guncannon ─────────────────────────────────
// [Deploy] Choose 1 enemy Unit token. Deal 2 damage to it.
registerCardEffect('GD02-046', {
  deploy: (card) => {
    const candidates = state.oppField.filter(c => c && isUnitToken(c));
    if (candidates.length === 0) {
      addLog(`${card.name} — [Deploy] no enemy Unit tokens. No effect.`);
      return;
    }
    showCardPickerModal(
      card.name,
      'Choose 1 enemy Unit token. Deal 2 damage to it.',
      candidates,
      (target) => {
        if (!target) return;
        dealDamageToUnit(target, 2, { fromEnemy: true });
        addLog(`${card.name} — [Deploy] dealt 2 damage to ${target.name} (token).`);
        render(); setupDropZones();
      },
      card, { mandatory: true }
    );
  },
});

// ─── GD03-037 · Bertigo ──────────────────────────────────────────────────────
// [During Link] During your turn, while this Unit is battling an enemy Unit
// with a [Destroyed] effect, it gains <First Strike>.
// attack trigger (fires before Block Step so First Strike is in place for
// runDamageStep); isLinkUnit gates the [During Link] condition.
registerCardEffect('GD03-037', {
  attack: (card, ctx) => {
    if (!isLinkUnit(card)) { ctx.onDone(); return; }
    if (!(ctx.defender?.effectRaw || '').includes('[Destroyed]')) { ctx.onDone(); return; }
    card.tempKeywords = card.tempKeywords || [];
    if (!card.tempKeywords.includes('First Strike')) card.tempKeywords.push('First Strike');
    NET.send({ type: 'TEMP_KEYWORD_ADD', uid: card.uid, keyword: 'First Strike' });
    addLog(`${card.name} — [During Link] battling ${ctx.defender.name} (has [Destroyed] effect): gained <First Strike>.`);
    ctx.onDone();
  },
});

// ─── GD01-045 · Duel Gundam (Assault Shroud) ─────────────────────────────────
// [When Paired] Look at the top 3 cards of your deck. You may deploy 1 (ZAFT)
// Unit card that is Lv.4 or lower among them. Return the remaining cards
// randomly to the bottom of your deck.
// pair trigger on the unit (any pilot paired; no link check since "[When Paired]").
registerCardEffect('GD01-045', {
  pair: (card) => {
    const peeked = peekTopOfDeck(3);
    if (peeked.length === 0) {
      addLog(`${card.name} — [When Paired] deck is empty. No effect.`);
      return;
    }

    const shuffleAndReturnEntries = (entries) => {
      for (let i = entries.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [entries[i], entries[j]] = [entries[j], entries[i]];
      }
      entries.forEach(e => sendDeckEntryToBottom(e));
    };

    const deployAndReturn = (chosenItem) => {
      const remaining = peeked.filter(p => p !== chosenItem);
      if (chosenItem) {
        const slot = findNextFieldSlot();
        if (slot === -1) {
          addLog(`${card.name} — [When Paired] field is full, could not deploy ${chosenItem.card.name}.`);
          shuffleAndReturnEntries([chosenItem.entry, ...remaining.map(p => p.entry)]);
        } else {
          const chosen = chosenItem.card;
          state.playerField[slot] = chosen;
          chosen.tempCantAttack = true;
          updateTempState(chosen.cardNo, chosen.copyVer, 'field');
          const trait = chosen.trait || '';
          if (trait.includes('Superpower Bloc') || trait.includes('(UN)')) {
            state.superpowerDeployedThisTurn = true;
          }
          addLog(`${card.name} — [When Paired] deployed ${chosen.name} from deck.`);
          triggerCardEffect(chosen, 'deploy', { slot });
          NET.send({ type: 'PLAY_CARD', slot, card: chosen, counters: myCounters() });
          shuffleAndReturnEntries(remaining.map(p => p.entry));
        }
      } else {
        shuffleAndReturnEntries(peeked.map(p => p.entry));
      }
      render(); setupDropZones();
    };

    const isDeployable = p =>
      p.card.type === 'unit' && (p.card.trait || '').includes('(ZAFT)') && p.card.level <= 4;

    // Always reveal all 3 peeked cards, even when NONE qualify — see
    // GD02-038's identical fix and EB01-067's original correct convention.
    // Previously this short-circuited straight to shuffling them away with
    // just a log line when nothing qualified, silently skipping the reveal.
    if (!peeked.some(isDeployable)) {
      addLog(`${card.name} — [When Paired] no (ZAFT) Unit Lv.4 or lower in top 3 cards.`);
    }
    const disabledIndices = peeked.map((p, i) => i).filter(i => !isDeployable(peeked[i]));
    showCardPickerModal(
      card.name,
      'You may deploy 1 (ZAFT) Unit card that is Lv.4 or lower from the top 3 cards of your deck.',
      peeked.map(p => p.card),
      (chosen) => {
        if (!chosen) { deployAndReturn(null); return; }
        const chosenItem = peeked.find(p => p.card.uid === chosen.uid);
        deployAndReturn(chosenItem || null);
      },
      card,
      { mandatory: false, disabledIndices }
    );
  },
});

// ─── GD01-044 · Kshatriya ────────────────────────────────────────────────────
// [When Paired･(Cyber-Newtype)/(Newtype) Pilot] Choose 1 to 2 enemy Units. Deal 1 damage to them.
registerCardEffect('GD01-044', {
  pair: (card) => {
    const pilotTrait = card.pilot?.trait || '';
    if (!(pilotTrait.includes('(Cyber-Newtype)') || pilotTrait.includes('(Newtype)'))) return;
    const enemies = state.oppField.filter(c => c);
    if (enemies.length === 0) { addLog(`${card.name} — [When Paired] no enemy Units. No effect.`); return; }
    showCardPickerModalMulti(
      card.name,
      'Choose 1 to 2 enemy Units. Deal 1 damage to each.',
      enemies,
      { min: 1, max: 2, mandatory: true },
      (targets) => {
        if (!targets || targets.length === 0) return;
        targets.forEach(t => {
          dealDamageToUnit(t, 1, { fromEnemy: true });
          addLog(`${card.name} — [When Paired] dealt 1 damage to ${t.name}.`);
        });
        render(); setupDropZones();
      },
      card
    );
  },
});

// ─── GD03-042 · Duel Gundam (Assault Shroud) ─────────────────────────────────
// While this Unit has 5 or more AP, it may choose an active enemy Unit that is
// Lv.5 or lower as its attack target.
// passiveKeywords — live, re-evaluated by hasNativeKeyword() on every target
// build, so the ability appears/disappears as AP changes without per-turn mutation.
registerCardEffect('GD03-042', {
  passiveKeywords: (card) => effectiveBp(card) >= 5 ? ['CanAttackActiveLv5'] : [],
});

// ─── ST06-001 · GQuuuuuuX (Omega Psycommu) ───────────────────────────────────
// [When Linked] If another friendly (Clan) Unit is in play, this gains
// <First Strike> during this turn.
registerCardEffect('ST06-001', {
  pair: (card, ctx) => {
    const unit = ctx?.unit || card;
    if (!isLinkUnit(unit)) return; // [When Linked]
    const hasClan = state.playerField.some(c => c && c.uid !== unit.uid && (c.trait || '').includes('(Clan)'));
    if (!hasClan) {
      addLog(`${card.name} — [When Linked] no other friendly (Clan) Units in play. No effect.`);
      return;
    }
    unit.tempKeywords = unit.tempKeywords || [];
    if (!unit.tempKeywords.includes('First Strike')) unit.tempKeywords.push('First Strike');
    NET.send({ type: 'TEMP_KEYWORD_ADD', uid: unit.uid, keyword: 'First Strike' });
    addLog(`${card.name} — [When Linked] another (Clan) Unit is in play: ${unit.name} gains <First Strike> this turn.`);
    render(); setupDropZones();
  },
});

// ─── GD04-041 · Gundam Throne Drei ───────────────────────────────────────────
// [Once per Turn] When this Unit is rested by an effect, set it as active.
// onRestedByEffect — fired by setRested() in white.js when a playerField unit
// is rested by a card effect (not by attacking). passiveUsedThisTurn gates the
// Once-per-Turn; cleared by UNREST_ALL.
registerCardEffect('GD04-041', {
  onRestedByEffect: (card) => {
    if (card.passiveUsedThisTurn) return; // [Once per Turn]
    card.passiveUsedThisTurn = true;
    setRested(card, false);
    addLog(`${card.name} — [Once per Turn] was rested by an effect: set as active.`);
    render(); setupDropZones();
  },
});

// ─── GD02-037 · Gundam Virsago ────────────────────────────────────────────────
// <Breach 1> (native — handled by text parse)
// [Deploy] If there are 3 or less enemy Shields, choose 1 enemy Unit with 5 or
// less AP. Deal 2 damage to it.
registerCardEffect('GD02-037', {
  deploy: (card) => {
    if (state.oppShield > 3) {
      addLog(`${card.name} — [Deploy] enemy has ${state.oppShield} Shields (need 3 or fewer). No effect.`);
      return;
    }
    const candidates = state.oppField.filter(c => c && effectiveBp(c) <= 5);
    if (candidates.length === 0) {
      addLog(`${card.name} — [Deploy] no enemy Units with 5 or less AP. No effect.`);
      return;
    }
    showCardPickerModal(
      card.name,
      'Choose 1 enemy Unit with 5 or less AP. Deal 2 damage to it.',
      candidates,
      (target) => {
        if (!target) return;
        dealDamageToUnit(target, 2, { fromEnemy: true });
        addLog(`${card.name} — [Deploy] dealt 2 damage to ${target.name}.`);
        render(); setupDropZones();
      },
      card, { mandatory: true }
    );
  },
});

// ─── ST03-001 · Sinanju ───────────────────────────────────────────────────────
// [During Pair] This Unit gains <High-Maneuver>.
// During your turn, when this Unit destroys an enemy shield area card with
// battle damage, choose 1 enemy Unit. Deal 2 damage to it.
// passiveKeywords grants High-Maneuver while any pilot is paired.
// onAttackDestroysShield fires from runDamageStep's shield-hit branch whenever
// this unit attacks and removes ≥ 1 enemy shield card (see game.js).
registerCardEffect('ST03-001', {
  passiveKeywords: (card) => card.pilot ? ['High-Maneuver'] : [],
  onAttackDestroysShield: (card) => {
    const candidates = state.oppField.filter(c => c);
    if (candidates.length === 0) {
      addLog(`${card.name} — [During Pair] shield destroyed: no enemy Units to deal damage to.`);
      return;
    }
    showCardPickerModal(
      card.name,
      'Choose 1 enemy Unit. Deal 2 damage to it.',
      candidates,
      (target) => {
        if (!target) return;
        dealDamageToUnit(target, 2, { fromEnemy: true });
        addLog(`${card.name} — [During Pair] shield destroyed: dealt 2 damage to ${target.name}.`);
        render(); setupDropZones();
      },
      card, { mandatory: true }
    );
  },
});

// ─── ST09-003 · Saviour Gundam ────────────────────────────────────────────────
// <Breach 3> (native — handled by text parse)
// [When Linked] If there are 5 or more purple cards in your trash,
// deal 2 damage to all Units with 5 or less AP.
// "all Units" = both sides; effectiveBp is used for the AP check.
registerCardEffect('ST09-003', {
  pair: (card, ctx) => {
    const unit = ctx?.unit || card;
    if (!isLinkUnit(unit)) return; // [When Linked]
    const purpleCount = state.playerTrash.filter(c => (c.color || '') === 'purple').length;
    if (purpleCount < 5) {
      addLog(`${card.name} — [When Linked] only ${purpleCount} purple card(s) in trash (need 5). No effect.`);
      return;
    }
    const allUnits = [
      ...state.playerField.filter(c => c),
      ...state.oppField.filter(c => c),
    ].filter(c => effectiveBp(c) <= 5);
    if (allUnits.length === 0) {
      addLog(`${card.name} — [When Linked] no Units with 5 or less AP on the field. No effect.`);
      return;
    }
    allUnits.forEach(c => {
      const fromEnemy = state.oppField.includes(c);
      dealDamageToUnit(c, 2, { fromEnemy });
    });
    addLog(`${card.name} — [When Linked] ${purpleCount} purple cards in trash: dealt 2 damage to all Units with 5 or less AP.`);
    render(); setupDropZones();
  },
});

// ─── GD04-036 · Gundam Throne Eins ───────────────────────────────────────────
// [Deploy] You may choose 1 to 2 of your other active (CB) Units. Rest them.
// If you do, deal damage equal to the number rested to all enemy Units Lv.6 or lower.
registerCardEffect('GD04-036', {
  deploy: (card) => {
    const getCBCandidates = (exclude) => state.playerField.filter(c =>
      c && c.uid !== card.uid && !c.rested && (c.trait || '').includes('(CB)')
      && (!exclude || c.uid !== exclude.uid)
    );
    const first = getCBCandidates(null);
    if (first.length === 0) {
      addLog(`${card.name} — [Deploy] no other active (CB) Units. No effect.`);
      return;
    }
    showCardPickerModal(
      card.name,
      'You may choose 1 of your other active (CB) Units to rest (then optionally a second).',
      first,
      (pick1) => {
        if (!pick1) { addLog(`${card.name} — [Deploy] skipped. No effect.`); return; }
        setRested(pick1, true);
        let count = 1;
        const second = getCBCandidates(pick1);
        const applyDamage = () => {
          const targets = state.oppField.filter(c => c && (parseInt(c.level) || 0) <= 6);
          targets.forEach(t => dealDamageToUnit(t, count, { fromEnemy: true }));
          addLog(`${card.name} — [Deploy] rested ${count} (CB) Unit(s): dealt ${count} damage to all enemy Units Lv.6 or lower.`);
          render(); setupDropZones();
        };
        if (second.length === 0) { applyDamage(); return; }
        showCardPickerModal(
          card.name,
          'You may choose a second active (CB) Unit to rest.',
          second,
          (pick2) => {
            if (pick2) { setRested(pick2, true); count = 2; }
            applyDamage();
          },
          card, { mandatory: false }
        );
      },
      card, { mandatory: false }
    );
  },
});

// ─── GD03-022 · Gundam Kyrios ─────────────────────────────────────────────────
// [During Link] During your turn, when this Unit destroys an enemy Unit with
// battle damage, deal 1 damage to all enemy Units that are Lv.3 or lower.
// onUnitDestroysEnemy — standing hook on the unit itself, fired by
// notifyUnitDestroyedByBattle() in game.js (mirrors onLinkedUnitDestroysEnemy
// on pilots). isLinkUnit gate enforces [During Link].
registerCardEffect('GD03-022', {
  onUnitDestroysEnemy: (card, destroyed, destroyerSide) => {
    if (!isLinkUnit(card)) return;
    if (destroyerSide !== 'player' || !state.myTurn) return;
    const targets = state.oppField.filter(c => c && (parseInt(c.level) || 0) <= 3);
    if (targets.length === 0) {
      addLog(`${card.name} — [During Link] destroyed ${destroyed.name}: no enemy Units Lv.3 or lower to damage.`);
      return;
    }
    targets.forEach(t => dealDamageToUnit(t, 1, { fromEnemy: true }));
    addLog(`${card.name} — [During Link] destroyed ${destroyed.name}: dealt 1 damage to all enemy Units Lv.3 or lower.`);
    render(); setupDropZones();
  },
});

// ─── GD03-035 · GFreD ────────────────────────────────────────────────────────
// [Activate･Main][Once per Turn]①, exile 1 Pilot card from your trash:
//   Deal 1 damage to all enemy Units.
// [When Linked] During this turn, this Unit may choose an active enemy Unit
//   with AP equal to or less than this Unit as its attack target.
//
// canAttackActiveMaxAp hook — live, re-evaluated per target-build — returns
// effectiveBp(card) so the threshold always tracks this Unit's current AP
// (it can still change later the same turn from other effects), but only
// while card.linkedThisTurn is set — that flag is true only for the turn a
// Unit newly becomes a Link Unit (set in PAIR_PILOT/pairFromTrash, cleared
// by CLEAR_TURN_EFFECTS at End Phase; see GD03-069's identical convention).
// Previously gated on isLinkUnit(card) alone, which stays true for as long
// as the pilot stays paired — letting this "during this turn" ability wrongly
// persist on every later turn instead of expiring at the end of the turn it
// linked. Checked in the target-building loop in game.js via getCardEffects.
//
// canActivate additionally requires a Pilot card in the trash (the exile cost).
registerCardEffect('GD03-035', {
  canActivate: (card) => {
    if (card.activateMainUsedThisTurn) return false;
    if (state.res < 1) return false;
    return state.playerTrash.some(c => c.type === 'pilot');
  },
  activate: (card, ctx) => {
    const pilotCards = state.playerTrash.filter(c => c.type === 'pilot');
    if (pilotCards.length === 0) {
      addLog(`${card.name} — no Pilot cards in trash to exile. No effect.`);
      ctx.onDone(); return;
    }
    showCardPickerModal(
      card.name,
      'Exile 1 Pilot card from your trash.',
      pilotCards,
      (exiled) => {
        if (!exiled) { ctx.onDone(); return; }
        exileFromTrash(exiled);
        addLog(`${card.name} — exiled ${exiled.name} from trash.`);
        broadcastCounters();
        const targets = state.oppField.filter(c => c);
        targets.forEach(t => dealDamageToUnit(t, 1, { fromEnemy: true }));
        addLog(`${card.name} — dealt 1 damage to all enemy Units.`);
        render(); setupDropZones(); ctx.onDone();
      },
      card, { mandatory: true }
    );
  },
  canAttackActiveMaxAp: (card) => (isLinkUnit(card) && card.linkedThisTurn) ? effectiveBp(card) : -1,
});

// ─── GD03-036 · Ξ Gundam (Flight Form) ───────────────────────────────────────
// [When Linked] Deal 1 damage to all enemy Units.
registerCardEffect('GD03-036', {
  pair: (card) => {
    if (!isLinkUnit(card)) return;
    const targets = state.oppField.filter(c => c);
    if (targets.length === 0) {
      addLog(`${card.name} — [When Linked] no enemy Units to damage.`);
      return;
    }
    targets.forEach(t => dealDamageToUnit(t, 1, { fromEnemy: true }));
    addLog(`${card.name} — [When Linked] dealt 1 damage to all enemy Units.`);
    render();
    setupDropZones();
  },
});

// ─── GD02-036 · Qubeley ───────────────────────────────────────────────────────
// [When Linked] This Unit gains <Suppression> during this turn.
//   (Suppression is already handled by game.js's shield-hit branch — resolves
//   2 shields when attacker has the Suppression keyword.)
// [During Pair･(Neo Zeon) Pilot][Attack] Choose 1 damaged enemy Unit.
//   Deal 2 damage to it.
registerCardEffect('GD02-036', {
  pair: (card, ctx) => {
    const unit = ctx?.unit || card;
    if (!isLinkUnit(unit)) return; // [When Linked]
    unit.tempKeywords = unit.tempKeywords || [];
    if (!unit.tempKeywords.includes('Suppression')) unit.tempKeywords.push('Suppression');
    NET.send({ type: 'TEMP_KEYWORD_ADD', uid: unit.uid, keyword: 'Suppression' });
    addLog(`${card.name} — [When Linked] ${unit.name} gains <Suppression> this turn.`);
    render(); setupDropZones();
  },
  attack: (card, ctx) => {
    if (!card.pilot || !(card.pilot.trait || '').includes('(Neo Zeon)')) { ctx.onDone(); return; }
    const candidates = state.oppField.filter(c => c && c.damaged);
    if (candidates.length === 0) { ctx.onDone(); return; }
    showCardPickerModal(
      card.name,
      'Choose 1 damaged enemy Unit. Deal 2 damage to it.',
      candidates,
      (target) => {
        if (target) {
          dealDamageToUnit(target, 2, { fromEnemy: true });
          addLog(`${card.name} — [During Pair·(Neo Zeon) Pilot][Attack] dealt 2 damage to ${target.name}.`);
          render(); setupDropZones();
        }
        ctx.onDone();
      },
      card, { mandatory: true }
    );
  },
});

// ─── GD04-042 · Psycho Gundam (GQ) ───────────────────────────────────────────
// [During Link][Once per Turn] When damage from one of your Units paired with a
// (Cyber-Newtype) Pilot destroys an enemy shield area card, choose 1 enemy Unit
// with 5 or less AP. Deal 2 damage to it.
// onAnyPlayerAttackDestroysShield — standing watcher on this unit, fired from
// game.js's shield-hit branch whenever any player unit destroys an enemy shield.
registerCardEffect('GD04-042', {
  onAnyPlayerAttackDestroysShield: (card, attacker) => {
    if (!isLinkUnit(card)) return; // [During Link]
    if (card.passiveUsedThisTurn) return; // [Once per Turn]
    if (!attacker.pilot || !(attacker.pilot.trait || '').includes('(Cyber-Newtype)')) return;
    const candidates = state.oppField.filter(c => c && effectiveBp(c) <= 5);
    if (candidates.length === 0) {
      addLog(`${card.name} — [During Link][Once per Turn] shield destroyed by ${attacker.name}: no enemy Units with AP≤5. No effect.`);
      return;
    }
    card.passiveUsedThisTurn = true;
    showCardPickerModal(
      card.name,
      'Choose 1 enemy Unit with 5 or less AP. Deal 2 damage to it.',
      candidates,
      (target) => {
        if (!target) return;
        dealDamageToUnit(target, 2, { fromEnemy: true });
        addLog(`${card.name} — [During Link][Once per Turn] dealt 2 damage to ${target.name}.`);
        render(); setupDropZones();
      },
      card, { mandatory: true }
    );
  },
});

// ─── GD03-033 · Providence Gundam ────────────────────────────────────────────
// [During Pair･(ZAFT) Pilot] During your turn, all your (ZAFT) Units get AP+2.
// [Attack] Choose 1 enemy Unit. Deal 1 damage to it for each 4 AP this Unit has.
//
// globalUnitApBonus — checked live via getGlobalUnitApBonus() in effectiveBp()
// for every unit on the field. Grants +2 to each (ZAFT) unit while this card
// has a (ZAFT) pilot and it is currently our turn (state.myTurn).
registerCardEffect('GD03-033', {
  globalUnitApBonus: (targetCard, sourceCard) => {
    // Use field-position check so both clients agree: on attacker's client
    // sourceCard is in playerField + myTurn=true; on defender's client
    // sourceCard is in oppField + myTurn=false — both correctly reflect "owner's turn".
    const ownersField = state.playerField.some(c => c && c.uid === sourceCard.uid);
    if (ownersField ? !state.myTurn : state.myTurn) return 0;
    if (!sourceCard.pilot || !(sourceCard.pilot.trait || '').includes('(ZAFT)')) return 0;
    return (targetCard.trait || '').includes('(ZAFT)') ? 2 : 0;
  },
  attack: (card, ctx) => {
    const dmg = Math.floor(effectiveBp(card) / 4);
    if (dmg <= 0) { ctx.onDone(); return; }
    const candidates = state.oppField.filter(c => c);
    if (candidates.length === 0) { ctx.onDone(); return; }
    showCardPickerModal(
      card.name,
      `Choose 1 enemy Unit. Deal ${dmg} damage to it (1 per 4 AP this Unit has).`,
      candidates,
      (target) => {
        if (target) {
          dealDamageToUnit(target, dmg, { fromEnemy: true });
          addLog(`${card.name} — [Attack] dealt ${dmg} damage to ${target.name} (AP: ${effectiveBp(card)}).`);
          render(); setupDropZones();
        }
        ctx.onDone();
      },
      card, { mandatory: true }
    );
  },
});

// ─── GD03-041 · Patulia ───────────────────────────────────────────────────────
// [Deploy] Deal 3 damage to all Bases.
// "all Bases" = both player's and opponent's base.
registerCardEffect('GD03-041', {
  deploy: (card) => {
    let affected = false;
    if (state.oppBase) {
      dealDamageToBase('opp', 3, { fromEnemy: true });
      addLog(`${card.name} — [Deploy] dealt 3 damage to ${state.oppBase?.name || 'enemy base'}.`);
      affected = true;
    }
    if (state.playerBase) {
      dealDamageToBase('player', 3, { fromEnemy: false });
      addLog(`${card.name} — [Deploy] dealt 3 damage to ${state.playerBase?.name || 'your base'}.`);
      affected = true;
    }
    if (!affected) addLog(`${card.name} — [Deploy] no Bases in play. No effect.`);
    render(); setupDropZones();
  },
});

// ─── GD02-038 · GQuuuuuuX (Omega Psycommu) ───────────────────────────────────
// [Deploy] Look at the top 3 cards of your deck. You may deploy 1 (Clan) Unit
// card that is Lv.4 or lower among them. Return the remaining cards randomly to
// the bottom of your deck.
// Same pattern as GD01-045 (ZAFT) — just targets (Clan) units instead.
registerCardEffect('GD02-038', {
  deploy: (card) => {
    const peeked = peekTopOfDeck(3);
    if (peeked.length === 0) {
      addLog(`${card.name} — [Deploy] deck is empty. No effect.`);
      return;
    }

    const shuffleAndReturnEntries = (entries) => {
      for (let i = entries.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [entries[i], entries[j]] = [entries[j], entries[i]];
      }
      entries.forEach(e => sendDeckEntryToBottom(e));
    };

    const deployAndReturn = (chosenItem) => {
      const remaining = peeked.filter(p => p !== chosenItem);
      if (chosenItem) {
        const slot = findNextFieldSlot();
        if (slot === -1) {
          addLog(`${card.name} — [Deploy] field is full, could not deploy ${chosenItem.card.name}.`);
          shuffleAndReturnEntries([chosenItem.entry, ...remaining.map(p => p.entry)]);
        } else {
          const chosen = chosenItem.card;
          state.playerField[slot] = chosen;
          chosen.tempCantAttack = true;
          updateTempState(chosen.cardNo, chosen.copyVer, 'field');
          const trait = chosen.trait || '';
          if (trait.includes('Superpower Bloc') || trait.includes('(UN)')) {
            state.superpowerDeployedThisTurn = true;
          }
          addLog(`${card.name} — [Deploy] deployed ${chosen.name} from deck.`);
          triggerCardEffect(chosen, 'deploy', { slot });
          NET.send({ type: 'PLAY_CARD', slot, card: chosen, counters: myCounters() });
          shuffleAndReturnEntries(remaining.map(p => p.entry));
        }
      } else {
        shuffleAndReturnEntries(peeked.map(p => p.entry));
      }
      render(); setupDropZones();
    };

    const isDeployable = p =>
      p.card.type === 'unit' && (p.card.trait || '').includes('(Clan)') && p.card.level <= 4;

    // Always reveal all 3 peeked cards, even when NONE qualify — a "look at
    // the top N cards" effect should show the player what was actually
    // looked at regardless of whether anything in it was selectable (see
    // EB01-067's identical convention). Previously this short-circuited
    // straight to shuffling them away with just a log line when nothing
    // qualified, silently skipping the reveal entirely.
    if (!peeked.some(isDeployable)) {
      addLog(`${card.name} — [Deploy] no (Clan) Unit Lv.4 or lower in top 3 cards.`);
    }
    const disabledIndices = peeked.map((p, i) => i).filter(i => !isDeployable(peeked[i]));
    showCardPickerModal(
      card.name,
      'You may deploy 1 (Clan) Unit card that is Lv.4 or lower from the top 3 cards of your deck.',
      peeked.map(p => p.card),
      (chosen) => {
        if (!chosen) { deployAndReturn(null); return; }
        const chosenItem = peeked.find(p => p.card.uid === chosen.uid);
        deployAndReturn(chosenItem || null);
      },
      card,
      { mandatory: false, disabledIndices }
    );
  },
});

// GD01-047: [Attack] 2+ other rested friendly units → pick enemy → deal 3
registerCardEffect('GD01-047', {
  attack: (card, ctx) => {
    const restedCount = state.playerField.filter(c => c && c.uid !== card.uid && c.rested).length;
    if (restedCount < 2) { ctx.onDone(); return; }
    const candidates = state.oppField.filter(c => c);
    if (candidates.length === 0) { ctx.onDone(); return; }
    showCardPickerModal(card.name, `Choose 1 enemy Unit. Deal 3 damage to it. (${restedCount} other rested friendly Units in play.)`, candidates, (target) => {
      if (target) { dealDamageToUnit(target, 3, { fromEnemy: true }); addLog(`${card.name} — [Attack] dealt 3 damage to ${target.name}.`); render(); setupDropZones(); }
      ctx.onDone();
    }, card, { mandatory: true });
  },
});

// GD03-034: [Deploy] pick enemy → deal 3
registerCardEffect('GD03-034', {
  deploy: (card) => {
    const candidates = state.oppField.filter(c => c);
    if (candidates.length === 0) { addLog(`${card.name} — [Deploy] no enemy Units. No effect.`); return; }
    showCardPickerModal(card.name, 'Choose 1 enemy Unit. Deal 3 damage to it.', candidates, (target) => {
      if (!target) return;
      dealDamageToUnit(target, 3, { fromEnemy: true });
      addLog(`${card.name} — [Deploy] dealt 3 damage to ${target.name}.`);
      render(); setupDropZones();
    }, card, { mandatory: true });
  },
});

// ST08-001: handLevelReduction + handCostReduction (no friendly Lv6+ → -1 per enemy);
//           [When Paired] pick highest-Lv enemy → deal 3
registerCardEffect('ST08-001', {
  handLevelReduction: () => {
    if (state.playerField.some(c => c && (parseInt(c.level) || 0) >= 6)) return 0;
    return state.oppField.filter(c => c).length;
  },
  handCostReduction: () => {
    if (state.playerField.some(c => c && (parseInt(c.level) || 0) >= 6)) return 0;
    return state.oppField.filter(c => c).length;
  },
  pair: (card, ctx) => {
    const enemies = state.oppField.filter(c => c);
    if (enemies.length === 0) { addLog(`${card.name} — [When Paired] no enemy Units. No effect.`); return; }
    const maxLv = Math.max(...enemies.map(c => parseInt(c.level) || 0));
    const candidates = enemies.filter(c => (parseInt(c.level) || 0) === maxLv);
    if (candidates.length === 1) {
      dealDamageToUnit(candidates[0], 3, { fromEnemy: true });
      addLog(`${card.name} — [When Paired] dealt 3 damage to ${candidates[0].name} (highest Lv: ${maxLv}).`);
      render(); setupDropZones();
    } else {
      showCardPickerModal(card.name, `Choose 1 enemy Unit with the highest Lv (${maxLv}). Deal 3 damage to it.`, candidates, (target) => {
        if (!target) return;
        dealDamageToUnit(target, 3, { fromEnemy: true });
        addLog(`${card.name} — [When Paired] dealt 3 damage to ${target.name} (Lv: ${maxLv}).`);
        render(); setupDropZones();
      }, card, { mandatory: true });
    }
  },
});

// GD04-033: [Deploy] pick enemy → deal 3; [When any player unit deployed] if deployed is Neo Zeon OR self is linked → pick enemy → deal 3
registerCardEffect('GD04-033', {
  deploy: (card) => {
    const candidates = state.oppField.filter(c => c);
    if (candidates.length === 0) { addLog(`${card.name} — [Deploy] no enemy Units. No effect.`); return; }
    showCardPickerModal(card.name, 'Choose 1 enemy Unit. Deal 3 damage to it.', candidates, (target) => {
      if (!target) return;
      dealDamageToUnit(target, 3, { fromEnemy: true });
      addLog(`${card.name} — [Deploy] dealt 3 damage to ${target.name}.`);
      render(); setupDropZones();
    }, card, { mandatory: true });
  },
  onAnyPlayerUnitDeployed: (card, deployed) => {
    const isNeoZeon = (deployed.trait || '').includes('(Neo Zeon)') || isLinkUnit(card);
    if (!isNeoZeon) return;
    const candidates = state.oppField.filter(c => c);
    if (candidates.length === 0) { addLog(`${card.name} — ${deployed.name} deployed: no enemy Units. No effect.`); return; }
    showCardPickerModal(card.name, 'Choose 1 enemy Unit. Deal 3 damage to it.', candidates, (target) => {
      if (!target) return;
      dealDamageToUnit(target, 3, { fromEnemy: true });
      addLog(`${card.name} — ${deployed.name} deployed: dealt 3 damage to ${target.name}.`);
      render(); setupDropZones();
    }, card, { mandatory: true });
  },
});

// ════════════════════════════════════════════════════════════════════════════
// GD05 RED CARDS
// ════════════════════════════════════════════════════════════════════════════

// ─── GD05-033 · Master Gundam ───────────────────────────────────────────────
// [Attack] You may choose 2 (Special Move) Command cards from your trash.
// Exile them from the game. If you do, deal 5 damage to the enemy Base if
// one is in play; otherwise deal 5 damage to the first card in their shield
// area (per ruling — the raw text says "shield area" unconditionally, but
// Base takes priority when present).
registerCardEffect('GD05-033', {
  attack: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    const candidates = state.playerTrash.filter(c =>
      (c.type === 'command' || c.type === 'command pilot') && (c.trait || '').includes('(Special Move)')
    );
    if (candidates.length < 2) { ctx.onDone(); return; }
    showCardPickerModalMulti(
      card.name,
      'You may exile 2 (Special Move) Command cards from your trash. If you do, deal 5 damage to the enemy Base, or the first card in their shield area if they have no Base.',
      candidates,
      { min: 2, max: 2, mandatory: false },
      (chosen) => {
        if (!chosen || chosen.length < 2) { ctx.onDone(); return; }
        exileCardsFromTrash(chosen);
        addLog(`${card.name} — exiled ${chosen.map(c => c.name).join(', ')}.`);
        if (state.oppBase) {
          dealDamageToBase('opp', 5, { fromEnemy: true });
          addLog(`${card.name} — dealt 5 damage to the enemy Base.`);
        } else {
          NET.send({ type: 'FORCE_SHIELD_HIT', count: 1 });
          addLog(`${card.name} — destroyed the first card in the enemy shield area.`);
        }
        broadcastCounters(); render(); setupDropZones();
        ctx.onDone();
      },
      card
    );
  },
});

// ─── GD05-034 · Gaia Gundam ──────────────────────────────────────────────────
// [During Pair][Once per Turn] When this Unit destroys an enemy shield area
// card with battle damage, that enemy player may discard 1. If they don't
// discard with this effect, you may deploy 1 (Phantom Pain) Unit card that
// is Lv.4 or lower from your hand.
// onAttackDestroysShield fires for both a direct shield hit and a Base
// destroyed by battle damage — both count as "shield area card" per the
// engine's existing convention (see game.js).
registerCardEffect('GD05-034', {
  onAttackDestroysShield: (card) => {
    if (!card.pilot) return;
    if (card.passiveUsedThisTurn) return;
    card.passiveUsedThisTurn = true;
    addLog(`${card.name} — [During Pair][Once per Turn] destroyed an enemy shield-area card with battle damage.`);
    offerDiscardToOpponent((discarded) => {
      if (discarded) return;
      const candidates = state.hand.filter(c =>
        (c.type === 'unit' || c.type === 'unit-token') &&
        (c.trait || '').includes('(Phantom Pain)') && (parseInt(c.level) || 0) <= 4
      );
      if (candidates.length === 0) return;
      const slot = state.playerField.findIndex(c => !c);
      if (slot === -1) return;
      showCardPickerModal(
        card.name,
        'The enemy declined to discard: you may deploy 1 (Phantom Pain) Unit card (Lv.4 or lower) from your hand.',
        candidates,
        (target) => { if (target) deployUnitFromHandFree(target, slot); },
        card,
        { mandatory: false, skipLabel: 'Decline' }
      );
    });
  },
});

// ─── GD05-035 · Dragon Gundam ────────────────────────────────────────────────
// [Once per Turn] When this Unit destroys an enemy shield area card with
// damage, choose 1 enemy Unit with 3 or less AP. Deal 2 damage to it.
// [During Link][Attack] Activate [Main] on the card paired with this Unit.
registerCardEffect('GD05-035', {
  onAttackDestroysShield: (card) => {
    if (card.passiveUsedThisTurn) return;
    card.passiveUsedThisTurn = true;
    const candidates = state.oppField.filter(c => c && effectiveBp(c) <= 3);
    if (candidates.length === 0) {
      addLog(`${card.name} — no enemy Units with 3 or less AP. No effect.`);
      return;
    }
    addLog(`${card.name} — [Once per Turn] destroyed an enemy shield-area card: choose 1 enemy Unit with 3 or less AP.`);
    showCardPickerModal(card.name, 'Choose 1 enemy Unit with 3 or less AP. Deal 2 damage to it.', candidates, (target) => {
      if (target) {
        dealDamageToUnit(target, 2, { fromEnemy: true });
        addLog(`${card.name} — dealt 2 damage to ${target.name}.`);
        render(); setupDropZones();
      }
    }, card, { mandatory: true });
  },
  attack: (card, ctx) => activatePairedPilotMain(card, ctx),
});

// ─── GD05-036 · Haow Gundam ──────────────────────────────────────────────────
// [When Paired] You may choose 1 of your other active (MF) Units. Rest it.
// If you do, deal 2 damage to all enemy Units whose Lv. is equal to or
// lower than that Unit.
registerCardEffect('GD05-036', {
  pair: (card, ctx) => {
    const unit = ctx?.unit;
    if (!unit) return;
    const candidates = state.playerField.filter(c => c && c.uid !== unit.uid && !c.rested && (c.trait || '').includes('(MF)'));
    if (candidates.length === 0) return;
    showCardPickerModal(card.name, 'You may choose 1 of your other active (MF) Units. Rest it.', candidates, (target) => {
      if (!target) return;
      setRested(target, true);
      const lv = parseInt(target.level) || 0;
      const enemyTargets = state.oppField.filter(c => c && (parseInt(c.level) || 0) <= lv);
      addLog(`${card.name} — rested ${target.name}: deal 2 damage to all enemy Units Lv.${lv} or lower.`);
      enemyTargets.forEach(t => dealDamageToUnit(t, 2, { fromEnemy: true }));
      render(); setupDropZones();
    }, card, { mandatory: false, skipLabel: 'Decline' });
  },
});

// ─── GD05-037 · Destroy Gundam ───────────────────────────────────────────────
// While an enemy player has 7 or more cards in their trash, this card in
// your hand gets Lv-3 and cost-3.
// [During Link] This Unit gains <Breach 3>.
registerCardEffect('GD05-037', {
  handCostReduction: () => state.oppTrash.length >= 7 ? 3 : 0,
  handLevelReduction: () => state.oppTrash.length >= 7 ? 3 : 0,
  conditionalBreachValue: (card) => isLinkUnit(card) ? 3 : 0,
});

// ─── GD05-038 · Gundam Throne Eins (GN High Mega Launcher) ──────────────────
// [During Link] This Unit gains <Suppression>.
// [Activate･Main][Once per Turn] Rest 3 of your (CB) Units: Choose 1 enemy
// Unit. Deal 4 damage to it.
// getUnitActivationCost's "Rest (\d+) of your Units" regex requires the
// literal substring "of your Units" — "of your (CB) Units" doesn't match, so
// cost.restUnits parses to 0 and the generic rest-cost picker never appears.
// Once-per-Turn/Main-Phase gating and the Activate button ARE still handled
// generically (they only need [Activate･Main]/[Once per Turn] in the raw
// text) — only the (CB)-restricted rest cost itself is hand-rolled here,
// mirroring GD05-022 Gundam Schwarzette's hand-rolled exile-cost pattern.
registerCardEffect('GD05-038', {
  passiveKeywords: (card) => isLinkUnit(card) ? ['Suppression'] : [],
  canActivate: (card) => {
    return state.playerField.filter(c => c && c.uid !== card.uid && !c.rested && (c.trait || '').includes('(CB)')).length >= 3;
  },
  activate: (card, ctx) => {
    const cbCandidates = state.playerField.filter(c => c && c.uid !== card.uid && !c.rested && (c.trait || '').includes('(CB)'));
    if (cbCandidates.length < 3) {
      addLog(`${card.name} — need 3 active (CB) Units to activate. No effect.`);
      ctx.onDone(); return;
    }
    showCardPickerModalMulti(card.name, 'Rest 3 of your (CB) Units to pay this ability\'s cost.', cbCandidates, { min: 3, max: 3, mandatory: true }, (chosen) => {
      if (!chosen || chosen.length < 3) { ctx.onDone(); return; }
      chosen.forEach(c => setRested(c, true));
      addLog(`${card.name} — rested ${chosen.map(c => c.name).join(', ')} to pay the cost.`);
      const enemyTargets = state.oppField.filter(c => c);
      if (enemyTargets.length === 0) { render(); setupDropZones(); ctx.onDone(); return; }
      showCardPickerModal(card.name, 'Choose 1 enemy Unit. Deal 4 damage to it.', enemyTargets, (target) => {
        if (target) {
          dealDamageToUnit(target, 4, { fromEnemy: true });
          addLog(`${card.name} — dealt 4 damage to ${target.name}.`);
        }
        render(); setupDropZones();
        ctx.onDone();
      }, card, { mandatory: true });
    }, card);
  },
});

// ─── GD05-039 · Chaos Gundam ─────────────────────────────────────────────────
// [Attack] Choose 1 of your (Phantom Pain) Linked Units. It gains
// <High-Maneuver> during this turn.
registerCardEffect('GD05-039', {
  attack: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    const candidates = state.playerField.filter(c => c && isLinkUnit(c) && (c.trait || '').includes('(Phantom Pain)'));
    if (candidates.length === 0) { ctx.onDone(); return; }
    showCardPickerModal(card.name, 'Choose 1 of your (Phantom Pain) Linked Units. It gains <High-Maneuver> during this turn.', candidates, (target) => {
      if (target) {
        target.tempKeywords = target.tempKeywords || [];
        if (!target.tempKeywords.includes('High-Maneuver')) target.tempKeywords.push('High-Maneuver');
        NET.send({ type: 'TEMP_KEYWORD_ADD', uid: target.uid, keyword: 'High-Maneuver' });
        addLog(`${card.name} — ${target.name} gains <High-Maneuver> this turn.`);
        render(); setupDropZones();
      }
      ctx.onDone();
    }, card, { mandatory: true });
  },
});

// ─── GD05-040 · Abyss Gundam ─────────────────────────────────────────────────
// [Activate･Main]<Support 2> (Rest this Unit. 1 other friendly Unit gets
// AP+(specified amount) during this turn.) — no other effect text; same
// hand-rolled Support pattern as GD03-038 GuAIZ, minus its extra bonus.
registerCardEffect('GD05-040', {
  canActivate: (card) => {
    if (card.rested) return false;
    return state.playerField.some(c => c && c.uid !== card.uid);
  },
  activate: (card, ctx) => {
    const value = parseInt((card.nativeKeyword || '').match(/Support (\d+)/)?.[1]) || 0;
    setRested(card, true);
    const candidates = state.playerField.filter(c => c && c.uid !== card.uid);
    if (candidates.length === 0) { addLog(`${card.name} — no other friendly Units.`); ctx.onDone(); return; }
    showCardPickerModal(card.name, `Choose 1 other friendly Unit. It gets AP+${value} during this turn.`, candidates, (target) => {
      if (target) {
        applyApMod([target], value, 'tempApMod');
        addLog(`${card.name} — ${target.name} gets AP+${value} during this turn.`);
      }
      render(); setupDropZones(); ctx.onDone();
    }, card, { mandatory: true });
  },
});

// ─── GD05-041 · Gaia Gundam (MA Mode) ────────────────────────────────────────
// During a turn where your opponent has discarded due to one of your
// effects, this card in your hand gets cost -2.
registerCardEffect('GD05-041', {
  handCostReduction: () => state.causedEnemyDiscardThisTurn ? 2 : 0,
});

// ─── GD05-044 · Gundam Rose ──────────────────────────────────────────────────
// [During Link][Attack] Activate [Main] on the card paired with this Unit.
registerCardEffect('GD05-044', {
  attack: (card, ctx) => activatePairedPilotMain(card, ctx),
});

// ─── GD05-046 · Abyss Gundam (MA Mode) ───────────────────────────────────────
// [When Paired･(Phantom Pain) Pilot] Choose 1 enemy player with 4 or more
// cards in their hand. They discard 1.
registerCardEffect('GD05-046', {
  pair: (card, ctx) => {
    const unit = ctx?.unit;
    if (!unit || !unit.pilot || !(unit.pilot.trait || '').includes('(Phantom Pain)')) return;
    if (state.oppHandCount < 4) {
      addLog(`${card.name} — enemy has fewer than 4 cards in hand. No effect.`);
      return;
    }
    addLog(`${card.name} — [When Paired・(Phantom Pain) Pilot] the enemy player discards 1.`);
    forceDiscardOnOpponent();
  },
});

// ─── GD05-048 · Gundam Kyrios (Flight Mode) ─────────────────────────────────
// On the turn this Unit is deployed, it may choose a rested enemy Unit as
// its attack target and attack it.
registerCardEffect('GD05-048', {
  deploy: (card) => {
    card.tempAttackDespiteSickness = true;
    card.tempCantAttackNonUnit = true;
    addLog(`${card.name} — [Deploy] may attack a rested enemy Unit this turn despite just being deployed.`);
  },
});

// ─── GD05-089 · Master Asia — Pilot ─────────────────────────────────────────
// [Burst] Add this card to your hand (generic). If there are 3 or more (MF)
// cards in your trash, you must then choose: keep it as a Pilot in hand, or
// deploy it directly to the field as an (AP3・HP3) Unit instead (once
// deployed as a Unit, other Pilots/Command Pilots can pair to it normally,
// since pairing eligibility only checks "field card without a pilot", not
// card.type). This decision is made once, immediately, and is not revisited.
// While on the field it's a Unit; the moment it leaves the field (destroyed,
// bounced, milled, etc.) it goes back to being treated as a Pilot card —
// see the _deployedAsUnitFromPilot flag / toTrashCard() in game.js.
// [During Link][Attack] If you have activated a (Special Move) Command
// card's [Main]/[Action] during this turn, choose 1 enemy Unit. Deal 2
// damage to it.
registerCardEffect('GD05-089', {
  burst: (card, ctx) => {
    const mfCount = state.playerTrash.filter(c => (c.trait || '').includes('(MF)')).length;
    if (mfCount < 3) { ctx?.onDone?.(); return; }
    showChooseOneEffectModal(card, [
      {
        label: 'Add to hand as a Pilot card.',
        run: (done) => { done(); },
      },
      {
        label: 'Deploy as a Unit (AP3・HP3) instead.',
        run: (done) => {
          const idx = state.hand.findIndex(c => c.uid === card.uid);
          if (idx === -1) { done(); return; }
          const slot = state.playerField.findIndex(c => !c);
          if (slot === -1) {
            addLog(`${card.name} — no empty field slot. Stays in hand.`);
            done(); return;
          }
          state.hand.splice(idx, 1);
          // _deployedAsUnitFromPilot: read by toTrashCard() (game.js) so this
          // card reverts to being a real Pilot card the moment it leaves the
          // field for the trash, rather than staying stuck as a Unit.
          const unitForm = { ...card, type: 'unit', bp: 3, hp: 3, canPilot: false, pilot: null, _deployedAsUnitFromPilot: true };
          unitForm.tempCantAttack = true;
          state.playerField[slot] = unitForm;
          updateTempState(unitForm.cardNo, unitForm.copyVer, 'field');
          addLog(`${card.name} — deployed as a Unit (AP3・HP3) to slot ${(slot % 3) + 1}.`);
          triggerCardEffect(unitForm, 'deploy', { slot });
          NET.send({ type: 'PLAY_CARD', slot, card: unitForm, counters: myCounters() });
          broadcastCounters(); render(); setupDropZones();
          done();
        },
      },
    ], () => ctx?.onDone?.());
  },
  attack: (pilot, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    if (!isLinkUnit(ctx.attacker)) { ctx.onDone(); return; }
    if (!state.specialMoveCommandActivatedThisTurn) { ctx.onDone(); return; }
    const candidates = state.oppField.filter(c => c);
    if (candidates.length === 0) { addLog(`${pilot.name} — no enemy Units. No effect.`); ctx.onDone(); return; }
    showCardPickerModal(pilot.name, 'Choose 1 enemy Unit. Deal 2 damage to it.', candidates, (target) => {
      if (target) {
        dealDamageToUnit(target, 2, { fromEnemy: true });
        addLog(`${pilot.name} — dealt 2 damage to ${target.name}.`);
      }
      render(); setupDropZones();
      ctx.onDone();
    }, pilot, { mandatory: true });
  },
});

// ─── GD05-090 · Stellar Loussier — Pilot ────────────────────────────────────
// [Burst] Add this card to your hand (generic).
// [Destroyed] Look at the top card of your deck. If it is a (Phantom Pain)
// card, you may reveal it and add it to your hand. Return any remaining
// card to the bottom of your deck.
registerCardEffect('GD05-090', {
  destroyed: (pilot, ctx) => {
    if (ctx?.ownerSide !== 'player') return;
    lookAtTopAndPickToHand(
      pilot, 1,
      'Look at the top card. If it is a (Phantom Pain) card, you may reveal it and add it to your hand.',
      c => (c.trait || '').includes('(Phantom Pain)')
    );
  },
});

// ─── GD05-091 · Sting Oakley — Pilot ────────────────────────────────────────
// [Burst] Add this card to your hand (generic).
// While an enemy player has 7 or more cards in their trash, this Unit gets
// AP+1 and HP+1.
registerCardEffect('GD05-091', {
  passiveApBonus: (pilot, unit) => {
    const ownerSide = state.playerField.some(c => c && c.uid === unit.uid) ? 'player' : 'opp';
    const enemyTrash = ownerSide === 'player' ? state.oppTrash : state.playerTrash;
    return enemyTrash.length >= 7 ? 1 : 0;
  },
  passiveHpBonus: (pilot, unit) => {
    const ownerSide = state.playerField.some(c => c && c.uid === unit.uid) ? 'player' : 'opp';
    const enemyTrash = ownerSide === 'player' ? state.oppTrash : state.playerTrash;
    return enemyTrash.length >= 7 ? 1 : 0;
  },
});

// ─── GD05-092 · Auel Neider — Pilot ──────────────────────────────────────────
// [Burst] Add this card to your hand (generic).
// [During Link][Attack] If you are attacking the enemy player, this Unit
// gets AP+2 during this battle.
// "Attacking the enemy player" covers Base/Shield/Direct attacks — anything
// that isn't targeting an enemy Unit — see isAttackingEnemyPlayer() in
// white.js. Checked at the moment of declaration (before the Block Step) —
// battleApMod is battle-scoped (cleared at the top of the NEXT runDamageStep
// call), so the bonus persists through a later Blocker redirect, as ruled.
registerCardEffect('GD05-092', {
  attack: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    if (isAttackingEnemyPlayer(ctx)) {
      applyApMod([ctx.attacker], 2, 'battleApMod');
      addLog(`${card.name} — attacking the enemy player: ${ctx.attacker.name} gets AP+2 this battle.`);
      render();
    }
    ctx.onDone();
  },
});

// ─── GD05-110 · Darkness Finger — Command ───────────────────────────────────
// [Burst] Activate this card's [Main].
// [Main]/[Action] Choose 1 enemy Unit. Deal 2 damage to it. Then, if you
// have a Unit with "Master Gundam" in its card name in play, draw 1.
function gd05110Effect(card, onDone) {
  const candidates = state.oppField.filter(c => c);
  if (candidates.length === 0) { addLog(`${card.name} — no enemy Units. No effect.`); onDone(); return; }
  showCardPickerModal(card.name, 'Choose 1 enemy Unit. Deal 2 damage to it.', candidates, (target) => {
    if (target) {
      dealDamageToUnit(target, 2, { fromEnemy: true });
      addLog(`${card.name} — dealt 2 damage to ${target.name}.`);
      const hasMasterGundam = state.playerField.some(c => c && (c.name || '').includes('Master Gundam'));
      if (hasMasterGundam) {
        const drawn = drawCardsFromTempDeck(1, true);
        if (drawn.length > 0) {
          state.hand.push(drawn[0]);
          addLog(`${card.name} — Master Gundam in play: drew ${drawn[0].name}.`);
        }
      }
      broadcastCounters(); render(); setupDropZones();
    }
    onDone();
  }, card, { mandatory: true });
}
registerCardEffect('GD05-110', {
  burst: (card, ctx) => {
    addLog(`${card.name} — [Burst] activates this card's [Main].`);
    const handled = triggerCardEffect(card, 'main', { onDone: () => fireCommandEffectPassives(card, ctx?.onDone ?? (() => {})) });
    if (!handled) ctx?.onDone?.();
  },
  canActivate: () => state.oppField.some(c => c),
  main: (card, ctx) => gd05110Effect(card, ctx.onDone),
  action: (card, ctx) => gd05110Effect(card, ctx.onDone),
});

// ─── GD05-111 · Airframe Seizure — Command ──────────────────────────────────
// [Main] Discard 1. If you do, draw 2.
registerCardEffect('GD05-111', {
  canActivate: () => state.hand.length > 0,
  main: (card, ctx) => {
    if (state.hand.length === 0) { ctx.onDone(); return; }
    showCardPickerModal(card.name, 'Discard 1 card from your hand. If you do, draw 2.', [...state.hand], (discard) => {
      if (discard) {
        state.hand = state.hand.filter(c => c.uid !== discard.uid);
        updateTempState(discard.cardNo, discard.copyVer, 'trash');
        state.trashCount++;
        state.playerTrash.push(toTrashCard(discard));
        broadcastLog(`${card.name} — discarded ${discard.name}.`);
        broadcastCardToast(`${discard.name} was discarded due to ${card.name} effect.`);
        const drawn = drawCardsFromTempDeck(2, true);
        drawn.forEach(c => state.hand.push(c));
        addLog(`${card.name} — drew ${drawn.length} card(s).`);
      }
      broadcastCounters(); render(); setupDropZones();
      ctx.onDone();
    }, card, { mandatory: true });
  },
});

// ─── GD05-112 · Hoka Kyoten Juzetsujin — Command Pilot ──────────────────────
// [Main] Choose 1 of your (MF) Units without Breach. It gains <Breach 3>
// during this turn. After activating this card's [Main], you may pair this
// card from your trash with one of your (MF) Units.
// [Pilot] Sai Saici
registerCardEffect('GD05-112', {
  canActivate: () => state.playerField.some(c => c && (c.trait || '').includes('(MF)') && getBreachValue(c) === 0),
  main: (card, ctx) => {
    const candidates = state.playerField.filter(c => c && (c.trait || '').includes('(MF)') && getBreachValue(c) === 0);
    if (candidates.length === 0) {
      addLog(`${card.name} — no (MF) Units without Breach. No effect.`);
      offerPairFromTrash(card, '(MF)', ctx.onDone);
      return;
    }
    showCardPickerModal(card.name, 'Choose 1 of your (MF) Units without Breach. It gains <Breach 3> during this turn.', candidates, (target) => {
      if (target) {
        target.tempKeywords = target.tempKeywords || [];
        if (!target.tempKeywords.includes('Breach 3')) target.tempKeywords.push('Breach 3');
        NET.send({ type: 'TEMP_KEYWORD_ADD', uid: target.uid, keyword: 'Breach 3' });
        addLog(`${card.name} — ${target.name} gains <Breach 3> this turn.`);
        render(); setupDropZones();
      }
      offerPairFromTrash(card, '(MF)', ctx.onDone);
    }, card, { mandatory: true });
  },
});

// ─── GD05-113 · Rose Screamer — Command Pilot ───────────────────────────────
// [Main] Choose 1 of your (MF) Units with 4 or less AP. It gets AP+2 during
// this turn. After activating this card's [Main], you may pair this card
// from your trash with one of your (MF) Units.
// [Pilot] George de Sand
registerCardEffect('GD05-113', {
  canActivate: () => state.playerField.some(c => c && (c.trait || '').includes('(MF)') && effectiveBp(c) <= 4),
  main: (card, ctx) => {
    const candidates = state.playerField.filter(c => c && (c.trait || '').includes('(MF)') && effectiveBp(c) <= 4);
    if (candidates.length === 0) {
      addLog(`${card.name} — no (MF) Units with 4 or less AP. No effect.`);
      offerPairFromTrash(card, '(MF)', ctx.onDone);
      return;
    }
    showCardPickerModal(card.name, 'Choose 1 of your (MF) Units with 4 or less AP. It gets AP+2 during this turn.', candidates, (target) => {
      if (target) {
        applyApMod([target], 2, 'tempApMod');
        addLog(`${card.name} — ${target.name} gets AP+2 during this turn.`);
        // applyApMod() only self-renders on the RECEIVING peer via
        // NET.onFieldApMod — the caster needs its own render() here since
        // offerPairFromTrash() below doesn't always trigger one itself
        // (e.g. no eligible trash-pair target, or the player declines).
        render();
      }
      offerPairFromTrash(card, '(MF)', ctx.onDone);
    }, card, { mandatory: true });
  },
});

// ─── GD05-127 · Girty Lue — Base ────────────────────────────────────────────
// [Burst]/[Deploy] handled generically (deploy this card / shield to hand).
// [Once per Turn] When a friendly (Phantom Pain) Unit links, choose 1 enemy
// Unit. It can't activate <Blocker> during this turn.
registerCardEffect('GD05-127', {
  onPlayerUnitLinked: (base, unit) => {
    if (base.passiveUsedThisTurn) return;
    if (!(unit.trait || '').includes('(Phantom Pain)')) return;
    base.passiveUsedThisTurn = true;
    const candidates = state.oppField.filter(c => c);
    if (candidates.length === 0) return;
    showCardPickerModal(base.name, "Choose 1 enemy Unit. It can't activate <Blocker> during this turn.", candidates, (target) => {
      if (target) {
        target.tempCantBlock = true;
        NET.send({ type: 'FIELD_AP_MOD', uids: [target.uid], delta: 1, field: 'tempCantBlock' });
        addLog(`${base.name} — ${target.name} can't activate <Blocker> this turn.`);
        render();
      }
    }, base, { mandatory: true });
  },
});

// ─── GD05-128 · Gundam Fight — Base ─────────────────────────────────────────
// [Burst]/[Deploy] handled generically (deploy this card / shield to hand).
// [Activate･Main] Rest this Base: If a friendly (MF) Link Unit is in play,
// choose 1 friendly Unit. It gets AP+2 during this turn.
// "Rest this Base" cost parsed generically by getBaseActivationCost().
registerCardEffect('GD05-128', {
  canActivate: () => state.playerField.some(c => c && isLinkUnit(c) && (c.trait || '').includes('(MF)')),
  activate: (card, ctx) => {
    const hasLink = state.playerField.some(c => c && isLinkUnit(c) && (c.trait || '').includes('(MF)'));
    if (!hasLink) {
      addLog(`${card.name} — no friendly (MF) Link Unit in play. No effect.`);
      ctx.onDone(); return;
    }
    const candidates = state.playerField.filter(c => c);
    if (candidates.length === 0) { ctx.onDone(); return; }
    showCardPickerModal(card.name, 'Choose 1 friendly Unit. It gets AP+2 during this turn.', candidates, (target) => {
      if (target) {
        applyApMod([target], 2, 'tempApMod');
        addLog(`${card.name} — ${target.name} gets AP+2 during this turn.`);
        render();
      }
      ctx.onDone();
    }, card, { mandatory: true });
  },
});

// ─── ST12-005 · GQuuuuuuX (Omega Psycommu) ──────────────────────────────────
// [Attack] You may discard 1. If you do, draw 1.
registerCardEffect('ST12-005', {
  attack: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    if (state.hand.length === 0) { ctx.onDone(); return; }
    showCardPickerModal(card.name, 'You may discard 1 card from your hand. If you do, draw 1.', [...state.hand], (discarded) => {
      if (discarded) {
        state.hand = state.hand.filter(c => c.uid !== discarded.uid);
        updateTempState(discarded.cardNo, discarded.copyVer, 'trash');
        state.trashCount++;
        state.playerTrash.push(toTrashCard(discarded));
        broadcastLog(`${card.name} — discarded ${discarded.name}.`);
        broadcastCardToast(`${discarded.name} was discarded due to ${card.name} effect.`);
        const drawn = drawCardsFromTempDeck(1, true);
        drawn.forEach(c => state.hand.push(c));
        addLog(`${card.name} — drew 1 card.`);
        broadcastCounters();
        render();
      }
      ctx.onDone();
    }, card, { mandatory: false, skipLabel: 'Decline' });
  },
});

// ─── ST12-011 · Milliadro Peacecraft — Pilot ────────────────────────────────
// [Activate･Action][Once per Turn] Choose 1 damaged enemy Unit battling this
// Unit. Deal 2 damage to it.
// Action Step eligibility (getActionEligiblePilots) already gates this to
// [Activate･Action] + once-per-turn via pilot.passiveUsedThisTurn, and fires
// on both attacker's and defender's Action Step — no phase/turn check needed.
function st12011BattlingEnemyUnit(battle, unitUid) {
  if (!battle) return null;
  const enemyUnit = battle.attacker?.uid === unitUid ? battle.defender
                   : battle.defender?.uid === unitUid ? battle.attacker : null;
  if (!enemyUnit || battle.isShield || battle.isDirect) return null;
  if (enemyUnit.type !== 'unit' && enemyUnit.type !== 'unit-token') return null;
  return enemyUnit;
}
registerCardEffect('ST12-011', {
  canActivate: (card, ctx) => {
    const unit = state.playerField.find(c => c && c.pilot?.uid === card.uid);
    if (!unit) return false;
    const enemyUnit = st12011BattlingEnemyUnit(ctx, unit.uid);
    return !!enemyUnit && !!enemyUnit.damaged;
  },
  activate: (card, ctx) => {
    const unit = ctx.unit || state.playerField.find(c => c && c.pilot?.uid === card.uid);
    const battle = ctx.battle || ctx;
    const enemyUnit = unit && st12011BattlingEnemyUnit(battle, unit.uid);
    if (!enemyUnit || !enemyUnit.damaged) {
      addLog(`${card.name} — no damaged enemy Unit battling this Unit. No effect.`);
      ctx.onDone?.();
      return;
    }
    showCardPickerModal(
      card.name,
      'Choose 1 damaged enemy Unit battling this Unit. Deal 2 damage to it.',
      [enemyUnit],
      (target) => {
        if (target) {
          dealDamageToUnit(target, 2, { fromEnemy: true });
          render(); setupDropZones();
        }
        ctx.onDone?.();
      },
      card,
      { mandatory: false, skipLabel: 'Skip' }
    );
  },
});

// ─── ST13-007 · Gyunei's Jagd Doga ──────────────────────────────────────────
// [Activate･Main]<Support 1> — identical effect to GD02-049 Daughtress.
registerCardEffect('ST13-007', {
  canActivate: (card) => {
    if (card.rested) return false;
    return state.playerField.some(c => c && c.uid !== card.uid);
  },
  activate: (card, ctx) => {
    const value = parseInt((card.nativeKeyword || '').match(/Support (\d+)/)?.[1]) || 0;
    setRested(card, true);
    const candidates = state.playerField.filter(c => c && c.uid !== card.uid);
    pickAndApplyApMod(
      card,
      candidates,
      value,
      'tempApMod',
      `Choose 1 other friendly Unit. It gets AP+${value} during this turn.`,
      ctx.onDone
    );
  },
});

// ─── ST13-012 · Suletta Mercury — Pilot ─────────────────────────────────────
// [Once per Turn] When an enemy Unit is destroyed with effect damage while
// this Unit is attacking, draw 1.
// onLinkedUnitEffectDamageDestroysEnemy — new hook (see its call site in
// dealDamageToUnit, js/game.js) fired on the current attacker's paired pilot
// specifically while that Unit is the attacker of a battle in progress; not
// a battle-damage kill (that's onLinkedUnitDestroysEnemy), and not gated to
// "while attacking" at all for other cards since the hook itself already
// only fires during that Unit's own attack.
registerCardEffect('ST13-012', {
  onLinkedUnitEffectDamageDestroysEnemy: (pilot) => {
    if (pilot.passiveUsedThisTurn) return;
    pilot.passiveUsedThisTurn = true;
    const drawn = drawCardsFromTempDeck(1, true);
    if (drawn.length > 0) {
      state.hand.push(drawn[0]);
      addLog(`${pilot.name} — [Once per Turn] an enemy Unit was destroyed by effect damage while attacking: drew ${drawn[0].name}.`);
    } else {
      addLog(`${pilot.name} — [Once per Turn] deck is empty, no draw.`);
    }
    broadcastCounters();
  },
});

// ─── ST13-010 · Red Gundam (0079) ───────────────────────────────────────────
// [Activate･Main][Once per Turn] Destroy 1 friendly Unit token:
// This Unit gains <Breach 3> during this turn.
registerCardEffect('ST13-010', {
  canActivate: (card) =>
    !card.activateMainUsedThisTurn &&
    state.playerField.some(c => c && isUnitToken(c)),
  activate: (card, ctx) => {
    card.activateMainUsedThisTurn = true;
    const tokens = state.playerField.filter(c => c && isUnitToken(c));
    if (tokens.length === 0) { card.activateMainUsedThisTurn = false; ctx.onDone(); return; }
    const grantBreach = () => {
      card.tempKeywords = card.tempKeywords || [];
      if (!card.tempKeywords.includes('Breach 3')) card.tempKeywords.push('Breach 3');
      NET.send({ type: 'TEMP_KEYWORD_ADD', uid: card.uid, keyword: 'Breach 3' });
      addLog(`${card.name} — gains <Breach 3> during this turn.`);
      render();
      setupDropZones();
      ctx.onDone();
    };
    if (tokens.length === 1) {
      effectDestroyOwnUnit(tokens[0], card);
      grantBreach();
      return;
    }
    showCardPickerModal(
      card.name,
      'Destroy 1 friendly Unit token as cost. This Unit gains <Breach 3> during this turn.',
      tokens,
      (target) => {
        if (!target) { card.activateMainUsedThisTurn = false; ctx.onDone(); return; }
        effectDestroyOwnUnit(target, card);
        grantBreach();
      },
      card, { mandatory: true }
    );
  },
});

// ─── ST12-003 · Tallgeese III ────────────────────────────────────────────────
// [Once per Turn] During your turn, when this Unit destroys an enemy Unit
// with damage, deal 1 damage to all enemy Units with 3 or less AP.
// onUnitDestroysEnemy — standing hook fired by notifyUnitDestroyedByBattle
// (battle damage only), regardless of whether this Unit won as attacker or
// defender — the card text doesn't say "while attacking". Fires
// unconditionally for whichever side's card scored the kill (game.js's
// notifyUnitDestroyedByBattle doesn't gate the call itself), so
// destroyerSide === 'player' must be checked here or this would also fire
// when the OPPONENT's own copy of this card destroys one of MY Units.
registerCardEffect('ST12-003', {
  onUnitDestroysEnemy: (card, destroyed, destroyerSide) => {
    if (destroyerSide !== 'player' || !state.myTurn) return;
    if (card.passiveUsedThisTurn) return;
    card.passiveUsedThisTurn = true;
    const targets = state.oppField.filter(c => c && effectiveBp(c) <= 3);
    if (targets.length === 0) {
      addLog(`${card.name} — [Once per Turn] no enemy Units with 3 or less AP.`);
      return;
    }
    targets.forEach(t => dealDamageToUnit(t, 1, { fromEnemy: true }));
    addLog(`${card.name} — [Once per Turn] dealt 1 damage to ${targets.map(t => t.name).join(', ')} (3 or less AP).`);
    render();
    setupDropZones();
  },
});

// ─── ST13-006 · Gundam Aerial ────────────────────────────────────────────────
// [During Pair][Attack] If you are attacking the enemy player, choose 1
// enemy Unit. Deal 2 damage to it.
// [Activate･Action][Once per Turn] Rest 1 friendly Unit: Choose 1 enemy Unit
// that is Lv.4 or lower. Deal 1 damage to it.
// "During Pair" = any paired pilot (card.pilot truthy, no isLinkUnit check —
// same convention as ST08-006). "Attacking the enemy player" = base/shield/
// direct, via isAttackingEnemyPlayer(). The Activate·Action ability's
// Once-per-Turn gate + eligibility are its own canAction hook (unlike
// Activate·Main, the generic Action-Step cost parser only checks canAction —
// see GD05-021/gd04006ActivateEffect for the "rest 1 friendly Unit as cost,
// then pick a target" shape this mirrors); "Rest 1 friendly Unit" doesn't say
// "other", so this Unit itself is a valid cost target too.
registerCardEffect('ST13-006', {
  attack: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    if (!card.pilot) { ctx.onDone?.(); return; }
    if (!isAttackingEnemyPlayer(ctx)) { ctx.onDone?.(); return; }
    const candidates = state.oppField.filter(c => c);
    if (candidates.length === 0) {
      addLog(`${card.name} — [During Pair][Attack] no enemy Units in play. No effect.`);
      ctx.onDone?.(); return;
    }
    showCardPickerModal(card.name, 'Choose 1 enemy Unit. Deal 2 damage to it.', candidates, (target) => {
      if (target) {
        dealDamageToUnit(target, 2, { fromEnemy: true });
        render(); setupDropZones();
      }
      ctx.onDone?.();
    }, card, { mandatory: true });
  },
  canAction: (card) => {
    const hasCostUnit = state.playerField.some(c => c && !c.rested);
    const hasQualifyingEnemy = state.oppField.some(c => c && (parseInt(c.level) || 0) <= 4);
    return hasCostUnit && hasQualifyingEnemy;
  },
  action: (card, ctx) => {
    const costCandidates = state.playerField.filter(c => c && !c.rested);
    if (costCandidates.length === 0) { ctx.onDone(); return; }
    showCardPickerModal(card.name, 'Rest 1 friendly Unit as cost.', costCandidates, (costUnit) => {
      if (!costUnit) { ctx.onDone(); return; }
      setRested(costUnit, true);
      addLog(`${card.name} — rested ${costUnit.name} as cost.`);
      render(); setupDropZones();
      const enemyCandidates = state.oppField.filter(c => c && (parseInt(c.level) || 0) <= 4);
      if (enemyCandidates.length === 0) {
        addLog(`${card.name} — no enemy Units Lv.4 or lower.`);
        ctx.onDone(); return;
      }
      showCardPickerModal(card.name, 'Choose 1 enemy Unit that is Lv.4 or lower. Deal 1 damage to it.', enemyCandidates, (target) => {
        if (target) {
          dealDamageToUnit(target, 1, { fromEnemy: true });
          render(); setupDropZones();
        }
        ctx.onDone();
      }, card, { mandatory: true });
    }, card, { mandatory: true });
  },
});

// ─── GD06-035 · Zeta Gundam ──────────────────────────────────────────────────
// [Once per Turn] During your turn, when this Unit destroys an enemy Unit
// with battle damage, choose 1 enemy Unit with 3 or less AP. Deal 2 damage
// to it.
// onUnitDestroysEnemy — standing hook on the unit itself, fired by
// notifyUnitDestroyedByBattle() in game.js for both attacker and defender
// kills (the printed text says "with battle damage", not "while attacking",
// so this isn't restricted to destroyerIsAttacker). Once-per-turn gated via
// passiveUsedThisTurn, same convention as GD03-023.
registerCardEffect('GD06-035', {
  onUnitDestroysEnemy: (card, destroyed, destroyerSide) => {
    if (destroyerSide !== 'player' || !state.myTurn) return;
    if (card.passiveUsedThisTurn) return;
    const candidates = state.oppField.filter(c => c && effectiveBp(c) <= 3);
    if (candidates.length === 0) {
      addLog(`${card.name} — destroyed ${destroyed.name}, but no enemy Units with 3 or less AP. No effect.`);
      return;
    }
    card.passiveUsedThisTurn = true;
    showCardPickerModal(card.name, 'Choose 1 enemy Unit with 3 or less AP. Deal 2 damage to it.', candidates, (target) => {
      if (target) {
        dealDamageToUnit(target, 2, { fromEnemy: true });
        addLog(`${card.name} — destroyed ${destroyed.name}: dealt 2 damage to ${target.name}.`);
        render(); setupDropZones();
      }
    }, card, { mandatory: true });
  },
});

// ─── GD06-036 · Gundam Vidar ─────────────────────────────────────────────────
// [When Linked] Choose 1 enemy Unit. Deal 1 damage to it.
// pair trigger + isLinkUnit(ctx.unit) guard — fires on the Unit's own
// handler (triggerCardEffect fires 'pair' on both the pilot AND the paired
// unit; ctx.unit === card here), same convention as GD01-095's pilot-side
// [When Linked] check, just checked against the unit itself.
registerCardEffect('GD06-036', {
  pair: (card, ctx) => {
    if (!isLinkUnit(ctx.unit)) return; // [When Linked]
    const candidates = state.oppField.filter(c => c);
    if (candidates.length === 0) {
      addLog(`${card.name} — [When Linked] no enemy Units in play. No effect.`);
      return;
    }
    showCardPickerModal(card.name, 'Choose 1 enemy Unit. Deal 1 damage to it.', candidates, (target) => {
      if (target) {
        dealDamageToUnit(target, 1, { fromEnemy: true });
        render(); setupDropZones();
      }
    }, card, { mandatory: true });
  },
});

// ─── GD06-038 · Gundam Kimaris Vidar ─────────────────────────────────────────
// <Breach 3>
// [When Paired] Choose 1 enemy Unit with the highest Lv. Deal 3 damage to it.
// Same "highest Lv, tie shows a picker" shape as ST08-001.
registerCardEffect('GD06-038', {
  pair: (card) => {
    const enemies = state.oppField.filter(c => c);
    if (enemies.length === 0) { addLog(`${card.name} — [When Paired] no enemy Units. No effect.`); return; }
    const maxLv = Math.max(...enemies.map(c => parseInt(c.level) || 0));
    const candidates = enemies.filter(c => (parseInt(c.level) || 0) === maxLv);
    if (candidates.length === 1) {
      dealDamageToUnit(candidates[0], 3, { fromEnemy: true });
      addLog(`${card.name} — [When Paired] dealt 3 damage to ${candidates[0].name} (highest Lv: ${maxLv}).`);
      render(); setupDropZones();
    } else {
      showCardPickerModal(card.name, `Choose 1 enemy Unit with the highest Lv (${maxLv}). Deal 3 damage to it.`, candidates, (target) => {
        if (!target) return;
        dealDamageToUnit(target, 3, { fromEnemy: true });
        addLog(`${card.name} — [When Paired] dealt 3 damage to ${target.name} (Lv: ${maxLv}).`);
        render(); setupDropZones();
      }, card, { mandatory: true });
    }
  },
});

// ─── GD06-043 · Julieta's Reginlaze ──────────────────────────────────────────
// [During Link] During your turn, all other (Gjallarhorn) Units get AP+1.
// "During your turn" read relative to ownerSide, not state.myTurn directly —
// see ST01-001 Gundam (blue.js) for why a bare state.myTurn check breaks in
// networked play (always wrong from the non-controlling client's mirrored
// perspective).
registerCardEffect('GD06-043', {
  globalUnitApBonus: (targetCard, sourceCard, ownerSide) => {
    if (!isLinkUnit(sourceCard)) return 0;
    if (targetCard.uid === sourceCard.uid) return 0; // "all OTHER (Gjallarhorn) Units"
    if (!(targetCard.trait || '').includes('(Gjallarhorn)')) return 0;
    const isControllersTurn = ownerSide === 'player' ? state.myTurn : !state.myTurn;
    return isControllersTurn ? 1 : 0;
  },
});

// ─── GD06-090 · Vidar — Pilot ────────────────────────────────────────────────
// This card's name is also treated as [Gaelio Bauduin]. (generic — parsed
// automatically by buildGameCardFromDb's pilotAliases regex, no code needed.)
// [Burst] Add this card to your hand. (generic)
// [Attack] If you have 2 or more (Gjallarhorn) Units in play, choose 1 enemy
// Unit with 5 or less AP. Deal 1 damage to it.
// Plain [Attack] (no "During Link" qualifier) — fires for any paired pilot,
// same as fireAttackerPilotAttackTrigger's own only-checks-card.pilot gate.
registerCardEffect('GD06-090', {
  attack: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    const gjallarhornCount = state.playerField.filter(c => c && (c.trait || '').includes('(Gjallarhorn)')).length;
    if (gjallarhornCount < 2) { ctx.onDone?.(); return; }
    const candidates = state.oppField.filter(c => c && effectiveBp(c) <= 5);
    if (candidates.length === 0) {
      addLog(`${card.name} — [Attack] no enemy Units with 5 or less AP.`);
      ctx.onDone?.(); return;
    }
    showCardPickerModal(card.name, 'Choose 1 enemy Unit with 5 or less AP. Deal 1 damage to it.', candidates, (target) => {
      if (target) {
        dealDamageToUnit(target, 1, { fromEnemy: true });
        render(); setupDropZones();
      }
      ctx.onDone?.();
    }, card, { mandatory: true });
  },
});

// ─── GD04-046 · Gundam Dynames ───────────────────────────────────────────────
// [Deploy] You may rest this Unit. If you do, choose 1 enemy Unit that is
// Lv.3 or lower. Deal 2 damage to it.
registerCardEffect('GD04-046', {
  deploy: (card) => {
    showCardPickerModal(card.name, 'You may rest this Unit. If you do, choose 1 enemy Unit (Lv.3 or lower) and deal 2 damage to it.', [card], (chosen) => {
      if (!chosen) return;
      setRested(card, true);
      addLog(`${card.name} — rested itself.`);
      render(); setupDropZones();
      const candidates = state.oppField.filter(c => c && (parseInt(c.level) || 0) <= 3);
      if (candidates.length === 0) {
        addLog(`${card.name} — no enemy Units Lv.3 or lower. No effect.`);
        return;
      }
      showCardPickerModal(card.name, 'Choose 1 enemy Unit (Lv.3 or lower). Deal 2 damage to it.', candidates, (target) => {
        if (target) dealDamageToUnit(target, 2, { fromEnemy: true });
        render(); setupDropZones();
      }, card, { mandatory: true });
    }, card, { mandatory: false, skipLabel: 'Decline' });
  },
});

// ─── GD06-113 · Unsanctioned Ordnance — Command ─────────────────────────────
// [Burst] Choose 1 enemy Unit. Deal 2 damage to it.
// [Main]/[Action] Choose 1 damaged enemy Unit. Deal 5 damage to it.
// Same Burst-activates-Main / hand-vs-Action-Step shared resolver shape as
// GD05-110 Darkness Finger.
function gd06113Effect(card, onDone) {
  const candidates = state.oppField.filter(c => c && c.damaged);
  if (candidates.length === 0) { addLog(`${card.name} — no damaged enemy Units. No effect.`); onDone(); return; }
  showCardPickerModal(card.name, 'Choose 1 damaged enemy Unit. Deal 5 damage to it.', candidates, (target) => {
    if (target) {
      dealDamageToUnit(target, 5, { fromEnemy: true });
      addLog(`${card.name} — dealt 5 damage to ${target.name}.`);
      broadcastCounters(); render(); setupDropZones();
    }
    onDone();
  }, card, { mandatory: true });
}
registerCardEffect('GD06-113', {
  burst: (card, ctx) => {
    addLog(`${card.name} — [Burst] activates this card's [Main].`);
    const handled = triggerCardEffect(card, 'main', { onDone: () => fireCommandEffectPassives(card, ctx?.onDone ?? (() => {})) });
    if (!handled) ctx?.onDone?.();
  },
  canActivate: () => state.oppField.some(c => c && c.damaged),
  main: (card, ctx) => gd06113Effect(card, ctx.onDone),
  action: (card, ctx) => gd06113Effect(card, ctx.onDone),
});

// ─── ST12-013 · The Final Victor — Command ──────────────────────────────────
// [Burst] Choose 1 enemy Unit. Deal 1 damage to it.
// [Main] Choose 1 enemy player. You and that player each choose 1 of your
// own Units. Begin a battle between them and only perform the damage step.
// (You choose first, then your opponent.)
//
// New request/response round trip: the activating player picks their own
// Unit locally, then sends REQUEST_MUTUAL_BATTLE_UNIT so the opponent picks
// one of THEIR OWN Units (only they have authoritative field data) and
// replies with MUTUAL_BATTLE_UNIT_RESPONSE. Once both uids are known on the
// activating client, it dispatches the shared skipToDamage ATTACK shortcut
// (same one GD05-017 Nu Gundam and ST13-001 Qubeley already use) between
// them. If the opponent has no Units at all, they reply uid: null and the
// effect fizzles with no battle — same "no valid target" convention as
// every other card here.
// Neither chosen Unit ever rests from this: the card text never says to
// rest anything, and the shared dispatch handler treats skipToDamage alone
// as reason enough to skip resting the attacker, precisely because — unlike
// GD05-017/Qubeley, which always attack with a fixed known cardNo they can
// register a canStayActiveAfterAttack hook on — the attacker here is
// whichever arbitrary Unit either player happens to choose.
registerCardEffect('ST12-013', {
  burst: (card, ctx) => {
    const candidates = state.oppField.filter(c => c);
    if (candidates.length === 0) { addLog(`${card.name} — [Burst] no enemy Units. No effect.`); ctx?.onDone?.(); return; }
    showCardPickerModal(card.name, '[Burst] Choose 1 enemy Unit. Deal 1 damage to it.', candidates, (target) => {
      if (target) {
        dealDamageToUnit(target, 1, { fromEnemy: true });
        addLog(`${card.name} — [Burst] dealt 1 damage to ${target.name}.`);
        broadcastCounters(); render(); setupDropZones();
      }
      ctx?.onDone?.();
    }, card, { mandatory: true });
  },
  main: (card, ctx) => {
    if (!state.networked) {
      addLog(`${card.name} — requires an opponent to choose their own Unit; unavailable in solo practice.`);
      ctx.onDone();
      return;
    }
    const myCandidates = state.playerField.filter(c => c);
    if (myCandidates.length === 0) {
      addLog(`${card.name} — you have no Units. No effect.`);
      ctx.onDone();
      return;
    }
    showCardPickerModal(card.name, 'Choose 1 of your Units for the mutual battle.', myCandidates, (myUnit) => {
      if (!myUnit) { ctx.onDone(); return; }
      pendingMutualBattleResolve = (enemyUid) => {
        pendingMutualBattleResolve = null;
        hideDecisionWaitingOverlay();
        if (!enemyUid) {
          addLog(`${card.name} — the enemy player has no Units. No effect.`);
          ctx.onDone();
          return;
        }
        dispatch('ATTACK', {
          attackerUid: myUnit.uid,
          defenderUid: enemyUid,
          isShield: false,
          isDirect: false,
          skipToDamage: true,
        });
        ctx.onDone();
      };
      armStuckWaitWatchdog(() => pendingMutualBattleResolve, (fn) => { pendingMutualBattleResolve = null; fn(null); });
      setTimeout(() => {
        if (pendingMutualBattleResolve !== null) {
          showDecisionWaitingOverlay("Waiting on opponent to choose their Unit…");
        }
      }, 400);
      NET.send({ type: 'REQUEST_MUTUAL_BATTLE_UNIT' });
    }, card, { mandatory: true });
  },
});

// ════════════════════════════════════════════════════════════════════════════
// GD06 batch — "implement all remaining effects" pass
// ════════════════════════════════════════════════════════════════════════════

// ─── GD02-041 · Sugai's Gelgoog (GQ) — Unit ────────────────────────────────
// [Deploy] Choose 1 enemy Unit that is Lv.5 or higher. Deal 2 damage to it.
registerCardEffect('GD02-041', {
  deploy: (card) => {
    const candidates = state.oppField.filter(c => c && (parseInt(c.level) || 0) >= 5);
    if (candidates.length === 0) {
      addLog(`${card.name} — no enemy Units Lv.5 or higher. No effect.`);
      return;
    }
    showCardPickerModal(card.name, 'Choose 1 enemy Unit that is Lv.5 or higher. Deal 2 damage to it.', candidates, (target) => {
      if (target) {
        dealDamageToUnit(target, 2, { fromEnemy: true });
        addLog(`${card.name} — dealt 2 damage to ${target.name}.`);
        broadcastCounters(); render(); setupDropZones();
      }
    }, card, { mandatory: true });
  },
});

// ─── ST08-002 · Ξ Gundam — Unit ────────────────────────────────────────────
// [Deploy] Choose 1 enemy Unit. Deal 1 damage to it.
registerCardEffect('ST08-002', {
  deploy: (card) => {
    const candidates = state.oppField.filter(c => c);
    if (candidates.length === 0) return;
    showCardPickerModal(card.name, 'Choose 1 enemy Unit. Deal 1 damage to it.', candidates, (target) => {
      if (target) {
        dealDamageToUnit(target, 1, { fromEnemy: true });
        addLog(`${card.name} — dealt 1 damage to ${target.name}.`);
        broadcastCounters(); render(); setupDropZones();
      }
    }, card, { mandatory: true });
  },
});

// ─── GD03-040 · Gundam Virsago & Gundam Ashtaron — Unit ────────────────────
// [During Link] This Unit gains <High-Maneuver>. Same convention as GD04-008.
registerCardEffect('GD03-040', {
  passiveKeywords: (card) => isLinkUnit(card) ? ['High-Maneuver'] : [],
});

// ─── GD04-035 · Ξ Gundam — Unit ────────────────────────────────────────────
// [Deploy] Choose 1 of your (Mafty) Units. When it destroys an enemy Unit
// with battle damage during this turn, if you have 3 or less cards in your
// hand, draw 1.
// turnDestroyWatchers — same mechanism as GD05-002 Strike Freedom Gundam's
// "choose 1-2 Units, they draw 1 when they destroy an enemy with battle
// damage this turn" grant, just single-target and with the added hand-size
// condition checked at destroy time (not at grant time).
registerCardEffect('GD04-035', {
  deploy: (card) => {
    const candidates = state.playerField.filter(c => c && (c.trait || '').includes('(Mafty)'));
    if (candidates.length === 0) {
      addLog(`${card.name} — no friendly (Mafty) Units. No effect.`);
      return;
    }
    showCardPickerModal(card.name, 'Choose 1 of your (Mafty) Units. When it destroys an enemy Unit with battle damage this turn, draw 1 if you have 3 or less cards in hand.', candidates, (target) => {
      if (!target) return;
      const uid = target.uid;
      turnDestroyWatchers.push((destroyer, _destroyed, _destroyerSide, targetType) => {
        // Card text says "an enemy Unit" specifically — unlike GD05-002
        // Strike Freedom Gundam's "an enemy card" (see turnDestroyWatchers
        // in game.js), a shield or base kill must NOT trigger this.
        if (targetType !== 'unit') return;
        if (destroyer.uid !== uid) return;
        if (state.hand.length > 3) return;
        const drawn = drawCardsFromTempDeck(1, true);
        if (drawn.length > 0) {
          state.hand.push(drawn[0]);
          addLog(`${card.name} — ${destroyer.name} destroyed an enemy Unit (3 or less cards in hand): drew 1.`);
          broadcastCounters(); render();
        }
      });
      addLog(`${card.name} — ${target.name} will draw a card on a battle-damage kill this turn (if hand is 3 or less).`);
    }, card, { mandatory: true });
  },
});

// ─── GD06-041 · Neo Lorrnoke's Windam (Jet Striker) — Unit ─────────────────
// [Deploy] Choose 1 enemy Unit with 5 or less AP. Deal 1 damage to it.
registerCardEffect('GD06-041', {
  deploy: (card) => {
    const candidates = state.oppField.filter(c => c && effectiveBp(c) <= 5);
    if (candidates.length === 0) {
      addLog(`${card.name} — no enemy Units with 5 or less AP. No effect.`);
      return;
    }
    showCardPickerModal(card.name, 'Choose 1 enemy Unit with 5 or less AP. Deal 1 damage to it.', candidates, (target) => {
      if (target) {
        dealDamageToUnit(target, 1, { fromEnemy: true });
        addLog(`${card.name} — dealt 1 damage to ${target.name}.`);
        broadcastCounters(); render(); setupDropZones();
      }
    }, card, { mandatory: true });
  },
});

// ─── GD06-046 · Hyaku-Shiki — Unit ──────────────────────────────────────────
// [Attack] Choose 1 of your other (Gundam Team) Units. It gets AP+2 during
// this turn.
registerCardEffect('GD06-046', {
  attack: (card, ctx) => {
    const candidates = state.playerField.filter(c => c && c.uid !== card.uid && (c.trait || '').includes('(Gundam Team)'));
    if (candidates.length === 0) {
      addLog(`${card.name} — [Attack] no other friendly (Gundam Team) Units. No effect.`);
      ctx.onDone();
      return;
    }
    showCardPickerModal(card.name, 'Choose 1 of your other (Gundam Team) Units. It gets AP+2 during this turn.', candidates, (target) => {
      if (target) {
        applyApMod([target], 2, 'tempApMod');
        addLog(`${card.name} — ${target.name} gets AP+2 during this turn.`);
        render(); setupDropZones();
      }
      ctx.onDone();
    }, card, { mandatory: true });
  },
});

// ─── GD06-042 · Destroy Gundam (MA Mode) — Unit ────────────────────────────
// [Deploy] Look at the top 4 cards of your deck. You may pair 1 (Biological
// CPU) Pilot card among them with this Unit. Return the remaining cards
// randomly to the bottom of your deck.
// Synthetic top-deck pairing — same mechanics as GD01-023 Char's Gelgoog's
// "pair it with this Unit" (from trash instead of a peek), reused here with
// peekTopOfDeck/shuffle-to-bottom for the remainder (same shuffle as
// lookAtTopAndPickToHand, green.js).
registerCardEffect('GD06-042', {
  deploy: (card, ctx) => {
    const slot = (typeof ctx?.slot === 'number') ? ctx.slot : state.playerField.findIndex(c => c && c.uid === card.uid);
    if (slot === -1 || slot === undefined) return;
    const peeked = peekTopOfDeck(4);
    if (peeked.length === 0) {
      addLog(`${card.name} — deck is empty. No effect.`);
      return;
    }
    const disabledIndices = peeked
      .map((p, i) => (((p.card.type === 'pilot' || p.card.type === 'command pilot') && (p.card.trait || '').includes('(Biological CPU)')) ? -1 : i))
      .filter(i => i !== -1);
    showCardPickerModal(
      card.name,
      'Look at the top 4 cards of your deck. You may pair 1 (Biological CPU) Pilot card among them with this Unit.',
      peeked.map(p => p.card),
      (chosen) => {
        let chosenPeek = null;
        if (chosen) {
          chosenPeek = peeked.find(p => p.card.uid === chosen.uid);
          if (chosenPeek && state.playerField[slot] && !state.playerField[slot].pilot) {
            const pairedUnit = { ...state.playerField[slot], pilot: chosenPeek.card };
            if (isLinkUnit(pairedUnit)) {
              pairedUnit.linkedThisTurn = true;
              // Same fix as GD01-023 Char's Gelgoog (green.js): this pairs a
              // Pilot inline (from a deck peek, not the standard PAIR_PILOT
              // action), so newly satisfying the Link requirement has to
              // clear deploy sickness itself instead of getting it for free.
              pairedUnit.tempCantAttack = false;
              if (state.playerBase) {
                window.CardEffects?.[state.playerBase.cardNo]?.onPlayerUnitLinked?.(state.playerBase, pairedUnit, slot);
              }
            }
            state.playerField[slot] = pairedUnit;
            addLog(`${card.name} — paired ${chosenPeek.card.name} (from top of deck).`);
            triggerCardEffect(chosenPeek.card, 'pair', { unit: pairedUnit, slot });
            triggerCardEffect(pairedUnit, 'pair', { unit: pairedUnit, slot });
            NET.send({ type: 'PLAY_CARD', slot, card: state.playerField[slot], counters: myCounters() });
          }
        }
        const remaining = chosenPeek ? peeked.filter(p => p !== chosenPeek) : peeked;
        for (let i = remaining.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [remaining[i], remaining[j]] = [remaining[j], remaining[i]];
        }
        remaining.forEach(p => sendDeckEntryToBottom(p.entry));
        broadcastCounters(); render(); setupDropZones();
      },
      card,
      { mandatory: false, disabledIndices, skipLabel: 'Decline' }
    );
  },
});

// ─── GD06-092 · Neo Lorrnoke — Unit ─────────────────────────────────────────
// [When Paired] If this is a (Phantom Pain)/(Orb) Unit, choose 1 enemy Unit
// whose AP is equal to or less than this Unit. Deal 1 damage to it.
registerCardEffect('GD06-092', {
  pair: (card, ctx) => {
    const unit = ctx?.unit;
    if (!unit) return;
    const isPPOrOrb = (unit.trait || '').includes('(Phantom Pain)') || (unit.trait || '').includes('(Orb)');
    if (!isPPOrOrb) return;
    const myAp = effectiveBp(unit);
    const candidates = state.oppField.filter(c => c && effectiveBp(c) <= myAp);
    if (candidates.length === 0) {
      addLog(`${card.name} — [When Paired] no enemy Units with AP equal to or less than this Unit. No effect.`);
      return;
    }
    showCardPickerModal(card.name, "[When Paired] Choose 1 enemy Unit whose AP is equal to or less than this Unit. Deal 1 damage to it.", candidates, (target) => {
      if (target) {
        dealDamageToUnit(target, 1, { fromEnemy: true });
        addLog(`${card.name} — dealt 1 damage to ${target.name}.`);
        broadcastCounters(); render(); setupDropZones();
      }
    }, card, { mandatory: true });
  },
});

// ─── ST12-001 · Gundam Epyon — Unit ─────────────────────────────────────────
// [During Pair･Lv.5 or Higher Pilot] During your turn, when this Unit
// destroys an enemy Unit with damage, deal 2 damage to all enemy Units with
// 5 or less AP.
// [Deploy] Choose 1 enemy Base. Deal 5 damage to it.
registerCardEffect('ST12-001', {
  onUnitDestroysEnemy: (destroyer, destroyed, destroyerSide) => {
    if (destroyerSide !== 'player' || !state.myTurn) return;
    if (!destroyer.pilot || (parseInt(destroyer.pilot.level) || 0) < 5) return;
    const targets = state.oppField.filter(c => c && effectiveBp(c) <= 5);
    if (targets.length === 0) return;
    targets.forEach(t => dealDamageToUnit(t, 2, { fromEnemy: true }));
    addLog(`${destroyer.name} — destroyed an enemy Unit: dealt 2 damage to all enemy Units with 5 or less AP.`);
    broadcastCounters(); render(); setupDropZones();
  },
  deploy: (card) => {
    if (!state.oppBase) {
      addLog(`${card.name} — no enemy Base in play.`);
      return;
    }
    addLog(`${card.name} — dealing 5 damage to ${state.oppBase.name}.`);
    dealDamageToBase('opp', 5, { fromEnemy: true });
    render();
    setupDropZones();
  },
});

// ─── ST12-002 · Shining Gundam — Unit ───────────────────────────────────────
// [Deploy] Choose 1 friendly Unit with 5 or more AP. It gains <Breach 3>
// during this turn.
registerCardEffect('ST12-002', {
  deploy: (card) => {
    const candidates = state.playerField.filter(c => c && effectiveBp(c) >= 5);
    if (candidates.length === 0) {
      addLog(`${card.name} — no friendly Units with 5 or more AP. No effect.`);
      return;
    }
    showCardPickerModal(card.name, 'Choose 1 friendly Unit with 5 or more AP. It gains <Breach 3> during this turn.', candidates, (target) => {
      if (target) {
        target.tempKeywords = target.tempKeywords || [];
        if (!target.tempKeywords.includes('Breach 3')) target.tempKeywords.push('Breach 3');
        NET.send({ type: 'TEMP_KEYWORD_ADD', uid: target.uid, keyword: 'Breach 3' });
        addLog(`${card.name} — ${target.name} gains <Breach 3> during this turn.`);
        render(); setupDropZones();
      }
    }, card, { mandatory: true });
  },
});

// ─── ST13-009 · Gundam Pharact — Unit ───────────────────────────────────────
// [Attack] Choose 1 enemy player. That player choose 2 Unit cards from their
// trash. Exile them from the game.
// requestEnemyTrashUnitExile (game.js) — new REQUEST_TRASH_UNIT_EXILE/
// TRASH_UNIT_EXILE_DONE round trip; see its declaration for the full
// rationale (only the enemy player has authoritative trash data).
registerCardEffect('ST13-009', {
  attack: (card, ctx) => {
    requestEnemyTrashUnitExile(2, () => { ctx?.onDone?.(); });
  },
});

// ════════════════════════════════════════════════════════════════════════════
// ST11-14 batch — newly added cards, "implement all remaining effects" pass
// ════════════════════════════════════════════════════════════════════════════

// ─── ST13-014 · Final Duty — Command ────────────────────────────────────────
// [Main] Choose 1 friendly Unit and destroy it. Then, look at the top 4
// cards of your deck and deploy 1 Unit card that is Lv.4 or lower among
// them. Return the remaining cards randomly to the bottom of your deck.
// No "you may" qualifier on the deploy half — mandatory if a qualifying
// card exists among the 4, otherwise the whole peek is returned to the
// bottom. Guards the showCardPickerModal single-mandatory-candidate
// auto-resolve shortcut (game.js) by only entering that picker at all once
// at least one qualifying candidate is confirmed present — with 4 (or
// fewer, near end of deck) cards shown and disabledIndices marking the
// non-qualifying ones, exactly like the "look at top N, reveal 1 matching"
// family (green.js's lookAtTopAndPickToHand), just deploying to the field
// instead of adding to hand, and shuffling the remainder to the bottom
// instead of trashing it.
registerCardEffect('ST13-014', {
  canActivate: () => state.playerField.some(c => c),
  main: (card, ctx) => {
    const candidates = state.playerField.filter(c => c);
    if (candidates.length === 0) {
      addLog(`${card.name} — no friendly Units. No effect.`);
      ctx.onDone();
      return;
    }
    showCardPickerModal(card.name, 'Choose 1 friendly Unit and destroy it.', candidates, (target) => {
      if (!target) { ctx.onDone(); return; }
      effectDestroyOwnUnit(target, card);
      broadcastCounters(); render(); setupDropZones();

      const peeked = peekTopOfDeck(4);
      if (peeked.length === 0) {
        addLog(`${card.name} — deck is empty. No effect.`);
        ctx.onDone();
        return;
      }
      const isQualifying = (c) => c.type === 'unit' && (parseInt(c.level) || 0) <= 4;
      const finishWithShuffle = (chosenPeek) => {
        const remaining = chosenPeek ? peeked.filter(p => p !== chosenPeek) : peeked;
        for (let i = remaining.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [remaining[i], remaining[j]] = [remaining[j], remaining[i]];
        }
        remaining.forEach(p => sendDeckEntryToBottom(p.entry));
        broadcastCounters(); render(); setupDropZones();
        ctx.onDone();
      };
      if (!peeked.some(p => isQualifying(p.card))) {
        addLog(`${card.name} — no Unit card Lv.4 or lower among the top 4. Returning them to the bottom of your deck.`);
        finishWithShuffle(null);
        return;
      }
      const disabledIndices = peeked
        .map((p, i) => (isQualifying(p.card) ? -1 : i))
        .filter(i => i !== -1);
      showCardPickerModal(card.name, 'Deploy 1 Unit card that is Lv.4 or lower among the top 4 cards of your deck.', peeked.map(p => p.card), (chosen) => {
        const chosenPeek = peeked.find(p => p.card.uid === chosen.uid);
        const slot = findNextFieldSlot();
        if (chosenPeek && slot !== -1) {
          const deployedCard = chosenPeek.card;
          state.playerField[slot] = deployedCard;
          updateTempState(deployedCard.cardNo, deployedCard.copyVer, 'field');
          deployedCard.tempCantAttack = true;
          addLog(`${card.name} — deployed ${deployedCard.name} from the top 4 cards of your deck.`);
          triggerCardEffect(deployedCard, 'deploy', { slot, fromDeck: true });
          NET.send({ type: 'PLAY_CARD', slot, card: deployedCard, counters: myCounters() });
          finishWithShuffle(chosenPeek);
        } else {
          finishWithShuffle(null);
        }
      }, card, { mandatory: true, disabledIndices });
    }, card, { mandatory: true });
  },
});

// ─── ST13-015 · Operation: Intercept Solomon — Command ─────────────────────
// [Burst] Add this card to your hand. (generic)
// [Main]/[Action] Exile 3 Unit cards from your trash. Choose 1 enemy Unit.
// Deal 3 damage to it.
// Same "exile N from trash, then a follow-up target picker" shape as
// GD03-050 (purple.js).
function st13015Effect(card, ctx) {
  const candidates = state.playerTrash.filter(c => c.type === 'unit');
  if (candidates.length < 3) {
    addLog(`${card.name} — fewer than 3 Unit cards in trash. No effect.`);
    ctx.onDone();
    return;
  }
  showCardPickerModalMulti(card.name, 'Exile 3 Unit cards from your trash.', candidates,
    { min: 3, max: 3, mandatory: true },
    (chosen) => {
      if (!chosen || chosen.length < 3) { ctx.onDone(); return; }
      exileCardsFromTrash(chosen);
      addLog(`${card.name} — exiled ${chosen.map(c => c.name).join(', ')}.`);
      broadcastCounters(); render();
      const enemies = state.oppField.filter(c => c);
      if (enemies.length === 0) { ctx.onDone(); return; }
      showCardPickerModal(card.name, 'Choose 1 enemy Unit. Deal 3 damage to it.', enemies, (target) => {
        if (target) {
          dealDamageToUnit(target, 3, { fromEnemy: true });
          addLog(`${card.name} — dealt 3 damage to ${target.name}.`);
          broadcastCounters(); render(); setupDropZones();
        }
        ctx.onDone();
      }, card, { mandatory: true });
    },
    card
  );
}
registerCardEffect('ST13-015', {
  canActivate: () => state.playerTrash.filter(c => c.type === 'unit').length >= 3,
  main: (card, ctx) => st13015Effect(card, ctx),
  action: (card, ctx) => st13015Effect(card, ctx),
});

// ─── ST12-016 · Libra — Base ────────────────────────────────────────────────
// [Burst] Deploy this card. (generic)
// [Deploy] Add 1 of your Shields to your hand. (generic)
// [Activate･Main] Rest this Base: During this turn, when a friendly Unit
// destroys an enemy Unit with battle damage, choose 1 enemy Unit that is
// Lv.4 or lower. Deal 1 damage to it.
// The raw DB text originally read "Rest this base" (lowercase 'b'), which
// getBaseActivationCost() (game.js) doesn't recognize as the restBase cost
// keyword (case-sensitive match against "Rest this Base") — fixed directly
// in the DB (every other "Rest this Base" card in the file uses the capital
// B) so the generic rest-cost/once-per-turn gating actually applies here
// too, instead of this base never getting rested or gated by its own
// ability. turnDestroyWatchers — same "any friendly Unit" mechanism as
// GD05-002 Strike Freedom Gundam, just base-triggered and registered fresh
// each activation instead of per-chosen-unit.
registerCardEffect('ST12-016', {
  activate: (card, ctx) => {
    turnDestroyWatchers.push((destroyer, destroyed, destroyerSide, targetType) => {
      // Card text says "an enemy Unit" specifically — unlike GD05-002 Strike
      // Freedom Gundam's "an enemy card" (see turnDestroyWatchers in
      // game.js), a shield or base kill must NOT trigger this.
      if (targetType !== 'unit') return;
      if (destroyerSide !== 'player') return;
      const candidates = state.oppField.filter(c => c && (parseInt(c.level) || 0) <= 4);
      if (candidates.length === 0) return;
      showCardPickerModal(card.name, 'Choose 1 enemy Unit that is Lv.4 or lower. Deal 1 damage to it.', candidates, (target) => {
        if (target) {
          dealDamageToUnit(target, 1, { fromEnemy: true });
          addLog(`${card.name} — dealt 1 damage to ${target.name}.`);
          broadcastCounters(); render(); setupDropZones();
        }
      }, card, { mandatory: true });
    });
    addLog(`${card.name} — [Activate･Main] active this turn: a friendly battle-damage kill deals 1 damage to an enemy Unit Lv.4 or lower.`);
    ctx.onDone();
  },
});

// ─── GD06-112 · Rivalry with Roux — Command Pilot ───────────────────────────
// [Attack] Choose 1 enemy Unit battling a friendly (AEUG) Unit, Deal 2 damage
// to it.
// A Command Pilot card is dual-mode: paired as a Pilot (its plain "[Attack]"
// text — no "During Link" prefix — fires via the unit-level 'attack' hook
// whenever the paired Unit attacks, same as fireAttackerPilotAttackTrigger's
// pilot path but unconditional on Link status) OR played from hand during
// the combat Action Step ('action' hook, ctx.battle). Both modes share the
// same battle-scan logic since the text isn't self-referential. Mirrors
// GD02-118 Heart Set on Revenge's ctx.battle-scanning + canActivate(card,
// ctx) pattern (registry.js: canActivate is called with the raw battle
// object for both call sites).
function gd06112Target(battle) {
  if (!battle) return null;
  // A friendly (AEUG) Unit is attacking — target is the enemy defender.
  if (battle.attackerSide === 'player' && battle.attacker && (battle.attacker.trait || '').includes('(AEUG)') &&
      battle.defenderSide === 'opp' && battle.defender && !battle.defender.isShield && !battle.defender.isDirect) {
    return battle.defender;
  }
  // An enemy Unit is attacking a friendly (AEUG) Unit — target is the enemy attacker.
  if (battle.defenderSide === 'player' && battle.defender && (battle.defender.trait || '').includes('(AEUG)') &&
      battle.attackerSide === 'opp' && battle.attacker) {
    return battle.attacker;
  }
  return null;
}
function gd06112Effect(card, battle, onDone) {
  const target = gd06112Target(battle);
  if (!target) {
    addLog(`${card.name} — no enemy Unit battling a friendly (AEUG) Unit. No effect.`);
    onDone?.();
    return;
  }
  dealDamageToUnit(target, 2, { fromEnemy: true });
  addLog(`${card.name} — dealt 2 damage to ${target.name}.`);
  render();
  setupDropZones();
  onDone?.();
}
registerCardEffect('GD06-112', {
  canActivate: (card, ctx) => !!gd06112Target(ctx),
  attack: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    gd06112Effect(card, ctx, ctx.onDone);
  },
  action: (card, ctx) => gd06112Effect(card, ctx.battle, ctx.onDone),
});

// ─── GD06-091 · Roux Louke — Pilot ──────────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// While this Unit is (AEUG), it gains <Breach 1>.
// Same pilotConditionalBreachValue pattern as GD03-088 Asemu Asuno (green.js).
registerCardEffect('GD06-091', {
  pilotConditionalBreachValue: (pilot, unit) =>
    (unit.trait || '').includes('(AEUG)') ? 1 : 0,
});

// ─── GD06-088 · Judau Ashta — Pilot ──────────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// [During Link][Destroyed] Choose 1 (Gundam Team) Unit card that is Lv.2 or
// lower from your trash. Add it to your hand.
// Own 'destroyed' trigger (fires when the paired Unit is destroyed — see
// effectDestroyOwnUnit/notifyUnitDestroyedByBattle's pilot-side dispatch)
// gated on isLinkUnit(ctx.unit), same idiom as GD04-086 Garma Zabi.
registerCardEffect('GD06-088', {
  destroyed: (card, ctx) => {
    if (!ctx?.unit || !isLinkUnit(ctx.unit)) return;
    if (ctx.ownerSide !== 'player') return;
    const candidates = state.playerTrash.filter(c =>
      c.type === 'unit' && (c.trait || '').includes('(Gundam Team)') && (parseInt(c.level) || 0) <= 2
    );
    if (candidates.length === 0) {
      addLog(`${card.name} — no (Gundam Team) Unit cards Lv.2 or lower in trash. No effect.`);
      return;
    }
    showCardPickerModal(
      card.name,
      'Choose 1 (Gundam Team) Unit card that is Lv.2 or lower from your trash. Add it to your hand.',
      candidates,
      (picked) => {
        if (picked) {
          state.playerTrash = state.playerTrash.filter(c => c.uid !== picked.uid);
          state.trashCount = Math.max(0, state.trashCount - 1);
          updateTempState(picked.cardNo, picked.copyVer, 'hand');
          state.hand.push(picked);
          addLog(`${card.name} — added ${picked.name} from trash to hand.`);
          broadcastCardToast(`${picked.name} was added from trash to hand due to ${card.name} effect.`);
          glowHandCard(picked.uid);
          broadcastCounters();
          render();
          setupDropZones();
        }
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── GD06-033 · ZZ Gundam — Unit ────────────────────────────────────────────
// [Deploy] Deal 2 damage to all enemy Units.
// When your (AEUG) cards deal damage to enemy Units, increase the damage
// dealt by 1. A field-wide outgoing-damage-bonus aura — applies to BOTH
// battle and effect damage, dealt by ANY (AEUG)-trait card the controller
// has in play (not just this card itself), added before the target's own
// reductions — see getGlobalOutgoingDamageBonus() in game.js (wired into
// dealDamageToUnit for effect damage, runDamageStep for battle damage).
registerCardEffect('GD06-033', {
  deploy: (card) => {
    const targets = state.oppField.filter(c => c);
    if (targets.length === 0) {
      addLog(`${card.name} — [Deploy] no enemy Units. No effect.`);
      return;
    }
    targets.forEach(t => dealDamageToUnit(t, 2, { fromEnemy: true }));
    addLog(`${card.name} — [Deploy] dealt 2 damage to all enemy Units.`);
    render();
    setupDropZones();
  },
  globalOutgoingDamageBonus: (sourceOfDamageCard) =>
    (sourceOfDamageCard?.trait || '').includes('(AEUG)') ? 1 : 0,
});

// ─── GD06-045 · Core Base — Unit ─────────────────────────────────────────────
// [Destroyed] Choose 1 enemy Unit that is Lv.5 or lower. Deal 1 damage to it.
// Battle destruction fires on both clients (ctx has defenderSide/attackerSide
// — derive which side actually owned this card and only proceed for
// 'player'); effect destruction fires locally already, with a ctx that has
// no defenderSide field at all, so it always proceeds. Same idiom as
// GD02-044 above.
registerCardEffect('GD06-045', {
  destroyed: (card, ctx) => {
    if (ctx && ctx.defenderSide !== undefined) {
      const ownerSide = card.uid === ctx.defender?.uid ? ctx.defenderSide : ctx.attackerSide;
      if (ownerSide !== 'player') return;
    }
    const candidates = state.oppField.filter(c => c && (parseInt(c.level) || 0) <= 5);
    if (candidates.length === 0) {
      addLog(`${card.name} — [Destroyed] no enemy Units Lv.5 or lower. No effect.`);
      return;
    }
    showCardPickerModal(card.name, 'Choose 1 enemy Unit that is Lv.5 or lower. Deal 1 damage to it.', candidates, (target) => {
      if (target) {
        dealDamageToUnit(target, 1, { fromEnemy: true });
        addLog(`${card.name} — [Destroyed] dealt 1 damage to ${target.name}.`);
        render();
        setupDropZones();
      }
    }, card, { mandatory: true });
  },
});

// ─── GD06-039 · Neo Core Fighter — Unit ─────────────────────────────────────
// [Activate･Main] Destroy 2 of your (Gundam Team) Units with unique card
// names that are Lv.3 and this Unit: Deploy 1 (Gundam Team) Unit card that
// is Lv.8 or higher from your hand.
// A "destroy N other Units (+ this Unit itself) as a cost" shape has no
// equivalent in getUnitActivationCost()/showUnitActivateModal() (which only
// parse a RES-circle-number or "Rest N of your Units" cost) — rather than
// extend that generic parser for this one bespoke cost, the WHOLE cost (both
// the 2-unique-name-Lv.3-Unit destroy AND destroying this Unit) is paid
// entirely inside this card's own canActivate/activate handlers instead.
// getUnitActivationCost still correctly reports res:0/restUnits:0 for this
// card's text (neither pattern matches), so the generic modal's own
// resource-spend logic is simply a no-op and this handler owns the entire
// cost-and-effect resolution, same as any other registered 'activate' hook.
function gd06039DestroyCandidates(card) {
  return state.playerField.filter(c =>
    c && c.uid !== card.uid && (c.trait || '').includes('(Gundam Team)') && (parseInt(c.level) || 0) === 3
  );
}
function gd06039DeployCandidates() {
  return state.hand.filter(c =>
    c && c.type === 'unit' && (c.trait || '').includes('(Gundam Team)') && (parseInt(c.level) || 0) >= 8
  );
}
registerCardEffect('GD06-039', {
  canActivate: (card) => {
    const distinctNames = new Set(gd06039DestroyCandidates(card).map(c => c.name));
    return distinctNames.size >= 2 && gd06039DeployCandidates().length > 0 && findNextFieldSlot() !== -1;
  },
  activate: (card, ctx) => {
    const pool = gd06039DestroyCandidates(card);
    const distinctNames = new Set(pool.map(c => c.name));
    const deployCandidates = gd06039DeployCandidates();
    if (distinctNames.size < 2 || deployCandidates.length === 0 || findNextFieldSlot() === -1) {
      addLog(`${card.name} — cost can't be paid or no valid Unit to deploy. No effect.`);
      ctx.onDone?.(); return;
    }
    showCardPickerModalMulti(
      card.name,
      'Destroy 2 of your (Gundam Team) Units (Lv.3, unique card names) and this Unit to deploy 1 (Gundam Team) Unit card Lv.8 or higher from your hand.',
      pool,
      { min: 2, max: 2 },
      (chosen) => {
        if (!chosen || chosen.length < 2 || chosen[0].name === chosen[1].name) {
          addLog(`${card.name} — the 2 chosen Units must have unique card names. Activation cancelled.`);
          ctx.onDone?.(); return;
        }
        [...chosen, card].forEach(u => effectDestroyOwnUnit(u, card));
        addLog(`${card.name} — destroyed ${chosen.map(c => c.name).join(', ')} and itself to pay the cost.`);
        showCardPickerModal(
          card.name,
          'Deploy 1 (Gundam Team) Unit card that is Lv.8 or higher from your hand.',
          deployCandidates,
          (target) => {
            if (target) {
              const slot = findNextFieldSlot();
              if (slot === -1) {
                addLog(`${card.name} — field is full. ${target.name} was not deployed.`);
              } else {
                state.hand = state.hand.filter(c => c.uid !== target.uid);
                state.playerField[slot] = target;
                target.tempCantAttack = true;
                updateTempState(target.cardNo, target.copyVer, 'field');
                addLog(`${card.name} — deployed ${target.name} for free.`);
                triggerCardEffect(target, 'deploy', { slot });
              }
            }
            render();
            setupDropZones();
            ctx.onDone?.();
          },
          card,
          { mandatory: true }
        );
      },
      card
    );
  },
});

// ─── GD06-089 · McGillis Fareed — Pilot ────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// [During Link] When this Unit destroys an enemy shield area card with
// battle damage, choose 1 of your (Gjallarhorn) Units. During this turn, it
// may choose an active enemy Unit that is Lv.5 or lower as its attack
// target.
// onLinkedUnitAttackDestroysShield — same hook as GD05-098 Heero Yuy.
// CanAttackActiveLv5 tempKeyword — same convention as ST04-011 Athrun Zala,
// just granted to a player-chosen Unit instead of always the paired one.
registerCardEffect('GD06-089', {
  onLinkedUnitAttackDestroysShield: (pilot, unit) => {
    if (!isLinkUnit(unit)) return; // [During Link]
    const candidates = state.playerField.filter(c => c && (c.trait || '').includes('(Gjallarhorn)'));
    if (candidates.length === 0) {
      addLog(`${pilot.name} — no (Gjallarhorn) Units in play. No effect.`);
      return;
    }
    showCardPickerModal(
      pilot.name,
      'Choose 1 of your (Gjallarhorn) Units. During this turn, it may choose an active enemy Unit (Lv.5 or lower) as its attack target.',
      candidates,
      (target) => {
        if (target) {
          target.tempKeywords = target.tempKeywords || [];
          if (!target.tempKeywords.includes('CanAttackActiveLv5')) target.tempKeywords.push('CanAttackActiveLv5');
          NET.send({ type: 'TEMP_KEYWORD_ADD', uid: target.uid, keyword: 'CanAttackActiveLv5' });
          addLog(`${pilot.name} — ${target.name} may attack active enemy Units (Lv.5 or lower) this turn.`);
          render();
          setupDropZones();
        }
      },
      pilot,
      { mandatory: true }
    );
  },
});

// ─── GD06-034 · Gundam Bael — Unit ──────────────────────────────────────────
// <High-Maneuver> (native — "This Unit can't be blocked.")
// [Deploy] If there are 4 or more (Gjallarhorn) cards in your trash, look at
// the top 4 cards of your deck. You may deploy 1 (Gjallarhorn)/(Tekkadan)
// Unit card that is Lv.6 or lower among them. Return the remaining cards
// randomly to the bottom of your deck.
// Same "look at top N, may deploy 1 matching Unit, shuffle rest to bottom"
// pattern as GD02-038 GQuuuuuuX (Omega Psycommu) — gated on a trash count
// first, and matching either of two traits instead of one.
registerCardEffect('GD06-034', {
  deploy: (card) => {
    const gjTrashCount = state.playerTrash.filter(c => (c.trait || '').includes('(Gjallarhorn)')).length;
    if (gjTrashCount < 4) {
      addLog(`${card.name} — [Deploy] fewer than 4 (Gjallarhorn) cards in trash. No effect.`);
      return;
    }
    const peeked = peekTopOfDeck(4);
    if (peeked.length === 0) {
      addLog(`${card.name} — [Deploy] deck is empty. No effect.`);
      return;
    }

    const shuffleAndReturnEntries = (entries) => {
      for (let i = entries.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [entries[i], entries[j]] = [entries[j], entries[i]];
      }
      entries.forEach(e => sendDeckEntryToBottom(e));
    };

    const deployAndReturn = (chosenItem) => {
      const remaining = peeked.filter(p => p !== chosenItem);
      if (chosenItem) {
        const slot = findNextFieldSlot();
        if (slot === -1) {
          addLog(`${card.name} — [Deploy] field is full, could not deploy ${chosenItem.card.name}.`);
          shuffleAndReturnEntries([chosenItem.entry, ...remaining.map(p => p.entry)]);
        } else {
          const chosen = chosenItem.card;
          state.playerField[slot] = chosen;
          chosen.tempCantAttack = true;
          updateTempState(chosen.cardNo, chosen.copyVer, 'field');
          const trait = chosen.trait || '';
          if (trait.includes('Superpower Bloc') || trait.includes('(UN)')) {
            state.superpowerDeployedThisTurn = true;
          }
          addLog(`${card.name} — [Deploy] deployed ${chosen.name} from deck.`);
          triggerCardEffect(chosen, 'deploy', { slot });
          NET.send({ type: 'PLAY_CARD', slot, card: chosen, counters: myCounters() });
          shuffleAndReturnEntries(remaining.map(p => p.entry));
        }
      } else {
        shuffleAndReturnEntries(peeked.map(p => p.entry));
      }
      render(); setupDropZones();
    };

    const isDeployable = p =>
      p.card.type === 'unit' &&
      ((p.card.trait || '').includes('(Gjallarhorn)') || (p.card.trait || '').includes('(Tekkadan)')) &&
      p.card.level <= 6;

    if (!peeked.some(isDeployable)) {
      addLog(`${card.name} — [Deploy] no (Gjallarhorn)/(Tekkadan) Unit Lv.6 or lower in top 4 cards.`);
    }
    const disabledIndices = peeked.map((p, i) => i).filter(i => !isDeployable(peeked[i]));
    showCardPickerModal(
      card.name,
      'You may deploy 1 (Gjallarhorn)/(Tekkadan) Unit card that is Lv.6 or lower from the top 4 cards of your deck.',
      peeked.map(p => p.card),
      (chosen) => {
        if (!chosen) { deployAndReturn(null); return; }
        const chosenItem = peeked.find(p => p.card.uid === chosen.uid);
        deployAndReturn(chosenItem || null);
      },
      card,
      { mandatory: false, disabledIndices }
    );
  },
});

// ════════════════════════════════════════════════════════════════════════════
// GD06 follow-up — newly added cards
// ════════════════════════════════════════════════════════════════════════════

// ─── GD06-125 · Argama — Base ────────────────────────────────────────────────
// [Burst] Deploy this card and [Deploy] Add 1 of your Shields to your hand
// are handled generically (see deployBaseFromShield()/applyBaseDeployEffect()
// in game.js) — no handler needed for either clause.
// [Once per Turn] When this Base receives battle damage while you have a
// rested (AEUG) Unit in play, choose 1 enemy Unit. Deal 1 damage to it.
// onBaseTakesBattleDamage — same hook as GD04-126 Izuma Colony (fired from
// runDamageStep's non-lethal Base-damage branch, defending client only).
// base.passiveUsedThisTurn gates [Once per Turn], cleared by UNREST_ALL.
// Note: this card's cardNo previously collided with GD06-123 Endra (a green
// Base) in the DB — both entries were mis-tagged "GD06-123"; fixed here to
// the correct GD06-125 (image path corrected to match) so both cards can be
// registered independently.
registerCardEffect('GD06-125', {
  onBaseTakesBattleDamage: (base, attacker, atkBp) => {
    if (base.passiveUsedThisTurn) return; // [Once per Turn]
    const hasRestedAeug = state.playerField.some(c => c && c.rested && (c.trait || '').includes('(AEUG)'));
    if (!hasRestedAeug) return;
    base.passiveUsedThisTurn = true;
    const candidates = state.oppField.filter(c => c);
    if (candidates.length === 0) {
      addLog(`${base.name} — [Once per Turn] no enemy Units to target. No effect.`);
      return;
    }
    showCardPickerModal(base.name, 'Choose 1 enemy Unit. Deal 1 damage to it.', candidates, (target) => {
      if (target) {
        dealDamageToUnit(target, 1, { fromEnemy: true });
        addLog(`${base.name} — [Once per Turn] dealt 1 damage to ${target.name}.`);
        broadcastCounters(); render(); setupDropZones();
      }
    }, base, { mandatory: true });
  },
});

// ─── GD06-110 · Familial Love — Command ──────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// [Action] Choose 1 of your Units. It gets AP+2 during this battle. Then, if
// you have 3 or less cards in your hand, draw 1.
// The draw is unconditional on the first clause actually picking a target —
// only gated on hand size — so it's checked on every path, including "no
// friendly Units in play".
function gd06110MaybeDraw(card) {
  if (state.hand.length <= 3) {
    const drawn = drawCardsFromTempDeck(1, true);
    if (drawn.length > 0) {
      state.hand.push(drawn[0]);
      addLog(`${card.name} — 3 or less cards in hand: drew 1.`);
    }
  }
}
registerCardEffect('GD06-110', {
  action: (card, ctx) => {
    const candidates = state.playerField.filter(c => c);
    if (candidates.length === 0) {
      addLog(`${card.name} — no friendly Units in play.`);
      gd06110MaybeDraw(card);
      broadcastCounters(); render();
      ctx.onDone(); return;
    }
    showCardPickerModal(card.name, 'Choose 1 of your Units. It gets AP+2 during this battle.', candidates, (target) => {
      if (target) {
        applyApMod([target], 2, 'battleApMod');
        addLog(`${card.name} — ${target.name} gets AP+2 during this battle.`);
      }
      gd06110MaybeDraw(card);
      broadcastCounters(); render(); setupDropZones();
      ctx.onDone();
    }, card, { mandatory: true });
  },
});

// ─── GD06-111 · Steely Loyalty — Command Pilot ───────────────────────────────
// [Action] If there are 4 or more (Gjallarhorn) cards in your trash, choose 1
// enemy Unit. Deal 2 damage to it.
// [Pilot] [Julieta Juris]
function gd06111GjallarhornTrashCount() {
  return state.playerTrash.filter(c => (c.trait || '').includes('(Gjallarhorn)')).length;
}
registerCardEffect('GD06-111', {
  canActivate: () => gd06111GjallarhornTrashCount() >= 4 && state.oppField.some(c => c),
  action: (card, ctx) => {
    if (gd06111GjallarhornTrashCount() < 4) {
      addLog(`${card.name} — fewer than 4 (Gjallarhorn) cards in trash. No effect.`);
      ctx.onDone(); return;
    }
    const candidates = state.oppField.filter(c => c);
    if (candidates.length === 0) {
      addLog(`${card.name} — no enemy Units in play. No effect.`);
      ctx.onDone(); return;
    }
    showCardPickerModal(card.name, 'Choose 1 enemy Unit. Deal 2 damage to it.', candidates, (target) => {
      if (target) {
        dealDamageToUnit(target, 2, { fromEnemy: true });
        addLog(`${card.name} — dealt 2 damage to ${target.name}.`);
        broadcastCounters(); render(); setupDropZones();
      }
      ctx.onDone();
    }, card, { mandatory: true });
  },
});

// ─── GD06-037 · Reginlaze Julia — Unit ───────────────────────────────────────
// During your turn, while this Unit is battling a damaged enemy Unit, it
// gains <First Strike>. Same attack-trigger shape as GD01-063 ZnO, gated on
// ctx.defender.damaged instead of its level.
// [During Pair] Enemy Units can't be set as active by enemy effects.
// New preventsEnemyUnrestByEffect hook (see setRested() in white.js) — a
// side-wide (not single-card) counterpart to cantBeSetActive: while this
// card is paired with any Pilot, every card-effect-driven attempt by the
// OPPOSING player to un-rest one of THEIR OWN Units is blocked. Checked
// against every card on the opposing field inside setRested() itself, so it
// covers every existing "choose 1 of your rested Units, set it active"
// effect without touching each one's own candidate filtering.
registerCardEffect('GD06-037', {
  attack: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    if (ctx.isShield || ctx.isDirect) { ctx.onDone(); return; }
    if (!ctx.defender?.damaged) { ctx.onDone(); return; }
    card.tempKeywords = card.tempKeywords || [];
    if (!card.tempKeywords.includes('First Strike')) card.tempKeywords.push('First Strike');
    NET.send({ type: 'TEMP_KEYWORD_ADD', uid: card.uid, keyword: 'First Strike' });
    addLog(`${card.name} — battling a damaged enemy Unit: gains <First Strike>.`);
    render();
    ctx.onDone();
  },
  preventsEnemyUnrestByEffect: (card) => !!card.pilot,
});

// ─── GD06-048 · Helmwige Reincar — Unit ────────────────────────────────────
// [Deploy] Choose 1 enemy Unit that is Lv.2 or lower. Deal 1 damage to it.
// If there are 4 or more (Gjallarhorn) cards in your trash, choose 1 enemy
// Unit instead (the Lv.2-or-lower restriction is lifted).
registerCardEffect('GD06-048', {
  deploy: (card) => {
    const bypassLevel = state.playerTrash.filter(c => (c.trait || '').includes('(Gjallarhorn)')).length >= 4;
    const candidates = state.oppField.filter(c => c && (bypassLevel || (parseInt(c.level) || 0) <= 2));
    if (candidates.length === 0) {
      addLog(`${card.name} — no eligible enemy Units. No effect.`);
      return;
    }
    showCardPickerModal(
      card.name,
      bypassLevel ? 'Choose 1 enemy Unit. Deal 1 damage to it.' : 'Choose 1 enemy Unit that is Lv.2 or lower. Deal 1 damage to it.',
      candidates,
      (target) => {
        if (target) {
          dealDamageToUnit(target, 1, { fromEnemy: true });
          addLog(`${card.name} — dealt 1 damage to ${target.name}.`);
          render();
          setupDropZones();
        }
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── GD06-126 · Skipjack-class Warship — Base ──────────────────────────────
// [Burst]/[Deploy] handled generically (deploy this card / shield to hand).
// [Deploy] Then, if a friendly (Gjallarhorn) Unit is in play, choose 1
// damaged enemy Unit. Deal 1 damage to it.
registerCardEffect('GD06-126', {
  deploy: (card) => {
    const hasGjallarhorn = state.playerField.some(c => c && (c.trait || '').includes('(Gjallarhorn)'));
    if (!hasGjallarhorn) return;
    const candidates = state.oppField.filter(c => c && c.damaged);
    if (candidates.length === 0) {
      addLog(`${card.name} — no damaged enemy Units. No effect.`);
      return;
    }
    showCardPickerModal(card.name, 'Choose 1 damaged enemy Unit. Deal 1 damage to it.', candidates, (target) => {
      if (target) {
        dealDamageToUnit(target, 1, { fromEnemy: true });
        addLog(`${card.name} — dealt 1 damage to ${target.name}.`);
        render();
        setupDropZones();
      }
    }, card, { mandatory: true });
  },
});
